/* =============================================================================
 * English — the dictionary, and the shape of every other one
 * -----------------------------------------------------------------------------
 * This file is the source of `TranslationKey`. Every other language is typed as
 * a partial of it, so a key that exists in Arabic and nowhere else is a type
 * error rather than a string nobody can ask for.
 *
 * It is imported statically. English is the fallback for a missing entry in any
 * language, so it has to be present before the first render whatever the reader
 * has chosen — there is no version of this application that does not need it.
 *
 * Arabic lives in its own file and is fetched only when somebody is reading
 * Arabic. See i18n.tsx.
 * ========================================================================== */

export const en = {
  'me.title': 'My Record',
  'me.today': 'Today',
  'me.markSelf': 'Mark Me',
  'me.crew': 'Crew',
  'me.trip': 'Trip',
  'me.attendance': 'Attendance',
  'me.payslips': 'Payslips',
  'me.leave': 'Leave',
  'me.flag': 'Report',
  'me.advances': 'Advances',
  'me.account': 'Account',
  'leave.whyRefused': 'Why It Was Refused:',
  'leave.officeSaid': 'The Office Added:',
  'me.employeeNo': 'Employee No.',
  'me.employer': 'Employer',
  'me.designation': 'Designation',
  'me.joined': 'Joined',
  'me.signOut': 'Sign Out',

  'today.onSite': 'On Site',
  'today.offSite': 'Not On Site',
  'today.working': 'At Work Now',
  'today.finished': 'Finished For Today',
  'today.notMarked': 'Not Marked Yet',
  'today.presentSince': 'Present Since',
  'today.markedBy': 'Marked By',
  'today.stillRunning':
    'Still Counting. The Final Figure Is Set Once You Are Marked Out, After The '
    + 'Break And Rounding Are Applied.',
  'today.notWorkedOutYet':
    'These Are Your Scans Added Up. The Office Has Not Worked The Day Out Yet — '
    + 'The Final Hours Apply The Break And Rounding, And May Differ A Little.',
  'today.notMarkedTitle': 'Nobody Has Marked You In Yet',
  'today.notMarkedBody':
    'Your Driver Scans Your Face When Work Starts. Your Hours Appear Here The '
    + 'Moment They Do.',
  'today.seeDetails': 'See Where And When',
  'today.site': 'Site',
  'today.started': 'Started',
  'today.hoursToday': 'Hours Today',
  /* A journey inside the working day, labelled so nobody adds it on top
     of the day it is already inside. */
  'today.driving': 'Driving',
  'today.overtime': 'Overtime',
  'today.correctedBy': 'Corrected By The Office:',
  'today.overtimeNote': 'About Your Overtime:',
  'today.noRecord': 'Nothing Recorded Yet Today',
  'today.noRecordBody':
    'Your Attendance Appears Here Once Your Phone Reaches A Site Boundary.',

  'attendance.thisMonth': 'This Month',
  'attendance.present': 'Present',
  'attendance.absent': 'Absent',
  // Today, before the day has settled one way or the other.
  'attendance.notInYet': 'Not In Yet',
  'attendance.onLeave': 'On Leave',
  'attendance.daysWorked': 'Days Worked',
  'attendance.overtime': 'Overtime',
  'attendance.date': 'Date',
  'attendance.status': 'Status',
  'attendance.job': 'Job',
  'attendance.hours': 'Hours',
  'attendance.empty': 'No Attendance Recorded For This Month.',
  'attendance.adjusted': 'Adjusted',
  'attendance.recordedWas': '{hours} Recorded',

  'payslip.month': 'Month',
  'payslip.days': 'Days',
  'payslip.gross': 'Gross',
  'payslip.deductions': 'Deductions',
  'payslip.net': 'Net Paid',
  'payslip.empty': 'No Payslips Yet.',
  'payslip.stillRunning': "This Month's Pay Is Still Being Worked Out. Your Payslip Appears Here Once It Has Been Approved.",
  'payslip.view': 'View',

  'leave.type': 'Type',
  'leave.from': 'From',
  'leave.to': 'To',
  'leave.days': 'Days',
  'leave.status': 'Status',
  'leave.empty': 'No Leave On Record.',
  'leave.request': 'Request Leave',
  'leave.balance': 'Leave Remaining',
  'leave.entitled': 'Entitled',
  'leave.taken': 'Taken',

  'advance.outstanding': 'Outstanding Balance',
  'advance.taken': 'Taken',
  'advance.repaid': 'Repaid',
  'advance.date': 'Date',
  'advance.amount': 'Amount',
  'advance.empty': 'No Advances On Record.',

  'common.readOnly': 'You Can View This Record But Not Change It.',
  'common.loadFailed': 'This Could Not Be Loaded',
  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.dismiss': 'Dismiss',
  'common.total': 'Total',
  'self.noLocation':
    'Your Location Could Not Be Read. Attendance Cannot Be Recorded Without It.',
  'self.noFace':
    'Your Face Is Not Enrolled Yet, So Nothing Can Recognise You. Ask The Office '
    + 'To Enrol It — It Takes A Minute And Is Done Once.',
  'self.siteRequired':
    'Your Company Records Every Hour Against A Job Number, And Your Phone Is '
    + 'Not Inside Any Site. Walk Onto The Site, Or Choose It Below, Then Scan.',
  'self.cameraStarting': 'Starting The Camera…',
  'self.cameraNotReady': 'The Camera View Is Not Ready.',
  'self.notYourFace':
    'That Is Not Your Face. Self-attendance Records Only You — If You Are '
    + 'Marking Somebody Else, The Driver Has To Do It.',
  'self.readAgain':
    'The Camera Did Not Read Your Face Clearly Enough To Be Sure It Is You. '
    + 'Face The Camera Squarely, In Better Light, And Scan Again.',
  'self.savedOffline':
    'Recorded On This Phone. It Will Send Itself When You Have Signal.',
  'self.scanFailed': 'The Scan Failed.',
  'self.starting': 'Starting Work',
  'self.finishing': 'Finishing Work',
  'self.findingYou': 'Finding You…',
  'self.checkAgain': 'Check Again',
  'self.turnOnLocation': 'Turn On Location',
  'self.chooseSite': 'Choose Your Site…',
  'self.siteFromLocation': 'Filled In From Your Location. Change It If It Is Wrong.',
  'self.siteDecides':
    'Your Location Decides Which Site This Is Recorded Against. Your Answer '
    + 'Is Kept Beside It So Anybody Can See If The Two Disagree.',
  'self.checkedIn': 'Checked In',
  'self.checkedOut': 'Checked Out',
  'self.noSite': 'No Site Recorded',
  'self.noSiteBody':
    'You Are Marked Present, But This Day Is Charged To No Job Number.',
  'self.scanning': 'Scanning…',
  'self.scanStart': 'Scan My Face — Starting Work',
  'self.scanFinish': 'Scan My Face — Finishing Work',
  'flag.title': 'Report A Problem',
  'flag.raise': 'Raise A Flag',
  'flag.about': 'What Is It About',
  'flag.whichDay': 'Which Day',
  'flag.inFewWords': 'In A Few Words',
  'flag.subjectHint': 'My Hours Show 6 But I Worked 9',
  'flag.anythingElse': 'Anything Else',
  'flag.detailHint': 'I Was At Al Awir Until Six, The Driver Marked Us Out Early',
  'flag.saySomething': 'Say In A Few Words What Is Wrong.',
  'flag.sending': 'Sending…',
  'flag.emptyTitle': 'Nothing Reported',
  'flag.emptyBody':
    'Anything You Raise Stays Here With Its Answer, So You Can Check Back Rather '
    + 'Than Having To Ask Again.',
  'flag.reportedOn': 'Reported',
  'flag.at': 'At',
  'flag.about2': 'About',
  'flag.fromOffice': 'From The Office',

  'flag.cat.attendance': 'My Hours Or Attendance',
  'flag.cat.overtime': 'Overtime',
  'flag.cat.payslip': 'My Payslip',
  'flag.cat.advance': 'An Advance',
  'flag.cat.leave': 'Leave',
  'flag.cat.document': 'A Document',
  'flag.cat.app': 'Something Wrong With The App',
  'flag.cat.other': 'Something Else',

  'flag.state.open': 'Waiting',
  'flag.state.in_progress': 'Being Looked At',
  'flag.state.resolved': 'Sorted',
  'flag.state.rejected': 'Closed',
  'trip.startFailed': 'The Trip Could Not Be Started.',
  'trip.recorded': 'Trip Recorded.',
  'trip.sentCount.one': '{count} Trip Sent.',
  'trip.sentCount.other': '{count} Trips Sent.',
  'trip.stopNumber': 'Stop {number}',
  'trip.savedOffline':
    'Trip Saved On This Phone. It Will Send Itself When You Have Signal.',
  'trip.closeFailed': 'The Trip Could Not Be Closed.',
  'trip.checking': 'Checking…',
  'trip.running': 'Trip In Progress',
  'trip.none': 'No Trip Running',
  'trip.distance': 'Distance',
  'trip.stops': 'Stops',
  'trip.points': 'Points',
  /* Which vehicle the driver has today.
     -------------------------------------------------------------------------
     A dropdown of every plate stood here, labelled by three keys that are now
     gone with it: it set a value nothing ever sent anywhere, because the trip's
     vehicle is decided server-side at mark-in. The card on the windscreen is
     what decides it. See ClaimVehicle. */
  'claim.title': 'Which Vehicle',
  'claim.scan': 'Scan The Card',
  'claim.stopScan': 'Stop',
  'claim.starting': 'Starting The Camera…',
  'claim.aim': 'Hold The Camera Over The Card On The Windscreen.',
  'claim.none':
    'You Have Not Taken A Vehicle Today. Scan The Card On The One You Are Driving.',
  'claim.todayScanned':
    'You Took {plate} Today. Your Route Will Be Recorded Against It.',
  'claim.todayAssigned':
    '{plate} Is On Your Record. Scan A Card If You Are Driving A Different One.',
  'claim.isYours': 'This Is The Vehicle On Your Record.',
  /* Not an error. A yard hands out whatever is free, and the driver is the
     person who knows which one they are standing next to — the note exists to
     make a mis-read card obvious, not to discourage them. */
  'claim.notYours':
    'This Is Not The Vehicle On Your Record. Taking It Is Fine — Just Check The Plate.',
  'claim.take': 'Take This Vehicle',
  'claim.taking': 'Taking…',
  'claim.notThis': 'Not This One',
  'claim.code': 'Vehicle Code',
  /* The shape of the code, in the box before anything is typed. Through the
     dictionary rather than written into the field: it is the only thing on
     that screen a scanner would otherwise find untranslated, and an Arabic
     screen may one day want Arabic-Indic digits in the mask. */
  'claim.codeShape': 'XXXX-XXXX-XXXX',
  'claim.orType': 'The Twelve Characters Printed Under The Square.',
  'claim.check': 'Check',
  'claim.checking': 'Checking…',
  'claim.taken': 'You Have {plate}. Your Route Will Be Recorded Against It.',
  'trip.finishing': 'Finishing…',
  'trip.finish': 'Finish Trip',
  'trip.starting': 'Starting…',
  'trip.start': 'Start Trip',
  'kiosk.title': 'Face Kiosk',
  'kiosk.back': 'Back To Attendance',
  'kiosk.lookAtCamera': 'Look At The Camera',
  'kiosk.moveALittle': 'Move A Little',
  'kiosk.moveWhy': 'Blink Or Turn Your Head — A Photograph Cannot, Which Is The Point.',
  'kiosk.cannotTellApart': 'Cannot Tell You Apart',
  'kiosk.notRecognised': 'Not Recognised',
  'kiosk.notEnrolled':
    'This Face Is Not Enrolled. The Supervisor Can Mark You By Roll-call.',
  'kiosk.notSure': 'Scan Again',
  'kiosk.notSureWhy':
    'The Face Was Not Read Clearly Enough To Name Anybody With Certainty. '
    + 'Face The Camera Squarely And Scan Again.',
  'kiosk.noFaces':
    'No Faces Are Enrolled Yet, So Nobody Can Be Recognised. Enrol The Crew First.',
  'kiosk.findingLocation': 'Finding Your Location…',
  'kiosk.slowBackend': 'Running On The {backend} Backend — This Phone Has No Graphics Acceleration Available, So Scanning Will Be Slow.',
  'kiosk.findingYou': 'Finding You…',
  'kiosk.cameraStarting': 'Starting The Camera…',
  'kiosk.cameraNotReady': 'The Camera View Is Not Ready.',
  'kiosk.couldNotStart': 'The Kiosk Could Not Start.',
  'kiosk.noLocationRead': 'Could Not Read Your Location.',
  'fk.refusedNoSite':
    '{count} Scan(s) On This Phone Named No Site And Could Not Be Saved. This '
    + 'Company Records Every Hour Against A Job Number — Those Scans Are Not '
    + 'Attendance, And The Day They Belong To Must Be Marked By Hand.',
  'kiosk.siteRequired': 'Choose The Site Before Scanning',
  'kiosk.siteRequiredWhy':
    'This Company Records Every Hour Against A Job Number, And This Phone Is '
    + 'Not Inside Any Site Boundary. Drive Onto The Site, Or Choose It Above, '
    + 'And The Kiosk Will Start.',
  'kiosk.notRecordedNoSite': 'Not Recorded — No Site',
  'kiosk.notRecordedNoSiteWhy':
    'The Location No Longer Matches Any Site, And This Company Records Every '
    + 'Hour Against A Job Number. Choose The Site On The Setup Screen, Then '
    + 'Scan Again.',
  'kiosk.chooseSite': 'Choose The Site…',
  'kiosk.arriving': 'Arriving',
  'kiosk.arrivingHint': 'Start Of The Shift',
  'kiosk.leaving': 'Leaving',
  'kiosk.leavingHint': 'End Of The Shift',
  'kiosk.loadingFaces': 'Loading Faces…',
  'kiosk.noSite': 'No Site Recorded',
  'kiosk.refusedScans.one': '{count} Scan Was Refused.',
  'kiosk.refusedScans.other': '{count} Scans Were Refused.',
  'kiosk.refusedYouCanMark': 'Those People Have No Attendance Record For Today Unless You Mark Them By Roll-call.',
  'kiosk.refusedTellTheOffice': 'Those People Have No Attendance Record For Today. Enrol Their Face, Or Tell The Office So It Can Be Recorded.',
  'kiosk.stillOnSite.one': '{count} Still On Site',
  'kiosk.stillOnSite.other': '{count} Still On Site',
  'kiosk.wasAlreadyIn': '{name} Was Already Marked In, So The Day Is Unchanged. Switch To Leaving To Check Them Out.',
  'kiosk.wasNotIn': '{name} Was Not Marked In, So Nothing Was Closed. The Day Is Unchanged.',
  'kiosk.alreadyRecorded': 'Already Recorded. Pass It On.',
  'kiosk.nothingScanned': 'Nothing Scanned Here Yet Today.',
  'kiosk.allRecognised': 'Everyone Who Looked At The Camera Was Recognised.',
  'kiosk.warnNoLocation':
    'No Location. Scans Are Being Recorded Without Coordinates, Which Means '
    + 'Nothing Can Confirm The Crew Was On Site.',
  'kiosk.warnNoBoundary':
    'No Site Has A Boundary Drawn, So Nothing Can Be Checked Against The '
    + 'Coordinates. The Site Is Whatever Was Named On The Setup Screen.',
  'kiosk.warnOutside':
    'This Phone Is Not Inside Any Site Boundary. Every Scan Is Recorded And '
    + 'Marked As Taken Away From The Site — Nothing Is Blocked, And Somebody '
    + 'Will Be Asked About It.',
  'roll.markEveryone': 'Mark Everyone',
  'roll.clearAll': 'Clear All',
  'roll.markedOf': '{marked} Of {total} Marked',
  'roll.nobodyMarkedYet': 'Nobody Marked Yet',
  'roll.enrolFace': 'Enrol A Face',
  'roll.enrolFaceHint': 'Put Somebody On The Kiosk So They Can Scan In.',
  'roll.title': 'Attendance',
  'roll.back': 'Back To Attendance',
  'roll.signOut': 'Sign Out',
  'roll.scanInstead': 'Scan Faces Instead',
  'roll.chooseSite': 'Choose The Site…',
  'roll.findingYou': 'Finding You…',
  'roll.updateLocation': 'Update Location',
  'roll.getLocation': 'Get Location',
  'roll.searchAdd': 'Search To Add Someone Not Assigned Here…',
  'roll.nobodyMatched': 'Nobody Matched',
  'roll.noCrew': 'No Crew Assigned To This Site',
  'roll.tryShorter': 'Try A Shorter Search.',
  'roll.assignPeople': 'Assign People To This Project, Or Search Above To Add Anyone.',
  'roll.markStarted': 'Mark Work Started',
  'roll.markFinished': 'Mark Work Finished',
  'roll.starting': 'Starting Work',
  'roll.startingHint': 'Records The Time And Place Work Began',
  'roll.finishing': 'Finishing Work',
  'roll.finishingHint': 'Closes The Day And Calculates The Hours',
  'roll.noLocation':
    'Could Not Read Your Location. Check That Location Is Switched On And That '
    + 'This App Has Permission, Then Try Again.',
  'roll.notLinked':
    'Your Account Is Not Linked To An Employee Record, So A Roll-call Cannot Be '
    + 'Attributed To You. Ask An Administrator To Link It.',
  'roll.chooseSiteFirst': 'Choose A Site First.',
  'roll.nobodyArrived': 'Nobody Is Marked As Arrived.',
  'roll.nobodyLeaving': 'Nobody Is Marked As Leaving.',
  'table.search': 'Search…',
  'table.emptyTitle': 'Nothing Here Yet',
  'table.preparing': 'Preparing…',
  'table.couldNotLoad': 'This Could Not Be Loaded',
  'table.couldNotLoadBody': 'The List Did Not Load, So This Is Not The Same As There Being Nothing Here. Check The Connection And Try Again.',
  'table.noMatch': 'Nothing Matched That Search',
  'table.noMatchBody': 'Try A Shorter Or Different Term.',
  'table.prev': 'Previous Page',
  'table.next': 'Next Page',

  'nav.dashboard': 'Dashboard',
  'nav.employees': 'Employees',
  'nav.attendance': 'Attendance',
  'nav.payroll': 'Payroll',
  'nav.salarySummary': 'Salary Summary',
  'nav.projects': 'Projects',
  'nav.leaves': 'Leaves',
  'nav.advances': 'Loans & Advances',
  'nav.fleet': 'Fleet',
  'nav.reports': 'Reports',
  'nav.documents': 'Documents',
  'nav.settings': 'Settings',
  'nav.light': 'Light',
  'nav.dark': 'Dark',
  'nav.matchDevice': 'Match Device',

  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.save': 'Save',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.search': 'Search',
  'common.language': 'Language',
  'dash.title': 'Dashboard',

  /* The dashboard, rebuilt to the mock. */
  'dash.welcome': 'Welcome Back, {name}',
  'dash.whatsHappening': 'Here’s What’s Happening In Your Organization Today.',
  'dash.totalEmployees': 'Total Employees',
  'dash.joinedThisMonth': '↑ {count} This Month',
  'dash.absentToday': 'Absent Today',
  'dash.activeJobs': 'Active Jobs',
  'dash.runningProjects': 'Running Projects',
  'dash.attendanceOverview': 'Attendance Overview',
  'dash.attendanceChartSummary':
    'Present, Absent And On Leave For Each Day Of The Period.',
  'dash.payrollSummary': 'Payroll Summary',
  'dash.payrollChartSummary':
    'Net Payroll Split Into Basic Salary, Allowances, Overtime And Deductions.',
  'dash.netPayroll': 'Net Payable',
  'dash.grossPayroll': 'Gross',
  'dash.basicSalary': 'Basic Salary',
  'dash.allowances': 'Allowances',
  'dash.deductions': 'Deductions',
  'dash.paidAlready': 'Pre-payment',
  'dash.noRunYet': 'No Payroll Has Been Run For This Month Yet.',
  'dash.wpsOverview': 'WPS Overview',
  'dash.companyWideOnly': 'Company-wide Only',
  'let.title': 'Letters & Certificates',
  'let.notForOutsideWorker':
    'No Letter Can Be Issued To An Outside Worker. Every Letter Here States That This Company Employs Them, And It Does Not.',
  'let.issue': 'Issue A Letter',
  'let.issueAndPrint': 'Issue And Print',
  'let.none': 'No Letters Have Been Issued To This Employee Yet.',
  'let.kind': 'Kind Of Letter',
  'let.language': 'Language',
  'let.print': 'Print',
  'let.withdrawn': 'WITHDRAWN',
  'dash.noSiteBreakdown': 'A Payslip Covers A Whole Month, Not One Site — Somebody Can Work Across Three Projects And Still Be Paid Once. There Is No Per-site Figure To Show.',
  'dash.wpsNoSiteBreakdown': 'A WPS Batch Is One Salary File For The Whole Company, So It Has No Site To Break Down.',
  'dash.seeJobCost': 'Labour Charged To This Site Is In Job Labour Cost Below.',
  'dash.showAllSites': 'Show All Sites',
  'dash.wpsChartSummary': 'Wage Protection Files By Whether They Were Accepted.',
  'dash.accepted': 'Accepted',
  'dash.pending': 'Pending',
  'dash.rejected': 'Rejected',
  'dash.noWpsYet': 'No Wage Protection File Has Been Generated For This Month.',
  'dash.total': 'Total',
  'dash.leaveSummary': 'Leave Summary',
  'dash.daysUsed': 'Days Used',
  'dash.pendingRequests': 'Pending Requests',
  'dash.requests': 'Requests',
  'dash.jobLabourCost': 'Job Labour Cost',
  'dash.jobSite': 'Job / Site',
  'dash.employees': 'Employees',
  'dash.labourCost': 'Labour Cost',
  'dash.totalCost': 'Total Cost',
  'dash.noJobCost': 'Nothing Has Been Costed To A Job Number Yet.',
  'dash.alerts': 'Alerts & Notifications',
  'dash.viewDetails': 'View Details',
  'dash.documentsExpiring': 'Documents Expiring In 30 Days',
  'dash.leavePending': 'Leave Requests Pending',
  'dash.wpsPending': 'WPS Pending Submission',
  'dash.settlementsPending': 'Final Settlements Pending',
  'dash.quickAccess': 'Quick Access',
  'dash.qa.addEmployee': 'Add Employee',
  'dash.qa.whoIsThis': 'Who Is This?',
  'dash.qa.markAttendance': 'Mark By Hand',
  'dash.qa.faceScan': 'Scan Faces',
  'dash.qa.attendance': 'Attendance Sheet',
  'dash.qa.leaveRequest': 'Leave Request',
  'dash.qa.runPayroll': 'Run Payroll',
  'dash.qa.wpsSubmission': 'WPS Submission',
  'dash.qa.jobSite': 'Job / Site',
  'dash.qa.reports': 'Reports',
  'dash.qa.documents': 'Documents',
  'dash.rulesActive': 'Rules Active',
  'dash.rulesComplete': 'Rules Complete And Active',
  'dash.today': 'Today',
  'dash.thisMonth': 'This Month',
  'dash.lastMonth': 'Last Month',
  'dash.thisYear': 'This Year',
  'dash.last12': 'Last 12 Months',
  'dash.between': 'Between Dates',
  'dash.from': 'From',
  'dash.to': 'To',
  'dash.allSites': 'All Sites',
  'dash.building': 'Building…',
  'dash.onPayroll': 'Active On The Payroll',
  'dash.presentToday': 'Present Today',
  'dash.daysWorked': 'Days Worked',
  'dash.onLeave': 'On Leave',
  'dash.daysOnLeave': 'Days On Leave',
  'dash.notInYet': 'Not In Yet',
  'dash.daysAbsent': 'Days Absent',
  'dash.approvedAbsence': 'Approved Absence',
  'dash.nothingToday': 'Nothing Recorded So Far Today',
  'dash.ofWorkforce': 'Of The Workforce',
  'dash.acrossPeople': 'Across {count} People',
  'dash.attendanceToday': 'Attendance Today',
  'dash.everyActiveSite': 'Across Every Active Site',
  'dash.daysEverySite': 'Days Recorded Across Every Active Site',
  'dash.nothingYet': 'Nothing Recorded Yet',
  'dash.nothingYetBody': 'Attendance Appears Here As Workers Reach Their Sites.',
  'dash.manpower': 'Manpower By Project',
  'dash.manpowerToday': 'Who Is On Site Today, And What Each Site Has Cost Today',
  'dash.whereToday': 'Where The Workforce Is Today',
  'dash.noSiteActivity': 'No Site Activity Yet',
  'dash.noSiteActivityBody':
    'Once Projects Have Geofences And Workers Arrive, The Split Appears Here.',
  'dash.activeProjects': 'Active Projects',
  'dash.outstandingAdvances': 'Outstanding Advances',
  'dash.payroll': 'Payroll',
  'dash.overtimeToday': 'Overtime Today',
  'dash.present': 'Present',
  'dash.otSub': 'Recorded, Or Approved Where Set',
  'dash.sitesRunning': 'Sites Currently Running',
  'dash.acrossWorkforce': 'Across The Workforce',
  'dash.overtime': 'Overtime',
  'dash.span.day': 'Today',
  'dash.span.month': 'This Month',
  'dash.span.year': 'This Year',
  'dash.span.twelve': 'Over Twelve Months',
  'dash.span.between': 'Between The Chosen Dates',
  'dash.attendanceSpan': 'Attendance',
  'dash.daysWorkedCost': 'Days Worked And Cost',
  'emp.title': 'Employees',
  'emp.recordIncrement': 'Record An Increment',
  'emp.setSalary': 'Set Salary',
  'emp.otRateHint': 'Money Per Ordinary Overtime Hour. Leave Empty To Work It Out From The Salary, Which Is What Almost Everybody Gets. Night, Rest-day And Public-holiday Hours Keep Their Premium Relative To This.',
  'emp.subtitle': 'The Master File. Every Other Module Reads From Here.',
  'emp.masterFile': 'Master File',
  'emp.headOffice': 'Head Office',
  'emp.noOffice': 'No Office Recorded',
  'emp.outsideWorkers': 'Outside Workers',
  'emp.masterSaving': 'Building…',
  'emp.waitingTitle.one': '{count} Person Is Waiting To Be Confirmed',
  'emp.waitingTitle.other': '{count} People Are Waiting To Be Confirmed',
  'emp.waitingWhy':
    'Taken On At A Site. They Are Scanned And Marked Present Already — Payroll '
    + 'Will Not Pay Them Until You Confirm The Record And The Face Enrolled With '
    + 'It. Every Day Already Recorded Is Paid Once You Do.',
  'emp.waitingDays.one': '{count} Day',
  'emp.waitingDays.other': '{count} Days',
  'emp.startedOn': 'Started {date}',
  'emp.confirm': 'Confirm',
  'emp.waiting': 'Waiting To Be Confirmed',
  'emp.add': 'Add Employee',
  'emp.visa': 'Visa',
  'emp.passport': 'Passport',
  'emp.papersOk': 'OK',
  'att.title': 'Attendance',
  'att.correctDayAria': 'Correct The Day For {name}',
  'att.adjustOtAria': 'Adjust Overtime For {name}',
  'att.correctOnAria': 'Correct {name} On {date}',
  'att.openDayAria': 'Open {name} On {date}',
  'att.dailySheet': 'Daily Sheet',
  'att.monthlyGrid': 'Monthly Grid',
  'att.reviewQueue': 'Review Queue',
  'att.recalculate': 'Recalculate',
  'att.recalculating': 'Recalculating…',
  'att.nothingQueued':
    'Nothing Was Queued For Recalculation. Anything Still Showing As Pending Is '
    + 'Already Up To Date.',
  'att.recomputeUnreachable': 'The Recompute Job Could Not Be Reached.',
  'att.correctByHand': 'Correct This Day By Hand',
  'att.prevDay': 'Previous Day',
  'att.nextDay': 'Next Day',
  'att.allStatuses': 'All Statuses',
  'att.notInYet': 'Not Marked Yet',
  /* The daily review. The site person reads the sheet, corrects what is wrong
     and signs the day off — and from then on the figures are theirs. */
  'att.reviewNote': 'Corrected During The Daily Review',
  'att.correctHoursFor': 'Corrected Hours For {name}',
  'att.correctOvertimeFor': 'Overtime To Pay {name}',
  'att.hoursUnreadable': 'Type Hours As 8, 7.5 Or 7:30',
  'att.approveRows.one': 'Approve 1 Row',
  'att.approveRows.other': 'Approve {count} Rows',
  'att.approving': 'Approving…',
  'att.dayCleared': 'Day Approved',
  'att.clearedBy': 'Approved By {name}',
  'att.noJobNumber': 'Worked, No Job Number',
  'att.publicHoliday': 'Public Holiday',
  'att.weeklyHoliday': 'Weekly Holiday',
  'att.addOtByHand': 'Add Overtime By Hand',
  'att.confirmOt': 'Confirm The Recorded Overtime',
  'att.notApproved': 'Not Yet Approved — Recorded Hours Will Be Paid',
  'att.adjustOt': 'Adjust Overtime',
  'att.adjustOtHint': 'Add Or Take Off Overtime, With A Reason',
  'att.loading': 'Loading…',
  'att.nothingFlagged': 'Nothing Flagged',
  'att.nothingFlaggedBody':
    'Suspicious Punches Appear Here With Their Evidence. Nothing Is Ever Rejected '
    + 'Automatically — A Wrong Accusation Costs More Than A Flag That Turns Out Fine.',
  'att.whatDidYouFind': 'What Did You Find?',

  'att.flag.fake_gps': 'Fake GPS App Detected',
  'att.flag.integrity': 'App Failed Integrity Check',
  'att.flag.rooted': 'Rooted Or Jailbroken Device',
  'att.flag.device': 'Unapproved Device',
  'att.flag.movement': 'Impossible Movement',
  'att.flag.pattern': 'Suspicious GPS Pattern',
  'att.flag.network': 'Network Location Disagrees',
  'proj.title': 'Projects',
  'proj.notFound': 'Project Not Found',
  'proj.back': 'Back To Projects',
  'proj.building': 'Building…',
  'proj.report': 'Report',
  'proj.details': 'Details',
  'proj.location': 'Location',
  'proj.changes': 'Changes To This Project',
  'stl.changes': 'Changes To This Settlement',
  'proj.boundary': 'Boundary',
  'proj.payrollCost': 'Payroll Cost',
  'proj.costUnread': 'Cost Could Not Be Read',
  'proj.retire': 'Retire This Job Number',
  'proj.retireSub':
    'It Stops Taking Attendance And Leaves Every Picker. Nothing Already Costed '
    + 'To It Moves.',
  'proj.required': 'Job Number And Project Name Are Both Required.',
  'proj.saveFailed': 'Could Not Save.',
  'proj.add': 'Add Project',
  'proj.jobNo': 'Job Number',
  'proj.jobNoHint': 'The Number Everyone Says Out Loud.',
  'proj.status': 'Status',
  'proj.name': 'Project Name',
  'proj.nameAr': 'Project Name In Arabic',
  'proj.nameArHint':
    'Optional. Used On Arabic Documents; The English Name Stands In Where This '
    + 'Is Blank.',
  'proj.client': 'Client',
  'proj.locationHint': 'Free Text. The Boundary Is Set On The Map.',
  'proj.startDate': 'Start Date',
  'proj.expectedEnd': 'Expected End',
  'proj.budget': 'Budget',
  'proj.notes': 'Notes',
  /* ---- the hours one site keeps -----------------------------------------
     A start time per site, because one gate opens at six and the next at
     seven. It colours the marking and moves no pay. */
  'proj.siteHours': 'Site Hours',
  'proj.siteHoursBlurb': 'Each Site Can Keep Its Own Hours. Leave A Box Empty And The Company Rule Applies. These Colour The Check-In And Check-Out On The Payslip — They Change No Hours And No Pay.',
  'proj.dutyStart': 'Duty Starts At',
  'proj.dutyStartHint': 'Arriving After This Is Marked Late. Empty: The Company Rule.',
  'proj.dutyEnd': 'Duty Ends At',
  'proj.dutyEndHint': 'Leaving Before This Is Marked As Going Early. Empty: A Short Day Is Judged On The Hours Worked Instead.',
  'proj.dutyGrace': 'Minutes That Still Count',
  'proj.dutyGraceHint': 'Arriving This Many Minutes Late, Or Leaving This Many Early, Still Counts. Empty: The Company Rule.',
  'proj.minutesSuffix': 'Min',
  'proj.companyRule': 'Company Rule',
  'proj.dutyHours': 'Duty Hours',
  'proj.budgetAlert': 'Warn At',
  'proj.budgetAlertHint': 'The Share Of The Budget At Which This Site Starts Warning. Empty: 80%.',
  /* ---- what each group is allowed to cost on a site --------------------- */
  'pb.title': 'Budget By Group',
  'pb.blurb': 'What Each Group Is Allowed To Cost On This Site, Against What It Has Cost So Far. The Spend Includes Outside Labour, Because A Budget Is About Money Paid Out Rather Than About Who Is Employed.',
  'pb.wholeJob': 'Whole Job',
  'pb.group': 'Group',
  'pb.budget': 'Budget',
  'pb.spent': 'Spent',
  'pb.left': 'Left',
  'pb.used': '{percent}% Used',
  'pb.overBy': 'Over By {amount}',
  'pb.warnAt': 'Warn At {percent}%',
  'pb.noBudget': 'No Budget Set',
  'pb.setBudget': 'Set A Budget',
  'pb.changeBudget': 'Change',
  'pb.removeBudget': 'Remove',
  'pb.pickGroup': 'Group',
  'pb.alertPercent': 'Warn At',
  'pb.alertPercentHint': 'The Share Of The Budget At Which This Group Starts Warning.',
  'pb.noteHint': 'Where The Figure Came From, For Whoever Reads This In A Year.',
  'pb.noGroups': 'No Groups Yet',
  'pb.noGroupsBody': 'Groups Are Named Once For The Whole Company — Tile Work, Block Work, Plaster — On An Employee Record. Each Site Then Sets Its Own Budget For Them Here.',
  'pb.nothingBudgeted': 'No Budgets Set On This Site',
  'pb.nothingBudgetedBody': 'Set One Against Any Group And This Card Starts Watching It.',
  'pb.groupsWarning': '{count} Of {total} Groups Are At Or Over Their Warning Share',
  'pb.everyGroupInside': 'Every Group Is Inside Its Budget',
  'pb.noCostYet': 'Nothing Charged Yet',
  'pb.total': 'Budgets Together',
  'proj.saving': 'Saving…',
  'proj.saveChanges': 'Save Changes',
  'proj.deactivate': 'Deactivate',
  'proj.deactivateNoMatch':
    'Anybody Scanned Here After This Will Match No Site At All, And Their Hours '
    + 'Will Be Costed To Nobody. ',
  'proj.deactivateBody':
    'It Disappears From Every Picker And Portal And Stops Taking Attendance. '
    + 'Everything Already Costed To It Stays Where It Is, And It Can Be Made '
    + 'Active Again.',
  'leaves.title': 'Leaves',
  'leaves.awaiting': 'Awaiting Decision',
  'leaves.overdue': 'Overdue Returns',
  'leaves.all': 'All Leave',
  'leaves.noTypes': 'No Leave Types Defined Yet',
  'leaves.nothingAwaiting': 'Nothing Awaiting A Decision',
  'leaves.noneRecorded': 'No Leave Recorded',
  'leaves.appearHere': 'Requests Appear Here As Employees Submit Them.',
  'leaves.recordOrWait':
    'Record Leave With The Button Above, Or Wait For Employees To Request It.',
  'leaves.everybodyBack': 'Everybody Is Back',
  'leaves.returnedOn': 'Returned On',
  'leaves.chooseEmpAndType': 'Choose An Employee And A Leave Type.',
  'leaves.endBeforeStart': 'The End Date Falls Before The Start Date.',
  'leaves.record': 'Record Leave',
  'leaves.employee': 'Employee',
  'leaves.choose': 'Choose…',
  'leaves.type': 'Leave Type',
  'leaves.paid': 'Paid Leave',
  'leaves.unpaid': 'Unpaid — These Days Will Not Be Paid',
  'leaves.days': 'Days',
  'leaves.daysHint': 'Calculated From The Dates Below',
  'leaves.from': 'From',
  'leaves.expectedBack': 'Expected Back At Work',
  'leaves.expectedBackHint':
    'Once This Date Passes With No Attendance, A Daily Alert Starts.',
  'leaves.reason': 'Reason Or Notes',
  'leaves.reasonHint': 'Annual Leave To Pakistan',
  'leaves.docRequired': 'Supporting Document (Required)',
  'leaves.doc': 'Supporting Document',
  'leaves.docHint':
    'Medical Certificate, Ticket, Or Anything Else Worth Keeping. Stored Privately.',
  'leaves.changeFile': 'Change File',
  'leaves.chooseFile': 'Choose File',
  'leaves.saving': 'Saving…',
  'leaves.reject': 'Reject This Leave Request',
  'leaves.whyRefused': 'Why It Is Refused',
  'leaves.whyRefusedHint':
    'This Is Sent To The Employee Word For Word. It Is The Only Explanation They Get.',
  'leaves.whyRefusedExample':
    'Two People From The Same Crew Are Already Off That Week.',
  'adv.title': 'Loans & Advances',
  'adv.amountIn': 'Amount ({currency})',
  'adv.lockedAmount': 'Locked Once Money Has Come Off It.',
  'adv.lockedSchedule': 'Locked — It Would Change A Schedule They Have Been Paying Against.',
  'adv.recoverInFull': 'Leave Empty To Recover It In Full Next Payroll.',
  'adv.totalOutstanding': 'Total Outstanding',
  'adv.awaitingApproval': 'Awaiting Approval',
  'adv.onRecord': 'Advances On Record',
  'adv.statement': 'Advance Statement',
  'adv.totalTaken': 'Total Taken',
  'adv.totalRepaid': 'Total Repaid',
  'adv.outstanding': 'Outstanding',
  'adv.date': 'Date',
  'adv.description': 'Description',
  'adv.taken': 'Taken',
  'adv.repaid': 'Repaid',
  'adv.balance': 'Balance',
  'adv.close': 'Close',
  'adv.correctIt': 'Correct It',
  'adv.chooseEmployee': 'Choose An Employee.',
  'adv.amountAboveZero': 'Enter An Amount Greater Than Zero.',
  'adv.record': 'Record An Advance',
  'adv.file': 'Advance File',
  'adv.fileBuilding': 'Building…',
  'adv.employee': 'Employee',
  'adv.choose': 'Choose…',
  'adv.amount': 'Amount',
  'adv.handedOver': 'Date Handed Over',
  'adv.recoverOver': 'Recover Over',
  'adv.recoverOverHint': 'Enter 1 To Take It All From The Next Payroll.',
  'adv.startFrom': 'Start Recovering From',
  'adv.startFromHint': 'Blank Means The Next Payroll Run.',
  'adv.reason': 'Reason',
  'adv.reasonExample': 'Family Emergency',
  'adv.receipt': 'Signed Receipt',
  'adv.receiptHint':
    'Photograph The Slip The Worker Signed. This Is What Settles A Dispute Two '
    + 'Years From Now.',
  'adv.changePhoto': 'Change Photo',
  'adv.attachReceipt': 'Attach Receipt',
  'adv.saving': 'Saving…',
  'adv.recordAdvance': 'Record Advance',
  'fleet.title': 'Fleet',
  'fleet.vehicles': 'Vehicles',
  'fleet.distance': 'Distance',
  'fleet.from': 'From',
  'fleet.trip': 'Trip',
  'fleet.topSpeed': 'Top Speed',
  'fleet.noSiteInRange': 'No Site Within Range',
  'fleet.noRoute': 'No Route Recorded',
  'fleet.noRouteBody':
    'This Trip Has No Track Points — Either It Was Very Short, Or The Phone Lost '
    + 'GPS Throughout.',
  'fleet.arrived': 'Arrived',
  'fleet.left': 'Left',
  'fleet.stayed': 'Stayed',
  'fleet.site': 'Site',
  'fleet.close': 'Close',
  'fleet.assignedTo': 'Assigned To',
  'fleet.edit': 'Edit',
  'fleet.papers': 'Papers',
  'fleet.issueCard': 'Issue Card',
  'fleet.reissueCard': 'Reissue Card',
  'fleet.printCards': 'Print {count} Cards',
  'fl.reissueBody':
    'The Card On {plate} Stops Working The Moment A New Code Is Issued. '
    + 'Print The New Sheet And Replace The One On The Windscreen.',
  'fleet.plateRequired': 'The Plate Number Is Required.',
  'fleet.addVehicle': 'Add Vehicle',
  'fleet.editVehicle': 'Edit {plate}',
  'fleet.papersFor': 'Papers — {plate}',
  'fleet.plate': 'Plate Number',
  'fleet.type': 'Type',
  'fleet.notSet': 'Not Set',
  'fleet.make': 'Make',

  'docs.title': 'Documents',
  'docs.allTypes': 'All Types',
  'docs.chooseFile': 'Choose A File.',
  'docs.chooseEmployee': 'Choose An Employee.',
  'docs.chooseFileUpload': 'Choose A File To Upload.',
  'bulkdoc.button': 'Upload A Folder',
  'bulkdoc.title': 'Upload Many Documents',
  'bulkdoc.blurb':
    'Choose A Whole Folder Of Scans. The File Names Say Who Each One Belongs To '
    + '— An Employee Number, An Emirates ID Or A Full Name — And Whatever Is '
    + 'Proposed Below Can Be Changed Before Anything Is Saved. A File Nobody Can '
    + 'Be Matched To Waits For You To Say Who.',
  'bulkdoc.choose': 'Choose Files',
  'bulkdoc.chooseAgain': 'Choose Other Files',
  'bulkdoc.mostlyAre': 'These Are Mostly',
  'bulkdoc.counted': '{files} Files, {placed} Matched To Somebody.',
  'bulkdoc.by.code': 'Matched By Employee Number',
  'bulkdoc.by.id-number': 'Matched By ID Number',
  'bulkdoc.by.name': 'Matched By Name',
  'bulkdoc.manyMatch': 'More Than One Person Has This Name',
  'bulkdoc.unplaced':
    '{count} File(s) Name Nobody This System Knows. Choose The Person On Each, '
    + 'Or Leave Them — They Will Not Be Uploaded.',
  'bulkdoc.send': 'Upload {count}',
  'bulkdoc.sending': 'Uploading {at} Of {of}…',
  'bulkdoc.finished': '{done} Uploaded, {failed} Failed.',
  'docs.upload': 'Upload Document',
  'docs.employee': 'Employee',
  'docs.choose': 'Choose…',
  'docs.type': 'Type',
  'docs.docTitle': 'Title',
  'docs.titleHint': 'Defaults To The File Name.',
  'docs.issuedOn': 'Issued On',
  'docs.expiresOn': 'Expires On',
  'docs.expiresHint': 'Drives The 60-Day Alert.',
  'docs.file': 'File',

  'docs.kind.passport': 'Passport',
  'docs.kind.visa': 'Residence Visa',
  'docs.kind.emirates_id': 'Emirates ID',
  'docs.kind.labour_card': 'Labour Card',
  'docs.kind.contract': 'Employment Contract',
  'docs.kind.certificate': 'Certificate Or Licence',
  'docs.kind.medical': 'Medical',
  'docs.kind.settlement': 'Employee Settlement',
  'docs.kind.other': 'Other',
  'pay.title': 'Payroll',
  'pay.noPeriods': 'No Payroll Periods Yet',
  'pay.noPeriodsBody':
    'Open A Period Covering The Month You Want To Pay. Attendance For Those '
    + 'Dates Is Already Waiting.',
  'pay.locked': 'Period Locked. Advance Balances Have Been Updated.',
  'pay.headcount': 'Headcount',
  'pay.deductions': 'Deductions',
  'pay.netPayable': 'Net Payable',
  'pay.calculating': 'Calculating…',
  'pay.recalculate': 'Recalculate',
  'pay.calculate': 'Calculate Payroll',
  'pay.notCalculated': 'Not Yet Calculated',
  'pay.everybody': 'Everybody In This Run',
  'pay.docsForOne': 'All Three Documents Below Will Cover This Person Only.',
  'pay.chooseSomebody':
    'Choose Somebody To Produce Their Documents On Their Own.',
  'pay.building': 'Building…',
  'pay.packSending': 'Emailing The Payroll Documents…',
  'pay.packSent.one': 'One Document Emailed To The Company Address.',
  'pay.packSent.other': '{count} Documents Emailed To The Company Address.',
  'pay.packSentTo': '{count} Documents Emailed To {email}.',
  'pay.packFailed': 'The Documents Were Not Emailed: {reason} The Payroll Itself Is Unaffected.',
  'pay.packRetry': 'Try Again',
  'pay.packAgain': 'Send Again',
  'pay.neverApproved.one': '1 Day In This Period Was Never Approved By The Site.',
  'pay.neverApproved.other': '{count} Days In This Period Were Never Approved By The Site.',
  'pay.reviewFrom': 'Review From {date}',
  'pay.payslips': 'Payslips',
  'pay.salariesOvertime': 'Salaries & Overtime',
  'pay.tab.statement': 'Salary Statement',
  'pay.tab.jobcost': 'Job Cost',
  'pay.tab.adjustments': 'Adjustments',
  'pay.tab.wps': 'WPS File',
  /* The advances coming off a run, and the office's say in them — 0219. */
  'pay.tab.advances': 'Advances',
  'pay.advNoneThisRun': 'No Advance Is Coming Off This Payroll',
  'pay.advNoneThisRunBody':
    'Nobody On This Run Owes An Advance. If The Payroll Has Not Been Calculated '
    + 'Yet, Calculate It First And This List Will Fill.',
  'pay.advHowItWorks':
    'Untick Somebody To Recover Nothing From Them This Month, Or Type An Amount '
    + 'To Take Instead Of The Scheduled Instalment. Whatever Is Not Recovered '
    + 'Stays On The Balance. A Decision Saved Here Survives Every Re-Calculation.',
  'pay.advRecalculate':
    'Some Recoveries Here Are Set By Hand. Calculate The Payroll Again To Put '
    + 'Them On The Payslips.',
  'pay.advEmployee': 'Employee',
  'pay.advOwed': 'Owed At Start',
  'pay.advTook': 'This Payslip Took',
  'pay.advRecover': 'Recover',
  'pay.advInstead': 'Take Instead',
  'pay.advWhy': 'Why',
  'pay.advWhyPlaceholder': 'Agreed With The Office',
  'pay.advSetByHand': 'Set By Hand',
  'pay.everyMonth': 'Every Payroll',
  'pay.everyMonthBody':
    'Each Month, What It Came To, And How Much Of It Has Actually Gone Out. Open One To Read Its Salary Statement.',
  'pay.stateOpen': 'Draft',
  'pay.stateCalculating': 'Calculating',
  'pay.stateReview': 'For Review',
  'pay.stateApproved': 'Approved',
  'pay.stateLocked': 'Locked',
  'pay.statePaid': 'Paid',
  'pay.statePartlyPaid': 'Partially Paid',
  'pay.stillOwed': '{amount} Still Owed',
  'pay.runsOfThisMonth': 'This Month Has Been Run {count} Times',
  'pay.runsBody':
    'Each Run Is Kept As The Statement It Produced, So The Figures Somebody Wrote Down Can Still Be Found After The Payroll Was Made Again. They Carry No Money: The Current Payslips Are What A Payment, A Ledger Or A Settlement Reads.',
  'pay.runNumber': 'Run {number}',
  'pay.runFinal': 'Current',
  'pay.runReplaced': 'Replaced',
  'pay.tab.payments': 'Payments',
  'pay.payNothingToPay': 'Nothing To Pay Yet',
  'pay.payNothingToPayBody':
    'This Period Has No Payslips. Calculate The Payroll And Everyone Who Is To Be Paid Will Appear Here.',
  'pay.payNetTotal': 'Net Payable',
  'pay.payPaidTotal': 'Paid',
  'pay.payOutstanding': 'Outstanding',
  'pay.paySettledCount': 'Fully Paid',
  'pay.payRecord': 'Record An Entry',
  'pay.payImport': 'Import A Sheet',
  'pay.payPrintVouchers': 'Print Vouchers',
  'pay.payVouchers': 'Vouchers',
  'pay.payFullyPaid': 'Paid',
  'pay.payPay': 'Pay',
  'pay.payAmount': 'Amount',
  'pay.payPaidOn': 'Paid On',
  'pay.payOwedIs': '{amount} Is Outstanding On This Payslip.',
  'pay.payWhatFor': 'What For',
  'pay.payWhatForPlaceholder': 'Site Allowance, Fine, Cash At The Site',
  'pay.payMethod': 'Method',
  'pay.payReference': 'Reference',
  'pay.payReferencePlaceholder': 'Transfer Or Cheque Number',
  'pay.payNote': 'Note',
  'pay.payRecorded': 'Recorded. Voucher {voucher}.',
  'pay.payEntered': 'Entered. It Goes On The Payslip At The Next Calculate.',
  'pay.methodBank': 'Bank Transfer',
  'pay.methodCash': 'Cash',
  'pay.methodCheque': 'Cheque',
  'pay.methodCard': 'Card',
  'pay.kindPayment': 'Payment',
  'pay.kindPrepayment': 'Pre-Payment',
  'pay.kindDeduction': 'Deduction',
  'pay.kindAddition': 'Addition',
  'pay.kindPaymentHow':
    'Money Going Out Against This Month’s Payslip. It Changes No Figure — It Settles One — And Cannot Be More Than Is Outstanding. Each One Gets Its Own Voucher Number.',
  'pay.kindAdjustmentHow':
    'This Changes What The Payslip Comes To, So It Is Picked Up By The Next Calculate Rather Than Paid Out Now.',
  'pay.voidPayment': 'Cancel Payment',
  'pay.voidPaymentBody':
    'Voucher {voucher} Is Marked Cancelled And Stops Counting Against The Payslip. The Number Stays In The Series, Because It Is On A Piece Of Paper Somebody Is Holding.',
  'pay.voidWhy': 'Say Why This Payment Is Being Cancelled.',
  'pay.sheetHow':
    'One Row Per Entry: Employee Id, Name, Kind, Amount, Date, Method, Reference, Note. Kind Takes Payment, Pre-Payment, Deduction Or Addition — Leave It Out And The Row Is A Payment. Nothing Is Written Until You Press Import, And Rows That Cannot Be Used Come Back As A Sheet With The Reason.',
  'pay.sheetTemplate': 'Download The Sheet',
  'pay.sheetChoose': 'Choose A File',
  'pay.sheetNoHeadings':
    'No Headings Were Found In That File. It Needs A Row With An Employee Column And An Amount Column On It — Download The Sheet Above For The Shape.',
  'pay.sheetNoEmployee': 'No Employee On The Row',
  'pay.sheetNoSuchEmployee': 'Nobody In This Payroll Matches',
  'pay.sheetNoAmount': 'No Amount',
  'pay.sheetNotANumber': 'The Amount Is Not A Number',
  'pay.sheetNotPositive': 'The Amount Is Not More Than Nothing',
  'pay.sheetUnknownKind': 'That Is Not A Kind This Sheet Takes',
  'pay.sheetBadDate': 'The Date Could Not Be Read',
  'pay.sheetWillPay': '{count} Payments, {amount} In All.',
  'pay.sheetRefused': '{count} Rows Cannot Be Used',
  'pay.sheetDownloadRefused': 'Download Them',
  'pay.sheetApply': 'Import {count} Rows',
  'pay.sheetApplied': '{ok} Rows Went In. {failed} Did Not.',
  'pay.sheetLockedPeriod':
    'This Period Is Locked, So Only Payments Can Go In. A Deduction Or An Addition Changes The Payslip And Belongs In An Open Period.',
  'pay.sheetWhoCanBeNamed':
    'The Sheet Can Only Name People Who Have A Payslip In This Period. Matched On The Employee Number First, Then On The Name.',
  'pay.settledHeading': 'Months A Settlement Has Already Paid',
  'pay.settledBody':
    'These Payslips Were Not Recalculated. Somebody Has Been Paid Against The Figure On Each Of Them, So The Run Left Them As They Were And Recorded What It Would Have Made Of Them Instead.',
  'pay.settledPaid': 'Settled At',
  'pay.settledNow': 'Now Comes To',
  'pay.settledAgrees': 'Agrees',
  'pay.settledShort': 'Paid Short',
  'pay.settledOver': 'Paid Over',
  'pay.settledCarried': 'Carried Forward',
  'pay.carryForward': 'Carry Forward',
  'pay.carryForwardBody':
    '{amount} Is Added To The Next Open Payroll For {name}, As An Adjustment Naming The Settled Month And The Settlement. The Settled Payslip Itself Is Not Touched.',
  'stl.settlement': 'Settlement',
  'pay.advWhyRequired':
    'Say Why Before Saving. It Goes On The Payslip And Into The Employee Ledger.',
  'pay.editFigures': 'Edit Figures',
  /* The figures and the two lists above the salary summary. The company's own
     payroll is what they describe; outside labour is counted beside them and
     never added in — see salarySummary, and the rule it quotes. */
  'sum.employees': 'Employees',
  'sum.gross': 'Total Gross',
  'sum.deductions': 'Deductions',
  'sum.prePaid': 'Paid Already',
  'sum.net': 'Net Payable',
  'sum.outsideAlso': 'And {count} From Outside The Company, Costing',
  'sum.ot': 'Overtime',
  'sum.absent': 'Absent',
  'sum.otTitle': 'Who Worked Overtime',
  'sum.absentTitle': 'Who Was Absent',
  'sum.leave': 'On Leave',
  'sum.leaveTitle': 'Who Was On Leave',
  'sum.movement': 'Joined Or Left',
  'sum.movementTitle': 'Who Joined Or Left This Month',
  'sum.pageTitle': 'Salary Summary',
  'sum.allOffices': 'All Offices',
  'sum.pageLede': 'Every Person, Every Figure, Office By Office.',
  'sum.pickMonth': 'Month',
  'sum.print': 'Print',
  'sum.pageBreak': 'Each Office On Its Own Page',
  'sum.outstanding': 'Outstanding',
  /* The tick on the Deductions column, and the two things it can be. */
  'sum.recoverFrom': 'Recover The Advance From',
  'sum.recoverHint': 'Untick To Recover Nothing From This Person This Month',
  'sum.nothingToRecover': 'Nothing Is Owed On Advances, So There Is Nothing To Recover',
  /* Written for the office the moment a recovery is switched off, because the
     database will not store that decision without a reason and the tick is not
     going to stop and ask. It is replaced by whatever is typed over it. */
  'sum.notRecovered': 'Not Recovered This Month',
  'sum.whyShort': 'Why…',
  'sum.remarkShort': 'Remark…',
  /* Shown while a changed figure is on its way to the payslip, and left up if
     it never got there. Ordinarily a second; a sentence that only ever said
     "being saved" would be a lie on the one occasion it matters. */
  'sum.waitingOnRun.one': 'One Figure Here Has Not Reached The Payslip Yet. Calculate The Month To Apply It.',
  'sum.waitingOnRun.other': '{count} Figures Here Have Not Reached The Payslips Yet. Calculate The Month To Apply Them.',
  'sum.nothingPaidYet': 'Nothing Has Been Paid Against This Month Yet.',
  'sum.voucher': 'Voucher',
  'sum.paidOn': 'Paid On',
  'sum.method': 'Method',
  'sum.includeOutside': 'Include {count} From Outside The Company',
  'sum.noMonths': 'No Payroll Month Has Been Created Yet.',
  'sum.colId': 'Emp ID',
  'sum.colTotal': 'Total',
  'sum.colPrePay': 'Pre-Pay',
  'sum.colNet': 'Net Amount',
  'sum.colPaidDays': 'Paid Days',
  'sum.colProf': 'Prof.',
  'sum.colSalary': 'Salary',
  'sum.colOtRate': 'OT Rate',
  'sum.colOtHours': 'OT Hrs',
  'sum.colRemarks': 'Remarks',
  'sum.totalFor': 'Total ·',
  'sum.everythingTogether': 'Everything Together',
  'sum.joined': 'Joined',
  'sum.left': 'Left',
  'sum.person': 'Person',
  'sum.hours': 'Hours',
  'sum.days': 'Days',
  'sum.amount': 'Amount',
  /* Not "Rating". It measures how much is wrong, and a word that sounds like
     a score invites back the gold stars this replaced. */
  'sum.howMuch': 'How Much',
  'sum.nobody': 'Nobody This Month.',
  'pay.editingFigures': 'Editing Figures',
  'pay.editFiguresHow':
    'The Advance And Additions Columns Can Be Clicked. Changing One Asks Why, And That Reason Goes On The Payslip And Into The Employee Ledger. Calculate The Payroll Again To Put A Change On The Payslips.',
  'pay.editAdvanceFor': 'Advance Recovery',
  'pay.editAdditionsFor': 'Additions',
  'pay.figureOnPayslip': 'On The Payslip Now',
  'pay.takeThisInstead': 'Recover This Instead',
  'pay.additionsTotal': 'Additions Come To',
  'pay.zeroTakesNothing':
    'Zero Recovers Nothing This Month. The Balance Is Untouched And Rolls Into The Next Period.',
  'pay.zeroPaysNoAdditions':
    'Zero Pays No Additions This Month. The Rows On The Adjustments Tab Are Left As They Are.',
  'pay.whyChanged': 'Why',
  'pay.whyChangedHint':
    'Required. It Is Printed On The Payslip And Saved In The Employee Ledger Against This Month, So It Is The Record Of Why The Figure Is Not The Calculated One.',
  'pay.whyChangedPlaceholder': 'On His Request, Not Recovered This Month',
  'pay.appliedOnNextRun':
    'Saved Against This Period And Kept Through Every Re-Calculation. The Payslip Changes When The Payroll Is Calculated Again.',
  'pay.backToCalculated': 'Back To The Calculated Figure',
  'pay.advSaving': 'Saving…',
  'pay.advSave': 'Save Changes',
  'pay.advUndo': 'Undo Changes',
  'pay.advSaved': 'Saved. Calculate The Payroll To Apply.',
  'pay.daysPaid': 'Days Paid',
  'pay.additions': 'Additions',
  'pay.loanLeft': 'Loan Left',
  'pay.giveCode': 'Give The Period A Code, Such As 2026-08.',
  'pay.endBeforeStart': 'The End Date Falls Before The Start Date.',
  'pay.fromHint': 'Moving This Sets The Last Day And The Code To That Month.',
  'pay.longerThanAMonth':
    'That Is More Than A Month. A Monthly Wage Is Worked Out By A Month’s Arithmetic — Across Two, The Days Of The Second Land On The Same Day Numbers As The First And The Payslip Pays One Month For Both. Run One Period Per Month.',
  'pay.openPeriod': 'Open A Payroll Period',
  'pay.code': 'Code',
  'pay.codeHint': 'Shown On Payslips. Usually The Month.',
  'pay.month': 'Payroll Month',
  'pay.monthHint': 'The Month Being Paid For. The Days Are Worked Out From It.',
  'pay.willCover': 'This Period Covers',
  'pay.codeIs': 'Called {code}',
  'pay.otherDates': 'Use Other Dates',
  'pay.toHint': 'One Month At Most. A Shorter Period Is Fine.',
  'pay.payDate': 'Pay Date',
  'pay.payDateHint': 'Optional. Used By The WPS File.',
  'pay.from': 'From',
  'pay.opening': 'Opening…',
  'pay.open': 'Open Period',
  'pay.cannotRun': 'Payroll Cannot Run Yet',
  'rep.title': 'Reports',
  'rep.intro':
    'Every Grid In The System Exports To Excel From The Screen It Lives On, With '
    + 'Whatever Filters You Have Applied. These Are The Shortcuts.',
  'rep.byScreen': 'Exports By Screen',
  'rep.byScreenBlurb':
    'Excel Or PDF, Over The Dates Below. Reports That Are A Snapshot Rather Than '
    + 'A Period Say So On The File Itself.',
  'rep.emptyRange': 'Nothing In That Range — The File Is Empty.',
  'rep.noJobCost': 'No Job Cost In That Range. Run A Payroll Period First.',
  'rep.byEmployee': 'By Employee',
  'rep.byProject': 'By Project',
  'rep.projectCost': 'Project Cost Across Periods',
  'rep.preparing': 'Preparing…',

  'rep.blurb.employees':
    'Every Employee With Designation, Documents And Joining Date.',
  'rep.blurb.attendance': 'One Day, Every Worker, With Job Number And Hours.',
  'rep.blurb.grid': 'Names Down, Days Across, Colours And Totals Intact.',
  'rep.blurb.jobcost': 'Labour Cost Per Project, Per Employee, Per Period.',
  'rep.blurb.advances': 'Outstanding Balances And Repayment History.',
  'rep.blurb.unallocated':
    'Days Paid For And Charged To No Site. The Report That Should Be Empty.',
  'rep.blurb.trips': 'Driver Journeys With Distance, Stops And Sites Visited.',
  'rep.blurb.documents': 'Passports, Visas And Contracts With Expiry Dates.',
  'rep.blurb.salary': 'A Full Payroll Period, Earnings Through To Net Payable.',
  'set.title': 'Settings',
  'set.appearance': 'Appearance',
  'set.appearanceBlurb': 'Theme, Light And Dark',
  'set.rules': 'Company Rules',
  'set.rulesBlurb': 'Every Rate Payroll Uses',
  'set.company': 'Company',
  'set.companyBlurb': 'Legal Details And WPS ID',
  'set.holidays': 'Public Holidays',
  'set.holidaysBlurb': 'Paid Days Off',
  /* ---- the offices one company works out of ----------------------- */
  'set.branches': 'Branches',
  'set.branchesBlurb': 'The Offices This Company Works Out Of, And The Code Each One Prints On A Payroll.',
  'br.title': 'Branches',
  'br.blurb': 
    'One Licence, Several Offices. Name Them Here, Choose One On Each Person\'s '
    + 'Record, And Every Payroll Document Groups Itself — The Main Company First, '
    + 'Then Each Branch Under Its Own Code. It Does Not Split The Payroll Run.',
  'br.code': 'Code',
  'br.codeHint': 'What The Documents Print. Short, And The Company\'s Own — AJM, DXB, SHJ.',
  'br.name': 'Branch Name',
  'br.nameExample': 'Ajman',
  'br.codeNeeded': 'Give The Branch A Code.',
  'br.nameNeeded': 'Give The Branch A Name.',
  'br.none': 'No Branches Yet',
  'br.noneBody': 
    'A Company With One Office Needs None Of This. Add One The Day A Second '
    + 'Office Opens, And Every Statement Starts Separating Them.',
  'br.main': 'Main',
  'br.isMain': 'The Head Office. Leads Every Statement.',
  'br.isBranch': 'A Branch. Printed Under Its Own Code, After The Main Company.',
  'br.makeMain': 'Make This The Head Office',
  'br.closed': 'Closed',
  'br.close': 'Close The Branch',
  'br.reopen': 'Reopen',
  'br.closeTitle': 'Close {code}?',
  'br.closeBody': 
    'It Leaves The Pickers So Nobody New Can Be Put On It, And Stays On Every '
    + 'Document It Already Appears On. Reopen It Whenever.',
  'br.mainExplained': 
    '{code} Is The Head Office And Leads Every Statement. Anybody Whose Branch '
    + 'Has Not Been Set Is Printed At The End, Under A Heading Of Their Own.',
  'br.noMainExplained': 
    'No Branch Is Marked As The Head Office, So Anybody Without A Branch Is The '
    + 'Main Company And Leads Every Statement.',
  'br.mainCompany': 'Main Company',
  'br.noBranch': 'No Branch Set',
  'br.branch': 'Branch',
  'br.chooseBranch': 'Choose A Branch',
  'br.branchHint': 
    'Which Office This Person Is On The Books Of. It Groups Them On The '
    + 'Payroll, The Job Cost And The Salary Statement. Leave It Empty For The '
    + 'Main Company.',
  'br.addBranch': 'Add A Branch',
  'br.adding': 'Adding…',
  'br.allBranches': 'All Branches',
  'br.together': 'All Branches Together',
  'br.branchTotal': '{code} Total',
  'set.users': 'Users & Logins',
  'set.usersBlurb': 'Who Can Sign In',
  'set.import': 'Import',
  'set.importBlurb': 'Load A Spreadsheet Of Staff, Sites Or Pay',
  'set.security': 'Security',
  'set.securityBlurb': 'Two-step Sign-in, And Who Needs It',
  'set.signIns': 'Sign-in Log',
  'set.signInsBlurb': 'Who Opened The App, And When',
  'set.activity': 'Activity Log',
  'set.activityBlurb': 'Every Change, And Who Made It',
  'set.email': 'Email',
  'set.emailBlurb': 'What Has Been Sent, And Whether It Arrived',
  'set.notifications': 'Notifications',
  'set.notificationsBlurb': 'Who Is Told What, And By Which Route',
  'set.selfCheck': 'Self-check',
  'set.selfCheckBlurb': 'Does This Phone Measure Right, And Do The Figures Agree',

  'app.themeFailed':
    'The Theme Could Not Be Saved. Your Previous Theme Has Been Restored.',
  'app.companyTheme': 'Company Theme',
  'av.title': 'Your Picture',
  'av.subtitle': 'Optional. It Appears Beside Your Name Wherever People Are Listed.',
  'av.choose': 'Choose A Picture',
  'av.change': 'Change Picture',
  'av.remove': 'Remove',
  'av.hint': 'A Photograph Of Your Face Works Best. It Is Reduced To A Small Square On This Device Before It Is Sent, So It Costs Almost Nothing To Upload.',
  'av.failed': 'That Picture Could Not Be Saved.',
  'app.companyThemeSub': 'Applies Across The Dashboard And Every Field Phone',
  'app.yourColours': 'Your Own Colours',
  'app.yourColoursSub':
    'Only On This Device, And Only For You. Everyone Else Keeps The Company Theme.',
  'app.lightAndDark': 'Light And Dark',
  'app.lightAndDarkSub': 'Your Own Preference, On This Account',
  'app.quickBar': 'Quick Bar On Your Phone',

  'chk.title': 'Self-check',
  'chk.subtitle':
    'What This Phone Can Measure, And Whether The Numbers Already Recorded Agree '
    + 'With One Another.',
  'chk.attendanceHours': 'Attendance And Hours',
  'chk.locationTrips': 'Location And Trips',
  'chk.running': 'Running…',
  'chk.runAgain': 'Run Again',
  'chk.findingYou': 'Finding You…',
  'chk.measureHere': 'Measure The Location Here',
  'chk.thisPhone': 'This Phone',
  'chk.allAgree': 'Every Figure That Has To Agree With Another One Does.',

  'log.signInHistory': 'Sign-in History',
  'log.signInHistorySub':
    'Recorded In The Database At The Moment Of Sign-in. Device And Build Are '
    + 'Reported By The App Itself.',

  'imp.columnInFile': 'Column In Your File',
  'imp.whatIsInIt': 'What Is In It',
  'imp.goesTo': 'Goes To',
  'imp.notFromFile': 'Not Coming From This File: ',
  'imp.historyUnread': 'The Import History Could Not Be Read',
  'imp.soFar': 'Imports So Far',
  'imp.soFarSub': 'Every File Anybody Has Put Through, Finished Or Not.',
  'imp.nothingYet': 'Nothing Imported Yet',
  'imp.nothingYetBody':
    'Runs Appear Here With What They Did And Anything They Refused.',
  'imp.undo': 'Undo The Import',
  'imp.closedByHand': 'Closed By Hand.',
  'imp.readingRefusals': 'Reading The Refusals…',
  'app.openMenu': 'Open Appearance Menu',
  'chk.group.money': 'Money',
  'chk.group.faces': 'Faces',
  'chk.group.leave': 'Leave',
  'co.profile': 'Company Profile',
  'co.profileSub': 'Appears On Payslips And Drives The WPS Export',
  'co.tradingName': 'Trading Name',
  'co.tradingNameHint':
    'Shown In The Sidebar. Used On Documents Only When There Is No Legal Name.',
  'co.legalName': 'Legal Name',
  'co.legalNameHint': 'As Registered On The Trade Licence.',
  'co.emailLocale': 'Language For Emails',
  'co.emailLocaleHint': 'Which Language Messages Are Written In. Separate From The Document Setting Above — That Decides Whether A Payslip Carries Both Languages; This Decides The One Language A Message Is Written In.',
  'co.emailLocaleEach': 'Each Person’s Own Language',
  'co.emailLocaleEn': 'English, For Everybody',
  'co.emailLocaleAr': 'Arabic, For Everybody',
  'co.offerArabic': 'Offer Arabic On Exports',
  'co.offerArabicHint':
    'Every PDF And Spreadsheet Asks Which Language It Should Be In. Arabic '
    + 'Produces The Whole Document In Arabic, Not A Mixture. Names, Sites And '
    + 'Anything Typed By A Person Stay Exactly As They Were Written. Turn It Off '
    + 'And Exports Are English Without Asking.',
  'co.askEachTime': 'Ask Each Time — English Or العربية',
  'co.englishOnly': 'English Only, No Question Asked',
  'co.nameAr': 'Name In Arabic',
  'co.nameArHint':
    'Typed, Never Translated: The Arabic Name Of A Business Is What Its Trade '
    + 'Licence Says, Not Something Derived From The English. Left Blank, Nothing '
    + 'Is Printed.',
  'co.addressAr': 'Address In Arabic',
  'co.addressArHint': 'Printed Under The Arabic Name, On The Same Side.',
  'co.address': 'Address',
  'co.addressHint':
    'One Line, As It Appears On The Trade Licence. Printed Under The Letterhead.',
  'co.phone': 'Telephone',
  'co.phoneHint': 'Printed On The Documents This Company Issues.',
  'co.email': 'Email',
  'co.emailHint': 'Where A Question About A Document Should Go.',
  'co.website': 'Website',
  'co.settlementContact': 'Settlement Contact',
  'co.settlementContactHint':
    'Whoever Handles Settlements, Named On The Settlement Itself. Somebody '
    + 'Querying Years Of Service Needs A Person To Ask For.',
  'co.settlementContactPhone': 'Settlement Contact Number',
  'co.tradeLicence': 'Trade Licence Number',
  'co.molId': 'MOHRE Establishment ID',
  'co.molIdHint': '13 Digits. Without It, No WPS File Can Be Generated.',
  'co.molIdMissing': 'Not Set — WPS Export Is Blocked',
  'co.currency': 'Currency',
  'co.dateFormat': 'Date Format',
  'co.dateFormatHint': 'Every Date On Every Screen And Every Document. Nothing To Do With The Timezone Below. An Arabic Export Keeps The Same Arrangement',
  'co.removeHoliday': 'Remove {name}?',
  'co.removeHolidayAria': 'Remove {name}',
  'co.leaveWarn': 'Warn About A Return From Leave',
  'co.leaveWarnHint':
    'How Many Days Before An Approved Return Date The Daily Reminders Start. Set '
    + 'It Generously Where A Ticket Has To Be Bought In Advance — The Warning Is '
    + 'Only Useful While There Is Still Time To Act On It. Zero Switches It Off.',
  'co.timeFormat': 'Time Format',
  'co.timeFormatHint':
    'Every Time On Every Screen. 17:18 Or 5:18 Pm — Whichever The Office Reads '
    + 'Without Thinking.',
  'co.timezone': 'Timezone',
  'co.timezoneHint': 'Decides Which Day A Punch Belongs To.',
  'co.saving': 'Saving…',
  'co.saveChanges': 'Save Changes',
  'co.holidayName': 'Give The Holiday A Name.',
  'co.holidayStart': 'Choose A Start Date.',
  'co.holidayEndBefore': 'The End Date Falls Before The Start.',
  'co.holidays': 'Public Holidays',
  'co.holidaysSub':
    'Attendance On These Days Is Paid Without Counting As Absence',
  'co.name': 'Name',
  'co.holidayExample': 'Eid Al Fitr',
  'co.from': 'From',
  'co.sameDay': 'Same Day If Left Blank',
  'co.noHolidays': 'No Holidays Entered',
  'co.noHolidaysBody':
    'The System Presumes No Calendar — Eid Moves Every Year And Every Company '
    + 'Observes A Slightly Different List.',
  'co.holidayPast':
    'This Date Has Passed. Removing It Changes How Any Month Containing It '
    + 'Recalculates.',
  'co.holidayOrdinary':
    'Attendance On This Date Will Be Treated As An Ordinary Working Day.',
  'del.notAvailable': 'Not Available',
  'del.notAvailableBody': 'This Requires The Super Admin Permission.',
  'del.noSuchRecord': 'Nothing Of That Kind Exists With That Id In This Company.',
  'del.title': 'Permanent Deletion',
  'del.subtitle':
    'Destroys A Record And Everything That Hangs Off It. There Is No Undo And No '
    + 'Copy Kept.',
  'del.kind': 'Kind Of Record',
  'del.id': 'Its Id',
  'del.idHint': "The Uuid, Copied From The Record's Own Page.",
  'del.typeName': 'Type Its Name Exactly',
  'del.typeNameHint':
    'Its Own Name, Not A Fixed Word — A Phrase That Is The Same Every Time Is One '
    + 'The Fingers Learn.',
  'del.why': 'Why',
  'del.whyHint':
    'The Only Thing That Survives This. Write What Somebody Reading The Log In A '
    + 'Year Needs To Know.',
  'del.whyExample': 'Test Record Created During Setup, Never A Real Employee',
  'del.password': 'Your Password',
  'del.passwordHint': 'Checked By The Database, Not By This Page.',
  'del.eraseName': 'Erase The Name From The Log Too',
  'del.eraseNameHint':
    'For A Data-protection Erasure, Where Keeping The Name On Record Would Be The '
    + 'Thing The Erasure Was For. The Line Stays; The Name Does Not.',
  'del.deleting': 'Deleting…',
  'del.confirm': 'Yes, Destroy It',

  'del.kind.employee': 'Employee',
  'del.kind.project': 'Project',
  'del.kind.payslip': 'Payslip',
  'del.kind.payroll_period': 'Payroll Period',
  'del.kind.attendance_day': 'Attendance Day',
  'del.kind.attendance_segment': 'Shift',
  'del.kind.face_scan': 'Face Scan',
  'del.kind.face_template': 'Face Template',
  'del.kind.trip': 'Trip',
  'del.kind.vehicle': 'Vehicle',
  'del.kind.vehicle_document': 'Vehicle Document',
  'del.kind.advance': 'Advance',
  'del.kind.leave_request': 'Leave Request',
  'del.kind.document': 'Employee Document',
  'del.kind.rollcall_session': 'Roll-call Session',
  'del.kind.holiday': 'Public Holiday',
  'del.kind.device': 'Device',
  'del.kind.user_profile': 'User Login',
  'del.kind.company': 'The Entire Company',
  'rules.title': 'Company Rules',
  'rules.notForYou': 'Company Rules Are Not Open To Your Role',
  'rules.complete': 'Complete',
  'rules.draftSaved': 'Draft Saved.',
  'rules.takeOutOfForce': 'Take These Rules Out Of Force?',
  'rules.deactivate': 'Deactivate',
  'rules.noLongerInForce': 'Rules Are No Longer In Force.',
  'rules.effectiveFrom': 'These Rules Take Effect From',
  'rules.effectiveFromHint': 'Earlier Months Keep The Version That Produced Them.',
  'rules.saving': 'Saving…',
  'rules.saveDraft': 'Save Draft',
  'rules.working': 'Working…',
  'rules.takeOut': 'Take Out Of Force',
  'rules.activating': 'Activating…',
  'rules.activateVersion': 'Activate This Version',
  'rules.activate': 'Activate Rules',
  'rules.choose': 'Choose…',
  'rules.notSetYet': 'Not Set Yet',
  'rules.somethingWrong': 'Something Went Wrong.',
  'rules.code': 'Code',
  'rules.codeHint': 'Short, E.g. ANNUAL',
  'rules.name': 'Name',
  'rules.leaveExample': 'Annual Leave',
  'rules.daysPerMonth': 'Days Earned Per Month Of Service',
  'rules.daysPerMonthHint':
    '2.5 Is Thirty Days A Year. Enter 0 If It Does Not Accrue.',
  'rules.days': 'Days',
  'rules.accruesAfter': 'Accrues Only After',
  'rules.accruesAfterHint':
    'Months Of Service Before Anything Is Earned. 0 Accrues From The First '
    + 'Month; 12 Makes It Due After A Year.',
  'rules.months': 'Months',
  'rules.maxCarry': 'Maximum Days Carried Into Next Year',
  'rules.paidLeave': 'Paid Leave',
  'rules.paidLeaveHint': 'Turn Off For Unpaid Types.',
  'rules.needsDocument': 'Requires A Document',
  'rules.needsDocumentHint': 'A Medical Certificate, For Example.',
  'rules.needsApproval': 'Needs Approval',
  'rules.countsService': 'Counts Toward Length Of Service',
  'rules.countsServiceHint': 'Affects Gratuity Accrual.',
  'rules.payBands': 'Pay Bands',
  'rules.fromDay': 'From Day',
  'rules.toDay': 'To Day',
  'rules.paidAt': 'Paid At',
  'rules.removeBand': 'Remove Band',
  'rules.accrualTiers': 'Accrual Tiers',
  'rules.fromYear': 'From Year',
  'rules.toYear': 'To Year',
  'rules.toYearHint': 'Blank = Onwards',
  'rules.daysPayPerYear': 'Days Of Pay Per Year',
  'rules.removeTier': 'Remove Tier',
  'rules.components': 'Allowances And Components',
  'rules.componentExample': 'Housing Allowance',
  'rules.type': 'Type',
  'rules.deduction': 'Deduction',
  'rules.countsOvertime': 'Counts Toward Overtime',
  'rules.countsGratuity': 'Counts Toward Gratuity',
  'rules.paidInFull': 'Paid In Full Monthly',
  'rules.paidInFullHint': 'Rather Than Pro-rated By Days Worked.',
  'rules.until': 'Until',
  'rules.minutesLong': '{minutes} Minutes',
  'rules.endsBeforeStart': 'Ends Before It Starts',
  'rules.paidBreak': 'Paid Break',
  'rules.paidBreakHint':
    'Off Is Usual — The Gap Between Two Shifts Is Already Unpaid.',
  'usr.linkedEmployee': 'Linked Employee',
  'usr.lastSeen': 'Last Seen',
  'usr.deleteLogin': 'Delete This Login',
  'usr.enterName': 'Enter Their Name.',
  'usr.enterEmail': 'Enter An Email Address.',
  'usr.chooseRole': 'Choose A Role.',
  'usr.passwordShort': 'The Password Must Be At Least 8 Characters.',
  'usr.mustLink':
    'An Employee Account Must Be Linked To An Employee Record, Or It Will Show '
    + 'Nothing.',
  'usr.created': 'Login Created',
  'usr.password': 'Password',
  'usr.copyBoth': 'Copy Both',
  'usr.done': 'Done',
  'usr.create': 'Create A Login',
  'usr.fullName': 'Full Name',
  'usr.email': 'Email',
  'usr.username': 'Username',
  'usr.usernameHint': 'Optional. Something Short To Type At A Sign-in Instead Of The Whole Address — No Spaces, And The Address Is Still What The Account Is. It Only Has To Be Unique Inside Your Own Company.',
  'usr.emailRequiredHint': 'Required. Where A Password Reset Goes, And Where Anything The System Sends Them Lands. An Account Without One Can Do Neither.',
  'usr.noEmailWarning': 'No Email — Cannot Reset A Password Or Be Sent Anything',
  'usr.usernameExample': 'Ali.raza',
  'usr.canSignIn': '{name} Can Now Sign In',
  'usr.copied': 'Copied',
  'usr.deleteTitle': 'Delete The Login For {name}?',
  'usr.deleteAria': 'Delete The Login For {name}',
  'usr.emailHint': 'Used To Sign In. It Does Not Have To Be Reachable.',
  'usr.role': 'Role',
  'usr.roleHint': 'Decides What They Can Reach.',
  'usr.choose': 'Choose…',
  'usr.linkRequired': 'Required — This Is Whose Record They Will See.',
  'usr.linkOptional':
    'Optional For Office Staff. Needed For Roll-call And Self-service.',
  'usr.notLinked': 'Not Linked',
  'usr.passwordHint': 'Shown Once. Give It To Them Directly.',
  'usr.creating': 'Creating…',
  'usr.createLogin': 'Create Login',
  'usr.newPassword': 'New Password',
  'usr.newPasswordHint': 'At Least 8 Characters.',
  'usr.changing': 'Changing…',

  'act.title': 'Activity Log',
  'act.noPermission': 'You Do Not Have Permission To View This.',
  'act.subtitle':
    'Every Change To People, Pay, Attendance And Projects. Recorded By The '
    + 'Database Itself, And Not Editable By Anyone.',
  'act.system': 'System',
  'act.anyAction': 'Any Action',
  'act.anything': 'Anything',

  'eml.sending': 'Sending',
  'eml.sendingSub':
    'NoorOne Carries Every Message And Keeps The Account Behind It. There Is '
    + 'Nothing To Set Up Here And Nothing That Can Be Left Half-configured.',
  'eml.switchedOn': 'Email Is Switched On',
  'eml.switchedOnHint':
    'When Off, Nothing Is Sent — And Every Attempt Is Still Logged, With The '
    + 'Reason.',
  'eml.fromName': 'From Name',
  'eml.fromNameHint': 'Shown As The Sender.',
  'eml.fromAddress': 'From Address',
  'eml.fromAddressHint':
    'Set By NoorOne And Verified Once, For Every Company.',
  'eml.replyTo': 'Reply-to',
  'eml.replyToHint': 'Optional. Where Replies Should Land.',
  'eml.sendTest': 'Send A Test',
  'eml.sendTestSub':
    'The Fastest Way To Find Out Whether Your Sender Address Is Actually Verified.',
  'eml.sendTo': 'Send To',
  'eml.clearLog': 'Clear The Log',
  'eml.clearTitle': 'Remove All {count} Entries?',
  'eml.clearBody': 'The Record Of What Was Sent Goes With Them. What Was Actually Delivered Does Not Change, And The Removal Itself Is Written To The Activity Log.',
  'eml.clearConfirm': 'Clear It',
  'eml.removeRow': 'Remove This Entry',
  'eml.deliveryLog': 'Delivery Log',
  'eml.deliveryLogSub':
    'Every Attempt, Successful Or Not. Written Before The Provider Is Called, So '
    + 'A Message That Vanishes Still Leaves A Trace.',
  'ntf.title': 'Notifications',
  'ntf.superOnly': 'Super Admins Only',
  'ntf.superOnlyBody':
    'Deciding Who Is Told When Somebody Never Clocked Out, Or When A Payroll Is '
    + 'Waiting, Is Kept Separate From The Other Settings.',
  'ntf.unreadable': 'The Notification Settings Could Not Be Read',
  'ntf.whereItGoes': 'Where It Goes',
  'ntf.quietNow': 'It Is {time} — Inside Quiet Hours, So No Phone Would Buzz Right Now.',
  'ntf.audienceMode': 'Who This Goes To',
  'ntf.audienceModeHint': 'A Permission Survives People Joining And Leaving; A List Of Names Does Not, And Is Sometimes Still The Honest Answer.',
  'ntf.byPermission': 'Whoever Holds A Permission',
  'ntf.byRole': 'These Roles',
  'ntf.byPerson': 'These People, By Name',
  'ntf.whichRoles': 'Which Roles',
  'ntf.whichRolesHint': 'Everybody Holding One Of These, As The Roles Change Hands.',
  'ntf.noRolesPicked': 'No Roles Yet. Add One In Users & Logins First.',
  'ntf.whichPeople': 'Which People',
  'ntf.whichPeopleHint': 'Named, And Only These. Somebody Who Leaves Has To Be Taken Off Here By Hand — Which Is The Cost Of Naming Names.',
  'ntf.noPeoplePicked': 'Nobody To Choose From Yet.',
  'ntf.noMailbox': 'No Mailbox',
  'ntf.whoIsTold': 'Who Is Told',
  'ntf.whoIsToldHint':
    'A Permission, So It Survives People Joining And Leaving.',
  'ntf.choose': 'Choose…',
  'ntf.howUrgent': 'How Urgent',
  'ntf.information': 'Information',
  'ntf.worthActing': 'Worth Acting On',
  'ntf.quietFrom': 'Quiet From',
  'ntf.quietFromHint': 'Phone Stays Silent. The Bell Still Records It.',
  'ntf.quietUntil': 'Quiet Until',
  'ntf.ch.inApp': 'Inside The App. Always The Record.',
  'ntf.ch.push': 'Needs The Installed App And A Registered Handset.',
  'ntf.ch.email': 'Sent To Their Email Address, Where They Have One.',

  'imp.title': 'Import From A Spreadsheet',
  'imp.nothingForYou': 'Nothing Here For You',
  'imp.nothingForYouBody':
    'Importing Needs The Grant For Whatever Is Being Imported — The Staff File, '
    + 'Projects, Or Pay.',
  'imp.didNotWork': 'That Did Not Work',
  'imp.settingsChanged': 'Checked, Then The Settings Changed.',
  'imp.readingFile': 'Reading The File…',
  'imp.nothingReadable': 'There Is Nothing Readable In That File.',
  'imp.checkingRows': 'Checking Every Row Against The Database…',
  'imp.importing': 'Importing…',
  'imp.refused': 'Refused By The Database.',
  'imp.leaveColumnOut': '— Leave This Column Out —',
  'imp.needed': 'Needed',
  'imp.whatImporting': 'What Are You Importing?',
  'imp.fileTypes':
    'Excel (.Xlsx) Or CSV. Nothing Leaves Your Browser Until You Press Check.',
  'imp.howTreated': 'How Should Existing Records Be Treated?',
  'imp.chooseAFile': 'Choose A File',
  'imp.theFile': 'The File',
  'imp.sheet': 'Sheet',
  'imp.headingRow': 'Which Row Holds The Headings',
  'imp.headingRowHint':
    'Most Files Have A Title Above The Table. This Is A Guess — Change It If It '
    + 'Is Wrong.',
  'imp.pointColumns': 'Point Each Column At A Field',
  'imp.pointColumnsSub':
    'The Values Beside Each Column Are What The System Read From It. Check The '
    + 'Dates.',
  'imp.fixFirst': 'Fix These Before Checking',
  'imp.alreadyImported': 'This Exact File Has Been Imported Before',
  'imp.whatWouldHappen': 'What Would Happen',
  'imp.whatWouldHappenSub':
    'Every Row Below Was Really Written And Then Undone. These Are Results, Not '
    + 'Estimates.',
  'imp.created': 'Created',
  'imp.updated': 'Updated',
  'imp.leftAlone': 'Left Alone',
  'imp.refusedCount': 'Refused',
  'imp.datesBothWays': 'The Dates In This File Are Written Both Ways Round',
  'imp.readAs': 'Read As ',

  'blk.created': 'Logins Created',
  'blk.code': 'Code',
  'blk.name': 'Name',
  'blk.signInWith': 'Sign In With',
  'blk.result': 'Result',
  'blk.done': 'Done',
  'blk.createBulk': 'Create Logins In Bulk',
  'blk.role': 'Role',
  'blk.chooseRole': 'Choose A Role',
  'blk.filter': 'Filter',
  'blk.filterHint': 'Code, Name, Trade Or Email…',
  'blk.employeeCode': 'Employee Code',
  'blk.password': 'Password',
  'eb.title': 'How {name} Is Employed',
  'eb.where': 'Where They Sit',
  'eb.whereHint':
    'A Temporary Worker Is Somebody The Company Pays And Does Not Employ. '
    + 'Their Documents Carry No Company Name And Their Cost Is Reported Apart '
    + 'From The Payroll.',
  'eb.onPayroll': 'On The Payroll',
  'eb.temporary': 'Temporary',
  'eb.basis': 'Wage Worked Out',
  'eb.basisHint':
    'Monthly Is A Salary Divided Into Days By The Company Rules. Daily Pays '
    + 'Only The Days Actually Worked.',
  'eb.monthly': 'Monthly',
  'eb.daily': 'Daily',
  'eb.reason': 'Reason',
  'eb.reasonHint': 'Optional, And Kept In The History Beside The Change.',
  'eb.pastStays':
    'Nothing Already Recorded Changes. The Days They Have Worked Keep The '
    + 'Figures They Were Worked Out With, And Payslips Already Issued Stay As '
    + 'They Are. This Decides How They Are Paid From Now On.',
  'eb.save': 'Save',
  'eb.saving': 'Saving…',
  'eb.change': 'Employment',
  'eb.payrollOnly': 'Set By The Payroll Office Or A Super Admin.',
  'fl.title': 'Who Is This?',
  'fl.subtitle': 'Scan A Face To See Whether This Person Has Worked Here Before.',
  'fl.standInFront': 'Have Them Stand In Front Of The Camera, Then Scan.',
  'fl.scan': 'Scan A Face',
  'fl.scanning': 'Looking…',
  'fl.cameraStarting': 'Starting The Camera…',
  'fl.lookAtCamera': 'Ask Them To Look At The Camera.',
  'fl.facesOnFile': '{count} Faces On File, Including People Who Have Left.',
  'fl.notOnFile': 'Not On File',
  'fl.notOnFileBody':
    'Nobody Enrolled Here Matches This Face. If They Are New, Take Them On '
    + 'From The Roll-Call Screen And Enrol Their Face Then.',
  'fl.ambiguous': 'Could Not Say Who This Is',
  'fl.ambiguousBody':
    'Two Reads Of This Face Named Two Different People, So Nothing Is Being '
    + 'Claimed. Move Somewhere Lighter And Scan Again.',
  'fl.unsure': 'The Scan Was Not Clear Enough',
  'fl.unsureBody':
    'This Face Was Read Too Poorly To Say Whose It Is, So Nothing Is Being '
    + 'Claimed Either Way. It Does Not Mean They Are Not On File. Face The '
    + 'Camera Squarely, In Better Light, And Scan Again.',
  'fl.working': 'Working Here',
  'fl.left': 'Has Left',
  'fl.joined': 'Joined',
  'fl.lastWorked': 'Last Worked',
  'fl.leftOn': 'Left On',
  'fl.daysWorked': 'Days Worked',
  'fl.reasonGiven': 'Reason Given:',
  'fl.alreadyWorking': 'They Are Already On The Books And Working Here. Nothing To Do.',
  'fl.takeOnAgainWhy':
    'They Are Already On File. Taking Them On Again Keeps This Record, This '
    + 'Employee ID And Everything They Worked Before — No Second Record, And '
    + 'No Name To Type Again.',
  'fl.takeOnAgain': 'Take Them On Again',
  'fl.takingOn': 'Taking On…',
  'fl.dashboard': 'Who Is This?',
  'fl.dashboardHint': 'Scan A Face To Check Whether Somebody Has Worked Here Before.',
  'et.title': 'History',
  'et.subtitle': 'Every Change To This Record, Newest First — Who Made It And When.',
  /* The button under a list that is showing only its first handful. */
  'more.show.one': 'Show 1 More',
  'more.show.other': 'Show {count} More',
  'more.fewer': 'Show Fewer',
  'more.andMore.one': 'And 1 More',
  'more.andMore.other': 'And {count} More',
  'et.empty': 'Nothing Has Been Recorded Against This Person Yet.',
  'et.somebody': 'Somebody',
  'et.added': 'Added This Person',
  'et.addedTemporary': 'Took This Person On As A Temporary Worker',
  'et.changed': 'Changed This Record',
  'et.deleted': 'Deleted This Record',
  'et.setPay': 'Set Their Pay',
  'et.changedPay': 'Changed Their Pay',
  'et.nothingWorthReporting': 'Nothing Worth Reporting Changed.',
  'cost.title': 'What It Cost',
  'cost.subtitle': 'A Project, A Work Group, And A Stretch Of Time.',
  'cost.project': 'Project',
  'cost.allProjects': 'All Projects',
  'cost.group': 'Work Group',
  'cost.allGroups': 'All Groups',
  'cost.noGroup': 'Not In A Group',
  'cost.grain': 'Shown By',
  'cost.allTime': 'All Time',
  'cost.yearly': 'Year',
  'cost.quarterly': 'Quarter',
  'cost.monthly': 'Month',
  'cost.daily': 'Day',
  'cost.from': 'From',
  'cost.to': 'To',
  'cost.period': 'Period',
  'cost.days': 'Days',
  'cost.normalHours': 'Normal Hours',
  'cost.otHours': 'Overtime Hours',
  'cost.total': 'Total',
  'cost.dailyIsHours':
    'By Day, This Shows Hours Rather Than Money. Hours Are Allocated To A Job '
    + 'Number As They Are Worked; What They Cost Is Settled When The Month Is '
    + 'Run, So A Daily Figure Would Not Match The Payslip It Is Meant To '
    + 'Explain.',
  'cost.nothing': 'Nothing In That Range',
  'cost.nothingBody':
    'No Cost Has Been Recorded For These Filters. Cost Appears Once The '
    + 'Payroll For That Month Has Been Run.',
  'ef.group': 'Work Group',
  'ef.groupHint':
    'The Kind Of Work This Person’s Crew Does — Electrical, Civil, Plumbing. '
    + 'Used To Report What A Trade Costs On A Project.',
  'ef.chooseGroup': 'Choose A Group',
  'ef.addGroup': 'Add Group',
  'ef.addingGroup': 'Adding…',
  'ef.groupNamePlaceholder': 'Electrical, Civil, Plumbing…',
  'ef.leaveSalaryAmount': 'Agreed Leave Salary',
  'ef.leaveSalaryAmountHint':
    'Leave Empty To Calculate It From The Days Earned. A Figure Here Is Paid '
    + 'Instead, And The Calculation Is Still Printed Beside It.',
  'ef.gratuityAmount': 'Agreed Gratuity',
  'ef.gratuityAmountHint':
    'Leave Empty To Calculate It From The Service Bands. A Figure Here Is Paid '
    + 'Instead, And The Bands Are Still Printed Beside It.',
  'ef.rules': 'Pay Rules',
  'ef.rulesHint': 'How This Person Is Paid, And What They Are Owed On Leaving. Set By The Payroll Office.',
  'ef.markingRules': 'Marking Rules',
  'ef.markingRulesHint': 'How This Person Is Recorded Present, And Whether Their Journeys Are Tracked.',
  'ef.overtimeCounts': 'Overtime',
  'ef.overtimeCountsHint':
    'Turn Off For Somebody Engaged On A Figure That Covers Whatever Hours The Job Takes. Their Hours Are Still Recorded On The Attendance Sheet And Against The Job Number — They Are Simply Not Paid As Overtime, And No Overtime Line Appears On Their Payslip.',
  'ef.overtimePaid': 'Overtime Is Paid',
  'ef.overtimeNotPaid': 'Overtime Is Not Paid',
  'ef.required': 'The Full Name And The Joining Date Are Both Required.',
  'ef.saveFailed': 'Could Not Save.',
  'ef.add': 'Add Employee',
  'ef.identity': 'Identity',
  'ef.employeeId': 'Employee ID',
  'ef.employeeIdEdit':
    'Shown On The Payslip. Changing It Registers The New One; The Old One Is '
    + 'Never Given To Anybody Else.',
  'ef.employeeIdNew':
    'Leave It Empty And The Next Free Number Is Issued. A Number Is Never Reused, '
    + 'So This Cannot Collide With Somebody From Two Years Ago.',
  'ef.issuedAuto': 'Issued Automatically',
  'ef.designation': 'Designation',
  'ef.notSet': 'Not Set',
  'ef.fullName': 'Full Name',
  'ef.nameAr': 'Name In Arabic',
  'ef.nameArHint': 'Appears On Arabic Payslips.',
  'ef.nationality': 'Nationality',
  'ef.dateOfBirth': 'Date Of Birth',
  'ef.documents': 'Documents',
  'ef.emiratesId': 'Emirates ID',
  'ef.labourCard': 'Labour Card Number',
  'ef.labourCardHint': '14 Digits. Required For The WPS File.',
  'ef.passportNo': 'Passport Number',
  'ef.passportExpiry': 'Passport Expiry',
  'ef.visaExpiry': 'Visa Expiry',
  'ef.contact': 'Contact',
  'ef.mobile': 'Mobile',
  'ef.email': 'Email',
  'ef.emailHint': 'Needed If They Are To Have A Self-service Login.',
  'ef.homePhone': 'Home Country Phone',
  'ef.workPhone': 'Work Phone',
  'ef.homeAddress': 'Home Country Address',
  'ef.accommodation': 'Accommodation In UAE',
  'ef.emergencyContact': 'Emergency Contact',
  'ef.emergencyPhone': 'Emergency Contact Phone',
  'ef.employment': 'Employment',
  'ef.joiningDate': 'Joining Date',
  'ef.probationEnds': 'Probation Ends',
  'ef.status': 'Status',
  'ef.statusHint':
    'Left And Inactive Both Stamp A Leaving Date, Which Takes Them Out Of Payroll '
    + 'And Attendance From That Day. Nothing Already Paid Or Costed To A Site Is '
    + 'Touched.',
  'ef.leftCompany': 'Left The Company',
  'ef.lastWorkingDayHint':
    'The Date Everything Is Worked Out To. Left Blank It Is Stamped With Today, '
    + 'Which On Somebody Who Went Home Three Weeks Ago Is Three Weeks Of Service '
    + 'Nobody Worked.',
  'ef.statusLeftNote':
    'The Leaving Date Below Is What Stops Everything: Gratuity Stops Earning On It, '
    + 'Leave Stops Accruing On It, And Every Payroll From That Date Leaves Them Out. '
    + 'It Is The Date They Actually Went, Not The Day You Type This — A Settlement '
    + 'Prepared Or Paid Two Months Later Owes Nothing For Those Two Months.',
  'ef.statusInactiveNote':
    'Stood Down Rather Than Resigned, And Treated The Same Way By The Money: The Date '
    + 'Below Takes Them Out Of Payroll And Attendance, And Gratuity And Leave Stop '
    + 'Earning On It. Everything Already Paid, And Every Hour Already Costed To A '
    + 'Site, Stays Where It Is.',
  'ef.statusSuspendedNote':
    'The Dates Below Are Recorded As Unpaid Days, And That Is What Stops The '
    + 'Clock: Nothing Is Paid For Them, And They Do Not Count Toward The Service '
    + 'That Earns Gratuity. It Starts Again On The Day They Come Back — Shorten '
    + 'The Suspension And Those Days Are Service Again.',
  'ef.statusBackNote':
    'The Unpaid Period Closes The Day Before They Came Back, However Long It Was '
    + 'Written For. Pay, Service And The Leave Accrual All Carry On From That Day.',
  'ef.suspendFrom': 'Suspended From',
  'ef.suspendTo': 'Last Day Of The Suspension',
  'ef.suspendToHint':
    'Write The Date You Expect. Correct It On The Day They Come Back — A Day Given '
    + 'Back Is Service And Pay Again.',
  'ef.suspendWhy': 'Why',
  'ef.suspendWhyExample': 'Kept With The Record',
  'ef.suspendNeedsDates':
    'A Suspension Needs A First Day And A Last Day. Those Dates Are What Stop The '
    + 'Pay And The Service; Without Them Nothing Stops.',
  'ef.suspendBackwards': 'The Suspension Ends Before It Begins.',
  'ef.backOn': 'Came Back On',
  'ef.backOnHint': 'The Day Pay And Service Start Again.',
  'ef.backOnNeeded': 'Say Which Day They Came Back.',
  'ef.statusOnLeaveNote':
    'A Marker For The Roster. The Days Themselves Come From The Leave Register: Paid '
    + 'Leave Is Service And Goes On Earning Gratuity, Unpaid Leave Does Not. Nothing '
    + 'New Is Earned While They Are Away, So The Leave Accrual Picks Up Where It Left '
    + 'Off On The Day They Come Back.',
  'ef.inactiveStoodDown': 'Inactive — Stood Down',
  'ef.employmentType': 'Employment Type',
  'ef.fullTime': 'Full Time',
  'ef.partTime': 'Part Time',
  'ef.contract': 'Contract',

  /* An outside worker, and how their wage is worked out. Two separate
     questions — a man taken on for a full month at an agreed monthly figure is
     an outside worker on a monthly wage, and paying him by the day because one
     switch meant both would be a different amount of money. */
  'ef.outsideWorker': 'Outside Worker',
  'ef.outsideWorkerHint':
    'Somebody This Company Pays But Does Not Employ — Daily And Short-term '
    + 'Workers Taken On Through A Subcontractor Or Off The Street. Their '
    + 'Documents Carry No Company Name, And Their Cost Is Reported Separately '
    + 'From The Payroll.',
  'ef.isOutsideWorker': 'Not Employed By This Company',
  /* Said before the save rather than after it. The database refuses the row
     outright — see the temporary_worker_identified check in 0145 — and a
     constraint name is no use to whoever is holding the passport. */
  'ef.outsideWorkerNeedsId':
    'An Outside Worker Needs A Passport Number Or An Emirates ID First. '
    + 'Their Documents Carry No Company Name, So It Is The Only Thing On Them '
    + 'That Says Who They Are. Both Are Under Documents, Below.',
  'ef.notOutsideWorker': 'On The Company Payroll',
  'ef.payBasis': 'Paid By',
  'ef.payBasisHint':
    'A Monthly Wage Is Divided Into Days By The Company Rules, And Rest Days, '
    + 'Public Holidays And Leave Are All Paid. A Daily Wage Pays Only The Days '
    + 'Actually Stood On A Site — Nothing For A Friday, Nothing For Eid, And '
    + 'Nothing At All For A Day Marked Absent.',
  'ef.settlementExtras': 'On Settlement, They Are Owed',
  'ef.settlementExtrasHint':
    'Off Unless Somebody Says Otherwise. An Outside Worker Was Engaged On A '
    + 'Rate And Nothing Else, So A Ticket And A Leave Salary Are Decisions A '
    + 'Person Makes Rather Than Figures The System Assumes. An Employee '
    + 'Follows Company Policy And Is Unaffected By These.',
  'ef.settlementTicket': 'An Air Ticket',
  'ef.settlementLeaveSalary': 'Accrued Leave Salary',
  /* The leave log — one row per trip a man has taken. See LeaveLogPanel. */
  /* The settlement, as a thing somebody assembles rather than reads. */
  /* Money already handed over against a settlement. See PrepaymentSection. */
  'spay.title': 'Already Paid Against This',
  'spay.record': 'Record A Payment',
  'spay.recording': 'Recording…',
  'spay.nonePaid': 'Nothing Has Been Paid Against This Settlement Yet.',
  'spay.saveDraftFirst':
    'Save The Settlement As A Draft First. A Payment Has Nothing To Attach To '
    + 'Until It Exists.',
  'spay.how': 'How It Was Paid',
  'spay.when': 'Date It Was Paid',
  'spay.whenHint': 'The Day The Money Moved, Not The Day Of The Settlement.',
  'spay.note': 'Note',
  'spay.noteHint': 'A Reference Somebody Can Match Against A Bank Line.',
  'spay.noteExample': 'Card Top-up Before Travel',

  'stl.ticketDue': 'A Ticket Is Due: {amount} Every {months} Months, And The Last Was {since}.',
  'stl.ticketNotDue': 'No Ticket Due Yet — {months} Of {every} Months Since {since}.',
  'stl.monthlySalary': 'Monthly Salary',
  'stl.basicSalary': 'Basic Salary',
  'stl.bandDaysRate': '{days} Days Basic Rate',
  'stl.leaveDayRate': 'Leave Rate A Day',
  'stl.joined': 'Joined',
  'stl.lastRejoining': 'Last Rejoining',
  'stl.totalService': 'Total Service',
  'stl.kind': 'Settlement Type',
  'stl.kindHint': 'Gratuity Is Only Ever Part Of A Final Settlement.',
  'stl.kindAnnual': 'Annual Leave Settlement',
  'stl.kindEmergency': 'Emergency Leave Settlement',
  'stl.kindMedical': 'Medical Leave Settlement',
  'stl.kindFinal': 'Final Settlement',
  'stl.pendingSalaries': 'Salary Still Unpaid',
  'stl.days': 'Days',
  'stl.otHours': 'OT Hrs',
  'stl.leaveSalary': 'Leave Salary',
  'stl.leaveNotOwed': 'Not Owed On This Engagement',
  'stl.leaveNotOwedWhy':
    'This Person Is Not On The Company Payroll, So No Leave Salary Is '
    + 'Worked Out For Them. Leave Accrues Under Company Policy And The '
    + 'Law, And Both Apply To Employees.',
  'stl.leaveNotOwedFix':
    'If Leave Salary Was Agreed With Them, Open Their Employee Record And '
    + 'Switch On “Accrued Leave Salary” Under “On Settlement, They Are '
    + 'Owed”, Then Open This Settlement Again.',
  'stl.gratuityNotOwed': 'Not Owed On This Engagement',
  'stl.gratuityNotOwedWhy':
    'Gratuity Is End-Of-Service Pay For Somebody On The Company Payroll, '
    + 'And This Person Is Marked As Not Employed By This Company. None Is '
    + 'Worked Out, And No Tick On This Screen Can Grant It — It Follows '
    + 'From Being Employed, Not From A Checkbox.',
  'stl.gratuityNotOwedFix':
    'If They Should Be On The Payroll, Open Their Employee Record And Turn '
    + 'Off “Not Employed By This Company” — Payroll Or A Super Admin Can. To '
    + 'Hand Them Something On The Way Out Without Changing That, Use The '
    + 'Bonus Below, Which Records What It Is And Why.',
  'stl.agreedGratuityIgnored':
    'An Agreed Gratuity Amount Is Set On This Person’s Record. It Is Not '
    + 'Being Paid, Because They Are Not On The Company Payroll.',
  'stl.previousLeave': 'Previous Leave, Never Paid',
  'stl.previousLeaveNote': '{days} Days From An Earlier Leave The Company Did Not Pay For',
  'stl.currentLeave': 'Leave Earned Since He Came Back',
  'stl.currentLeaveNote': '{days} Days Earned Over {worked} Days Worked',
  'stl.gratuity': 'End Of Service',
  'stl.includeGratuity': 'Pay Gratuity',
  'stl.gratuityNote': '{days} Days Earned Over {service} Days Of Service',
  'stl.ticketLedger': 'Tickets Bought For Him',
  'stl.printTickets': 'Print This History On The Document',
  'stl.byHand': 'Added Or Taken Off By Hand',
  'stl.bonus': 'Bonus ({currency})',
  'stl.bonusHint': 'Anything Management Is Adding At Its Discretion.',
  'stl.bonusFor': 'What The Bonus Is For',
  'stl.pettyCash': 'Petty Cash Returned ({currency})',
  'stl.pettyCashHint': 'A Float He Is Holding, Or Is Owed Back. Not A Bonus.',
  'stl.pettyCashFor': 'What The Float Was For',
  'stl.fine': 'Fine ({currency})',
  'stl.fineHint': 'A Penalty Or An Administrative Charge.',
  'stl.fineFor': 'What The Fine Is For',
  'stl.loan': 'Outstanding Advances',
  'stl.deductLoan': 'Take The Balance Off This Settlement',
  'stl.loanNote': 'Untick To Carry It Instead Of Recovering It Now.',

  'llog.title': 'Leave History',
  'llog.subtitle': 'Every Leave Taken, One Row Each. A Settlement Is Built On These.',
  'llog.add': 'Add A Leave',
  'llog.addTitle': 'Record A Leave',
  'llog.editTitle': 'Edit This Leave',
  'llog.none': 'No Leave Recorded',
  'llog.noneBody':
    'Leave Returned From In This System Is Added Here On Its Own. Anything From '
    + 'Before That Is Typed In Once, From The File.',
  'llog.type': 'Type',
  'llog.kind.annual': 'Annual',
  'llog.kind.emergency': 'Emergency',
  'llog.kind.medical': 'Medical',
  'llog.kind.unpaid': 'Unpaid',
  'llog.departed': 'Departed',
  'llog.returned': 'Returned',
  'llog.returnedHint': 'Leave Empty If They Have Not Come Back Yet.',
  'llog.approved': 'Approved',
  'llog.approvedHint': 'Days Granted On The Approval Letter.',
  'llog.availed': 'Taken',
  'llog.availedHint': 'Days Actually Taken, Which Is Not Always What Was Approved.',
  'llog.availedFromDates': 'The Two Dates Give {days}. Type A Figure To Override It.',
  'llog.extra': 'Extra',
  'llog.ticket': 'Ticket',
  'llog.noTicket': 'None',
  'llog.stillAway': 'Away',
  'llog.fromRequest': 'Recorded Automatically',
  'llog.leaveSalary': 'Leave Salary Paid ({currency})',
  'llog.ticketAmount': 'Ticket Cost ({currency})',
  'llog.ticketHint': 'What The Company Paid. Leave At Zero If It Bought Nothing.',
  'llog.ticketKind': 'Ticket Note',
  'llog.ticketKindHint': 'One Way, Return, Who It Was Booked With.',
  'llog.ticketKindSample': 'Return — Dhaka',
  'llog.note': 'Note',
  'llog.returnBeforeDeparture': 'The Return Date Is Before The Departure Date.',
  'llog.needDeparture': 'Say When They Left.',
  'llog.deleteTitle': 'Delete This Leave?',
  'llog.deleteBody':
    'It Comes Off The History A Settlement Is Worked Out From. If It Was '
    + 'Recorded Automatically On A Return, It Will Not Come Back On Its Own.',

  'ef.leavePayBasis': 'Leave Salary Is Worked Out On',
  'ef.leavePayBasisHint':
    'Which Figure One Day Of Leave Is Priced From. Not Always The Same As '
    + 'The Monthly Wage — A Man On 2,200 With A Basic Of 1,400 May Be Paid '
    + 'Leave On Either, Or On A Third Figure Agreed With Him.',
  'ef.leaveOnBasic': 'Basic Salary',
  'ef.leaveOnGross': 'Basic Plus Allowances',
  'ef.leaveOnCustom': 'A Separate Monthly Figure',
  'ef.leavePayRate': 'Leave Salary Rate',
  'ef.leavePayRateHint':
    'A Monthly Figure, Divided By The Same Day Divisor As Everything Else To '
    + 'Price One Leave Day.',
  'ef.monthlyWage': 'Monthly Wage',
  'ef.dailyWage': 'Daily Wage',
  'ef.mayMarkSelf': 'May Mark Their Own Attendance',
  'ef.canRecordOwn': 'They Can Record Their Own Arrival And Departure',
  'ef.onlyDriverMarks': 'Only A Driver Or Supervisor Can Mark Them',
  'ef.mayAutoMark': 'May Be Marked Automatically By Their Phone',
  'ef.phoneMayRecord': 'Their Phone May Record Arriving At And Leaving A Site',
  'ef.phoneWillNot': 'Their Phone Will Not Track Them',
  'ef.lastWorkingDay': 'Last Working Day',
  'ef.reason': 'Reason',
  'ef.bank': 'Bank Details',
  'ef.iban': 'IBAN',
  'ef.bankName': 'Bank',
  'ef.saving': 'Saving…',
  'ef.saveChanges': 'Save Changes',
  'stl.title': 'Final Settlement',
  'stl.creationDate': 'Creation Date',
  'stl.creationDateHint': 'The Date The Company Considers This Drawn Up.',
  'stl.lastWorkingDay': 'Leaving Date',
  'stl.lastWorkingDayHint':
    'The First Day NOT Worked. Somebody Whose Last Day On Site Was 31 December Leaves On 1 January — Service, Gratuity And Leave Are Counted Up To This Date And Not Including It.',
  'stl.afterTheyLeft':
    'This Record Says The Employment Ended On {date}. Leave And Gratuity Stop On That Day — A Later Date Here Pays For Days Nobody Worked, However Long The Settlement Took To Prepare.',
  'stl.ticketHint': 'Nothing Is Assumed — Enter It If It Is Owed.',
  'stl.owed': 'Owed',
  'stl.nothingOwed': 'Nothing Is Owed.',
  'stl.takenOff': 'Taken Off',
  'stl.nothingDeducted': 'Nothing Is Deducted.',
  'stl.theyOwe': 'They Owe The Company',
  'stl.payable': 'Payable',
  'stl.whyChanged': 'Why Is This Being Changed?',
  'stl.whyChangedHint':
    'Kept With The Settlement, Against Your Name And The Time. Required, Because '
    + 'An Approved Settlement Is A Figure Somebody Has Already Been Told.',
  'stl.whyExample': 'Gratuity Recalculated After Their Last Day Was Corrected',
  'stl.somebody': 'Somebody',
  'stl.close': 'Close',
  'stl.saving': 'Saving…',
  'stl.saveChanges': 'Save Changes',
  'stl.saveDraft': 'Save As Draft',
  'stl.endsEmployment': 'This Ends The Employment',
  'stl.endsEmploymentBody':
    'Approving A Final Settlement Marks {name} As Left On {date} — The Date This '
    + 'Settlement Was Calculated From. From Then On They Are Off The Payroll, '
    + 'Their Leave Stops Accruing And Their Gratuity Stops Earning. Putting Them '
    + 'Back Is A Separate Decision On Their Own Record.',
  'stl.approveAndEnd': 'Approve And End',
  'stl.attachPayslips': 'Attach The Payslip For Each Month Above To The PDF',
  'stl.approving': 'Approving…',
  'stl.discard': 'Discard Draft',
  'stl.discarding': 'Discarding…',
  'stl.discardTitle': 'Discard This Draft Settlement?',
  'stl.discardBody': 'The Draft For {name}, Coming To {amount}, Will Be Closed And Will Stop Counting As A Settlement Pending. Its Figures Are Kept In The Record, So What It Said Can Still Be Read. Nothing Is Paid And Nothing Changes For {name}.',
  'stl.discardConfirm': 'Discard It',
  'stl.building': 'Building…',


  'cd.sayWhy':
    'Say Why This Day Is Being Changed. The Reason Is Stored With The Correction.',
  'cd.chooseLeaveKind': 'Choose Which Kind Of Leave This Is.',
  'cd.undo': 'Undo This Correction?',
  'cd.undoRecalc': 'Undo And Recalculate',
  'cd.status': 'Status',
  'cd.leaveKind': 'Kind Of Leave',
  'cd.noLeaveTypes': 'No Leave Types Are Defined In The Company Rules Yet.',
  'cd.paidDecidedByRules':
    'Paid Or Unpaid Is Decided By The Leave Rules, Not Here.',
  'cd.choose': 'Choose…',
  'cd.site': 'Site',
  'cd.siteHint': 'Moving The Day To Another Job Number Moves Its Cost With It.',
  'cd.noSite': 'No Site',
  'cd.normalHours': 'Normal Hours',
  'cd.overtime': 'Overtime',
  'cd.overtimeHint': 'Paid At The Overtime Rate.',
  'cd.leaveNoHours':
    'Leave Is Paid From The Leave Rules And The Salary, So No Hours Are Recorded '
    + 'Against It.',
  'cd.noHoursThisKind': 'No Hours Are Recorded On This Kind Of Day.',
  'cd.why': 'Why',
  'cd.whyHint':
    'Stored With The Correction And Shown On The Record. Write It For Whoever '
    + 'Reads This In A Year.',
  'cd.whyExample':
    'Phone Left In The Vehicle — Confirmed Present By The Site Engineer.',
  'cd.goBack': 'Go Back To The Calculated Result',
  'cd.saving': 'Saving…',
  'cd.saveCorrection': 'Save Correction',
  'cd.sayWhyOt': 'Say Why The Overtime Is Being Changed.',
  'cd.recorded': 'Recorded',
  'cd.payThisMuch': 'Pay This Much Overtime',
  'cd.takingOff': 'Taking Off ',
  'cd.otWhyHint': 'Appears On The Record And In The Activity Log.',
  'cd.otWhyExample':
    'Two Hours Approved By The Project Manager; The Rest Was Waiting Time.',
  'cd.goBackRecorded': 'Go Back To Paying What Was Recorded',
  'ph.jobnumberorname': 'Job Number Or Name…',
  'map.searchPlaceholder': 'Search A Place, Or Paste Coordinates…',
  'ph.jobnumbernameorclient': 'Job Number, Name Or Client…',
  'ph.masondriverhelper': 'Mason, Driver, Helper…',
  'ph.nameoremployeenumber': 'Name Or Employee Number…',
  'ph.searchbydriverorplate': 'Search By Driver Or Plate…',
  'ph.searchbyemployeeorhandset': 'Search By Employee Or Handset…',
  'ph.searchbyemployeetitleortype': 'Search By Employee, Title Or Type…',
  'ph.searchbyjobnumberorname': 'Search By Job Number Or Name…',
  'ph.searchbyjobnumbernameorclien': 'Search By Job Number, Name Or Client…',
  'ph.searchbyjobnumberprojectorem': 'Search By Job Number, Project Or Employee…',
  'ph.searchbynameorid': 'Search By Name Or ID…',
  'ph.searchbynameidorjobnumber': 'Search By Name, ID Or Job Number…',
  'ph.searchbynameidorleavetype': 'Search By Name, ID Or Leave Type…',
  'ph.searchbynameidorreason': 'Search By Name, ID Or Reason…',
  'ph.searchbynameiddesignationorp': 'Search By Name, ID, Designation Or Phone…',
  'ph.searchbynamecodeordevice': 'Search By Name, Code Or Device…',
  'ph.searchbynameemailorrole': 'Search By Name, Email Or Role…',
  'ph.searchbypersonactionorfield': 'Search By Person, Action Or Field…',
  'ph.searchbyplatemakeordriver': 'Search By Plate, Make Or Driver…',
  'ph.searchbyrecipientsubjectorer': 'Search By Recipient, Subject Or Error…',
  'fleet.to': 'To',
  'fleet.noTrips': 'No Trips In This Range',
  'fl.closeStale.one': 'Close 1 Unfinished Trip',
  'fl.closeStale.other': 'Close {count} Unfinished Trips',
  'fl.closing': 'Closing…',
  'fleet.noTripsBody':
    'Trips Appear Once Drivers Are Running The Field App And Their Phones Detect '
    + 'Vehicle Motion.',
  'fleet.noVehicles': 'No Vehicles Yet',
  'fleet.noVehiclesBody': 'Add The Fleet So Trips Can Be Attributed To A Plate.',
  'fleet.model': 'Model',
  'fleet.assignedDriver': 'Assigned Driver',
  'fleet.assignedDriverHint':
    'A Driver Cannot Be Assigned Without A Licence On File. The Database Refuses '
    + 'It Either Way.',
  'fleet.unassigned': 'Unassigned',
  'fleet.regExpires': 'Registration Expires',
  'fleet.insExpires': 'Insurance Expires',
  'fleet.changes': 'Changes To This Vehicle',
  'fleet.whatIsIt': 'What Is It',
  'fleet.docTitle': 'Title',
  'fleet.docTitleHint': 'Left Blank, The Type Is Used.',
  'fleet.docTitleExample': 'Mulkiya 2026',
  'fleet.issuedOn': 'Issued On',
  'fleet.expiresOn': 'Expires On',
  'fleet.note': 'Note',
  'fleet.noteExample': 'Fine Paid 12 Aug, Receipt Attached',
  'fleet.driving': 'Driving',
  'fleet.idle': 'Idle',
  'fleet.reg': 'Reg',
  'fleet.ins': 'Ins',
  'fleet.pickup': 'Pickup',
  'fleet.bus': 'Bus',
  'fleet.car': 'Car',
  'fleet.trailer': 'Trailer',
  'fleet.crane': 'Crane',
  'fleet.other': 'Other',
  'fleet.tripsIntro':
    'Trips Start Themselves When A Driver\'s Phone Detects Vehicle Motion. There '
    + 'Is No Button To Forget.',
  'fleet.rematch': 'Re-match To Sites',
  'fleet.noStops':
    'No Stops Were Detected — The Vehicle Did Not Settle Anywhere Long Enough.',
  'fleet.travelCharged':
    'Travel Time Is Charged To The Destination — Somebody Driving To Job 1570 Is '
    + 'Working On Job 1570.',
  'fleet.addFirst': 'Add The First Vehicle',
  'fleet.noLicence':
    'No Driving Licence On File For This Person. Upload It Under Documents As A '
    + 'Licence, And They Can Be Assigned.',
  'fleet.nothingAttachedYet':
    'Nothing Attached To This Vehicle Yet — No Registration, No Insurance, '
    + 'Nothing.',
  'fleet.nothingAttached': 'Nothing Attached To This Vehicle Yet.',
  'fleet.fiveDocs':
    'This Vehicle Has Its Five Documents. Delete One Before Adding Another. The '
    + 'Limit Is Enforced In The Database, So It Holds Whatever Any Screen Allows.',
  'fleet.papersOnFile': 'Papers On File',
  'day.sun': 'Sun',
  'day.mon': 'Mon',
  'day.tue': 'Tue',
  'day.wed': 'Wed',
  'day.thu': 'Thu',
  'day.fri': 'Fri',
  'day.sat': 'Sat',
  'rules.adminOnly': 'Admin Only',
  'rules.inForceNow': 'In Force Now',
  'rules.addBand': 'Add Band',
  'rules.addLeaveType': 'Add Leave Type',
  'rules.addTier': 'Add Tier',
  'rules.remove': 'Remove',
  'rules.addComponent': 'Add Component',
  'rules.fixedBreaks': 'Breaks At A Fixed Time',
  'rules.addBreak': 'Add A Break',
  'rules.onTheseDays': 'On These Days',
  'rules.lunch': 'Lunch',
  'rules.from': 'From',
  'rules.earning': 'Earning',
  'rules.stopped':
    'Attendance And Payroll Stop Calculating Until A Version Is Active.',
  'rules.toChange':
    'To Simply Change The Rules You Do Not Need This — Edit Them And Press '
    + 'Activate, Which Closes The Current Version The Day Before The New One '
    + 'Starts.',
  'rules.askAdmin':
    'These Settings Decide How Everybody Is Paid. Ask An Administrator If You '
    + 'Need Something Changed.',
  'rules.everyRate':
    'Every Rate The System Uses. Nothing Is Assumed On Your Behalf — Payroll '
    + 'Will Not Run Until Each Of These Is Set.',
  'rules.optionalSection':
    'Payroll Can Run Without This Section, But The Feature It Controls Will Not '
    + 'Work Until It Is Set.',
  'rules.nothingCalculating':
    'Nothing. Attendance And Payroll Are Not Calculating.',
  'rules.stillToSet': 'Still To Set Before These Rules Can Go Live',
  'rules.noLeaveTypes':
    'No Leave Types Yet. Add Every Kind Your Company Grants — Annual, Sick, '
    + 'Unpaid, Whatever Else.',
  'rules.bandsBlurb':
    'For Leave Paid At Different Rates As It Goes On. Leave Empty If It Is Paid '
    + 'In Full Throughout.',
  'rules.tiersBlurb':
    'Each Tier Accrues Only Across The Years It Covers. Leave The Last Tier\'s '
    + 'End Year Blank So It Runs Onwards.',
  'rules.noTiers':
    'No Tiers Yet. Add At Least One For End-of-service To Calculate.',
  'rules.componentsBlurb':
    'Name The Allowances Your Company Actually Pays — Housing, Transport, Site, '
    + 'And Any Deduction That Comes Off Every Month.',
  'rules.breaksBlurb':
    'Optional, And Separate From The Break Above. Somebody Who Marks Themselves '
    + 'Out For One Of These And Comes Back Late Is Handled By The Rule Above.',
  'rules.noBreaks':
    'None Set. Nothing Can Be Late From A Break That Does Not Exist, So This Is '
    + 'Only Worth Filling In Where The Crew Breaks At A Fixed Hour.',
  'rules.noDaysChosen':
    'No Days Chosen, So This Break Applies To Nothing. That Is Allowed While You '
    + 'Are Still Setting It Up.',
  'ntf.ignoreQuiet': 'Ignore Quiet Hours When',
  'ntf.ignoreQuietHint': 'Nothing Wakes Anybody Unless You Say So Here.',
  'ntf.notAgainWithin': 'Not Again Within',
  'ntf.notAgainHint': '0 Means Every Time. Stops The Same Thing Arriving Hourly.',
  'ntf.min': 'Min',
  'ntf.oneMessage': 'One Message Instead Of Many',
  'ntf.oneMessageHint':
    'Thirty Separate Alerts Is A Phone Somebody Switches Off — And Then They Miss '
    + 'The One That Mattered.',
  'ntf.testIt': 'Test It',
  'ntf.testItSub':
    'Sends A Real Notification Through The Real Rules, Addressed Only To You.',
  'ntf.whichOne': 'Which One',
  'ntf.whichOneHint':
    'The Test Notification Is Safe. Any Other Kind Sends A Real-looking One To You.',
  'ntf.recentlySent': 'Recently Sent',
  'ntf.recentlySentSub': 'Including Everything That Was Not Sent, And Why.',
  'ntf.nothingYet': 'Nothing Yet',
  'ntf.nothingYetBody': 'Deliveries Appear Here As Notifications Are Raised.',
  'ntf.bell': 'Bell',
  'ntf.phone': 'Phone',
  'ntf.emailCh': 'Email',
  'ntf.aud.attendanceView': 'Anyone Who Can See Attendance',
  'ntf.aud.attendanceEdit': 'Anyone Who Can Correct Attendance',
  'ntf.aud.employeesView': 'Anyone Who Can See The Staff File',
  'ntf.aud.employeesEdit': 'Anyone Who Can Edit The Staff File',
  'ntf.aud.leaveView': 'Anyone Who Can See Leave',
  'ntf.aud.leaveApprove': 'Anyone Who Can Decide Leave',
  'ntf.aud.payrollView': 'Anyone Who Can See Payroll',
  'ntf.aud.payrollManage': 'Anyone Who Can Run Payroll',
  'ntf.aud.projectsEdit': 'Anyone Who Can Edit Projects',
  'ntf.aud.settings': 'Anyone Who Can Change Settings',
  'ntf.aud.super': 'Super Admins Only',
  'ntf.urgent': 'Urgent',
  'ntf.quietNever': 'Never — Always Stay Silent',
  'ntf.quietWorth': 'Worth Acting On, Or Urgent',
  'ntf.quietUrgent': 'Only If Urgent',
  'ntf.intro':
    'Everything This System Is Able To Tell Anybody, And Your Decision About Each '
    + 'Of Them.',
  'ntf.noHandset': 'Phone Alerts Are Switched On, But No Phone Is Registered',
  'ntf.noHandsetBody':
    'Nothing Will Arrive On Anybody\'s Handset. A Phone Registers Itself The First '
    + 'Time Somebody Opens The App On It And Allows Notifications.',
  'ntf.neverChanged':
    'Never Changed — These Are The Defaults This Notification Shipped With.',
  'ntf.allRetired':
    'Every Handset On This Account Has Been Retired By Firebase — Usually The App '
    + 'Being Reinstalled. They Register Again On The Next Sign-in.',
  'ntf.whoWouldGet': 'Who Would Get The Real One',
  'ntf.nothingSent':
    'Nothing Was Sent. A Name Shown With The Bell Icon Would Be Told In The App; '
    + 'One With A Phone Icon Would Also Get A Push.',
  'ntf.ifThisMentions': 'If This Mentions',
  'ntf.testThisOne': 'Test This One',
  'ntf.migrationsMissing': ', Migrations 0059 To 0061 Have Not Been Applied Yet.',
  'ed.backToEmployees': 'Back To Employees',
  'ed.notFound': 'Employee Not Found',
  'ed.notFoundBody':
    'This Record May Have Been Removed, Or It Belongs To Another Company.',
  'ed.settlement': 'Settlement',
  'ed.ledger': 'Ledger',
  'ed.bank': 'Bank',
  'ed.bankSub': 'Used By The WPS Export',
  'ed.noIban':
    'Without An IBAN This Employee Cannot Be Included In A WPS File.',
  'ed.salary': 'Salary',
  'ed.salarySub':
    'Effective-dated, So Re-running A Past Month Uses The Rate That Applied Then',
  'ed.noSalary':
    'No Salary Recorded. Payroll Will Skip This Employee Until One Is Set.',
  'ed.basicSalary': 'Basic Salary',
  'ed.dailyRate': 'Daily Rate',
  'ed.dailyRateHint':
    'What One Day Of Work Is Worth. Only Days Actually Stood On A Site Are '
    + 'Paid — A Day Marked Absent Pays Nothing At All.',
  'ed.effectiveFrom': 'Effective From',
  'ed.effectiveFromHint': 'The Previous Record Is Closed The Day Before.',
  'ed.agreedOtRate': 'Agreed Overtime Rate',
  'ed.reason': 'Reason',
  'ed.reasonExample': 'Increment / Promotion / Correction',
  'ed.removeAllowance': 'Remove Allowance',
  'ed.current': 'Current',
  'ed.allowances': 'Allowances',
  'ed.noAllowances':
    'None. Which Allowances Count Toward Overtime Is Set In Settings → Rules.',
  'ed.grossMonthly': 'Gross Monthly',
  'ed.nothingRecorded': 'Nothing Recorded Yet.',
  'kiosk.setUpOnce': 'Set This Up Once, Then Pass The Phone Down The Line.',
  'kiosk.scanningStillWorks':
    'Scanning Still Works And Nothing Is Blocked, But Every Scan Is Recorded '
    + 'Without Coordinates, So Nothing Can Confirm The Crew Was On Site.',
  'kiosk.noBoundaryDrawn':
    'Nothing Can Be Checked Against The Coordinates Until Somebody Draws A '
    + 'Boundary On A Project.',
  'kiosk.whichSite': 'Which Site Is This?',
  'kiosk.filledFromLocation':
    'Filled In From Your Location. Change It If It Is Wrong.',
  'kiosk.locationDisagrees': 'Your Location Does Not Agree With That',
  'kiosk.coordsRecorded':
    'The Coordinates Are What Gets Recorded. If The Boundary Is Drawn Wrongly, '
    + 'Fix The Project Rather Than Working Around It Here.',
  'kiosk.nothingChosen':
    'Nothing Chosen. If The Coordinates Cannot Place You Either, This Crew\'s '
    + 'Hours Will Reach No Job Number.',
  'kiosk.downloadedOnce':
    'Downloaded Once And Kept, So The Kiosk Works With No Signal.',
  'kiosk.nothingLost':
    'Nothing Is Lost: They Stay On This Phone Until They Are Accepted. But They '
    + 'Will Not Appear On Anybody\'s Attendance Until They Do.',
  'kiosk.scanAnyway': 'Scan Anyway, Without A Location',
  'kiosk.whenDone': 'When The Crew Is Done',
  'kiosk.buildTheDay':
    'Build The Day\'s Attendance From The Scans. The First Arrival Opens The Day '
    + 'And The Last Departure Closes It.',
  'kiosk.markedNoJob':
    'They Are Marked Present, But This Day Is Charged To No Job Number. Name The '
    + 'Site On The Setup Screen.',
  'kiosk.markByRollcall': 'Mark By Roll-call',
  'kiosk.whereYouAre': 'Where You Are',
  'kiosk.noLocationYet': 'No Location Yet',
  'kiosk.noSiteBoundary': 'No Site Has A Boundary Drawn',
  'kiosk.whichWay': 'Which Way',
  'kiosk.enrol': 'Enrol',
  'kiosk.markByHand': 'Mark By Hand',
  'kiosk.databaseHasNo': 'This Database Has No',
  'kiosk.columnMissing':
    ' Column, So Scans Are Uploading Without It. Attendance Is Unaffected; The '
    + 'Site You Name Is Not Being Kept Alongside The Measured One Until Migration '
    + '0065 Is Applied.',
  'kiosk.site': 'Site',
  'kiosk.done': 'Done',
  'kiosk.todaysScans': "Today's Scans",
  'kiosk.enrolAFace': 'Enrol A Face',
  'adj.none': 'No Adjustments This Period',
  'adj.noneBody':
    'Every Payslip Is Calculated Purely From Attendance And Salary Until You Add '
    + 'Something Here.',
  'adj.add': 'Add Adjustment',
  'adj.employee': 'Employee',
  'adj.choose': 'Choose…',
  'adj.removeTitle': 'Remove {label}?',
  'adj.removeBody': '{amount} Comes Off This Payslip. Recalculate The Run Afterwards, Or The Figure Already Shown Will Not Match.',
  'adj.remove': 'Remove',
  'adj.removeAria': 'Remove {label}',
  'adj.hintAddition': 'Added To Gross Pay',
  'adj.hintDeduction': 'Withheld From Net Pay',
  'adj.hintPaidAlready': 'Handed Over Before Pay Day — Reduces What Is Still Owed',
  'adj.siteHintChosen': 'This Amount Will Be Charged To That Job Number.',
  'adj.siteHintNone': 'Leave Blank Only If It Truly Belongs To No Site — A Long-service Bonus, Say.',
  'adj.egAddition': 'Watchman Duty Allowance',
  'adj.egDeduction': 'Replacement Of Lost Tools',
  'adj.egPaidAlready': 'Cash Paid On 15Th',
  'adj.type': 'Type',
  'adj.amount': 'Amount',
  'adj.whichSite': 'Which Site',
  'adj.noSiteOverhead': 'No Site — Company Overhead',
  'adj.description': 'Description',
  'adj.descriptionHint': 'Shown On The Payslip.',
  'adj.code': 'Code',
  'adj.codeHint': 'Short Reference For Reports.',
  'adj.internalNote': 'Internal Note',
  'adj.internalNoteHint': 'Not Shown To The Employee.',

  'adj.proof': 'Proof Of Payment',
  'adj.proofHint':
    'A Photograph Of The Signed Receipt, Or The Bank Transfer. Optional — '
    + 'Record The Payment Either Way, Because A Payment Nobody Recorded Is One '
    + 'The Company Pays Twice.',
  'adj.proofChoose': 'Attach A Photo Or PDF',
  'adj.proofChange': 'Choose A Different File',
  'adj.proofRemove': 'Remove',
  'adj.proofTooLarge': 'That File Is Over 10 MB. Photograph It Again At A Lower Size.',
  'adj.proofOnFile': 'Receipt On File',
  'adj.noProof': 'No Receipt Attached',
  'adj.addition': 'Addition',
  'adj.deduction': 'Deduction',
  'adj.alreadyPaid': 'Pre-payment',
  'adj.alreadyPaidCash': 'Pre-payment In Cash Or Bank',
  'adj.intro':
    'Anything Attendance Cannot Know About. These Feed Straight Into The Next '
    + 'Calculation.',
  'adj.noJobNumber': 'No Site — This Reaches No Job Number',
  'adj.recalcNeeded':
    'Adjustments Take Effect The Next Time This Period Is Calculated. '
    + 'Recalculate To See Them On The Payslips.',

  'psd.title': 'Payslip',
  'psd.gone': 'That Payslip No Longer Exists. The Run Was Most Likely Recalculated — Close This And Open It Again.',
  'psd.couldNotOpen': 'This Payslip Could Not Be Opened. It May Have Been Recalculated — Close This And Try Again.',
  'psd.titleFor': 'Payslip · {name} · {period}',
  'psd.askOffice': 'Ask The Office If You Need A Copy Of This.',
  'psd.calculation': 'Calculation',
  'psd.otRateAgreed': 'Agreed With This Employee · {salary} From Salary',
  'psd.daysCount': '{count} Days',
  'psd.item': 'Item',
  'psd.qty': 'Qty',
  'psd.rate': 'Rate',
  'psd.amount': 'Amount',
  'psd.gross': 'Gross',
  'psd.alreadyPaid': 'Pre-payment During The Month',
  'psd.netPayable': 'Net Payable',
  'psd.advanceAccount': 'Advance Account',
  'psd.whereCharged': 'Where This Labour Was Charged',
  'psd.job': 'Job',
  'psd.days': 'Days',
  'psd.normal': 'Normal',
  'psd.otPay': 'OT Pay',
  'psd.total': 'Total',
  'psd.dayByDay': 'Day By Day',
  'psd.date': 'Date',
  'psd.status': 'Status',
  'psd.overtime': 'Overtime',
  /* The two views of one payslip: the document as it prints, and the
     arithmetic behind it. See PayslipDetail. */
  'psd.theSheet': 'Payslip',
  'psd.theWorkings': 'Workings',
  'psd.close': 'Close',
  'adp.cannotOpen': 'This Record Could Not Be Opened',
  'adp.back': 'Back',
  'adp.totalWorked': 'Total Worked',
  'adp.arriving': 'Arriving',
  'adp.nothingRecorded':
    'Nothing Recorded For This Day — No Scan And No Mark By Hand.',
  'adp.leaving': 'Leaving',
  'adp.notMarkedOut':
    'They Have Not Been Marked Out Yet, So The Day Is Still Open And No Hours '
    + 'Have Been Calculated.',
  'adp.address': 'Address',
  'adp.coordinates': 'Coordinates',
  'adp.time': 'Time',
  'adp.gpsAccuracy': 'GPS Accuracy',
  'adp.recordedBy': 'Recorded By',
  'adp.faceMatch': 'Face Match',
  'adp.note': 'Note',
  'adp.noPositionBody':
    'The Scan Happened But The Phone Had No Position, So Nothing Can Place It.',
  'adp.onSite': 'On Site',
  'adp.outsideBoundary': 'Outside The Boundary',
  'adp.noPosition': 'No Position',
  'adp.livenessUnproven': 'Liveness Unproven',
  'adp.live': 'Live',
  'adp.noCoordinates': 'No Coordinates Recorded',

  /* Taking a daily-wage worker on at the gate. Short, because they are read
     standing up with a queue behind you. */
  'dw.title': 'Add A Worker',
  'dw.subtitle': 'Taken On For This Site. Not On The Company Payroll Until The Office Says So.',
  'dw.identityKind': 'Recorded By',
  'dw.passport': 'Passport',
  'dw.emiratesId': 'Emirates ID',
  'dw.number': 'Number',
  'dw.numberHint':
    'This Is What Goes On The Record Instead Of A Name. It Is The Only Thing '
    + 'That Will Be The Same Next Time He Works Here.',
  'dw.site': 'Site',
  'dw.trade': 'Trade',
  'dw.tradeHint': 'Optional. Mason, Carpenter, Helper — Whatever They Were Taken On As.',
  'dw.chooseTrade': 'Choose A Trade',
  'dw.noRate':
    'No Day Rate Is Set Here. He Goes Onto The Payroll List Without One, And '
    + 'The Office Puts A Figure Against Him.',
  'dw.take': 'Add Worker',
  'dw.taking': 'Adding…',
  'dw.taken': 'Added As',
  'dw.alreadyOnFile': 'Already On File — Same Person, Same Record:',
  'dw.nowScan':
    'They Are Marked By A Face Scan At The Kiosk, Not From The Register. A Day '
    + 'Ticked On The Register Will Be Refused — So Enrol Their Face Now, While '
    + 'They Are Standing In Front Of You.',
  'dw.enrolFace': 'Enrol Their Face',
  'dw.another': 'Add Another',
  'dw.addWorker': 'Add Worker',
  'dw.name': 'Full Name',
  'dw.nameHint': 'As It Is Written On The Passport Or Emirates ID.',
  /* Said on the form, because it is the whole of what this button does
     differently from the office's own. */
  'dw.temporaryNote':
    'Added As A Temporary Worker, Not On The Company Payroll. The Office Can '
    + 'Put Them On It Later, And Everything They Work In The Meantime Stays As '
    + 'It Was Recorded.',

  /* The trip screen after the buttons came off it. */
  'dt.followsShift':
    'Recording. This Stops On Its Own When You Mark Out — There Is Nothing '
    + 'To Press.',
  /* The advance flow after 0148 split asking from agreeing. */
  'adv.alreadyOwing':
    'This Person Already Has {count} Advance(s) Still Being Recovered. That Is '
    + 'Not A Refusal — Whoever Agrees It Should Know.',
  'adv.needsTwo':
    'This Is Above The {limit} In Your Rules, So A Second Person Has To Agree '
    + 'It Before The Money Can Go Out.',
  /* Said before the advance is saved. adv.needsTwo says the same thing after
     it has been, and the two are deliberately worded apart: one is a warning
     about what will happen, the other a statement of what did. */
  'adv.willNeedTwo':
    'This Is Above The {limit} Threshold In Your Rules, So It Will Be Saved As '
    + 'Pending And Needs A Second Approver Before It Is Recovered.',
  'adv.secondSignature': 'Second Signature',
  'adv.handOver': 'Hand Over',

  'ef.recordsTrips': 'Record Journeys',
  'ef.recordsTripsHint':
    'For Somebody Given A Vehicle. Their Route Is Recorded While They Are '
    + 'Marked In, And Stops When They Mark Out. It Watches GPS For The Whole '
    + 'Shift, So Leave It Off For Anybody Who Does Not Drive.',
  'ef.routeRecorded': 'Route Recorded During Their Shift',
  'ef.routeNotRecorded': 'No Route Recorded',

  'dt.startsOnMarkIn':
    'Nothing Is Being Recorded. A Route Starts On Its Own When You Mark In '
    + 'And Ends When You Mark Out.',
  'adp.locationWithheld': 'Location Not Shown',
  'adp.locationWithheldBody':
    'The Time And The Site Are On Record. Where It Happened Is Kept To The '
    + 'People Who Handle Pay And Personnel.',

  'tk.title': 'Tickets',
  'tk.recoverableHint': 'Recoverable — It Will Appear On The Next Settlement',
  'tk.historyOnlyHint': 'History Only — It Affects Nothing',
  'tk.subtitle':
    'Every Flight The Company Has Paid For. History Only, Unless A Line Says '
    + 'Otherwise.',
  'tk.none': 'No Tickets Recorded',
  'tk.noneBody':
    'Add The Ones Already Bought, Including The Old Ones. Nothing Entered Here '
    + 'Reaches A Calculation Unless You Say So On The Line Itself.',
  'tk.add': 'Add A Ticket',
  'tk.issuedOn': 'Issued On',
  'tk.whichWay': 'Which Way',
  'tk.whichWayHint':
    'Written Down Because The Company Often Buys The Two Halves Separately And '
    + 'Months Apart, And An Amount On Its Own Cannot Say Which It Was.',
  'tk.note': 'Note',
  'tk.noteHint':
    'Whatever You Would Want To Read Six Years From Now — “From UAE”, “Return”, '
    + '“Paid By Client”.',
  'tk.noteExample': 'From UAE',
  'tk.countsInSettlement': 'Does This Count In The Settlement?',
  'tk.countsHint':
    'Off Means It Is Recorded And Nothing More. Leave It Off For Anything Already '
    + 'Paid — Adding An Old Ticket To A Settlement Pays For It A Second Time.',
  'tk.oneWayOut': 'One Way Out — The Flight Home',
  'tk.returnFlight': 'Return Flight — The Way Back',
  'tk.returnBoth': 'Return Ticket, Bought As One',
  'tk.recordNotBalance':
    'This Total Is A Record, Not A Balance. Nothing Here Is Deducted From Anybody '
    + 'Or Added To A Settlement Unless Its Line Says “In Settlement”.',
  'tk.issued': 'Issued',
  'tk.amount': 'Amount',
  'tk.runningTotal': 'Running Total',
  'tk.counts': 'Counts',
  'att.devices': 'Devices',
  'att.faceKiosk': 'Face Kiosk',
  'att.rollCall': 'Roll-call',
  'att.derivedFromGps': 'Derived From Raw GPS Events. Every Figure Here Can Be Traced Back To A Coordinate.',
  /* What a company is told about the deployment, and all it is told. The
     build numbers and the command to fix it go to the platform operator. */
  'att.figuresMayBeBehind':
    'Some Of These Figures May Not Reflect The Latest Rules Yet. It Has Been '
    + 'Reported, And They Will Be Brought Up To Date. Reference {ref}.',
  'att.reloadForUpdate': 'The App Has Been Updated Since This Page Was Opened.',
  'att.reloadNow': 'Reload To Get It',
  'att.reviewIntro': 'Each Item Is A Signal, Not A Verdict. Look At The Evidence, Then Either Clear It Or Confirm It — Confirming Does Not Delete The Attendance, It Marks The Record For An Admin To Correct.',
  'att.deviceIntro': 'A Phone Enrols Itself When A Worker First Signs In, And Waits Here For Approval. One Employee Holds One Active Device — Which Is What Stops A Single Account Being Passed Round The Accommodation On Four Handsets.',
  'att.nothingToday': 'Nothing Recorded For This Day',
  'att.nothingTodayBody': 'Attendance Appears Once Workers Reach Their Sites And Their Phones Sync, Or Once A Supervisor Takes Roll-call.',
  'att.noDevices': 'No Devices Enrolled',
  'att.noDevicesBody': 'Devices Appear Here The First Time A Worker Signs In To The Field App.',
  'common.clear': 'Clear',
  'common.confirm': 'Confirm',
  'common.review': 'Review',
  'common.approve': 'Approve',
  'common.block': 'Block',
  'common.unblock': 'Unblock',
  'ret.oneLate': '{name} Has Not Come Back',
  'ret.manyLate.one': '{count} Person Has Not Come Back From Leave',
  'ret.manyLate.other': '{count} People Have Not Come Back From Leave',
  'ret.oneSoon': '{name} Is Due Back Soon',
  'ret.manySoon.one': '{count} Person Is Due Back Soon',
  'ret.manySoon.other': '{count} People Are Due Back Soon',
  'ret.overBy.one': 'Due Back {date} — {count} Day Over',
  'ret.overBy.other': 'Due Back {date} — {count} Days Over',
  'ret.dueIn.one': 'Due Back {date} — In {count} Day',
  'ret.dueIn.other': 'Due Back {date} — In {count} Days',
  'ret.dueToday': 'Due Back Today, {date}',
  'ret.comesOffLeave': 'These Days Will Come Off The Leave They Earn Next.',
  'pay.to': 'To',
  'pay.accrues': 'Salary Accrues From Attendance. Nothing Here Is Retyped.',
  'pay.newPeriod': 'New Period',
  'pay.adjustments': 'Adjustments',
  'pay.adjustmentsFor': 'Adjustments · {code}',
  'pay.openFirst': 'Open The First Period',
  'pay.lockPeriod': 'Lock Period',

  /* Taking a locked run back. Super Admin only, and the reason is kept. */
  'pay.reopen': 'Reopen Run',
  'pay.reopenTitle': 'Reopen {code}',
  'pay.reopenBlurb': 'This Takes The Run Back To Review So It Can Be Recalculated. Nearly Every Correction Belongs In The Next Open Period Instead — Use This Only When The Run Itself Is Wrong.',
  'pay.reopenWarn': 'Payslips Already Issued Will No Longer Match Once This Is Recalculated. Advance Repayments This Run Collected Are Taken Back, And The Balances Go Up Again.',
  'pay.reopenWhy': 'Why',
  'pay.reopenWhyHint': 'Kept On The Period. Somebody Reading This In A Year Needs To Know What Was Wrong.',
  'pay.reopenWhyExample': 'Locked Before The Overtime For The Marina Site Was Entered.',
  'pay.reopenConfirm': 'Reopen It',
  'pay.reopening': 'Reopening…',
  'pay.printExport': 'Print And Export',
  'pay.jobCostReport': 'Job Cost Report',
  'pay.goToRules': 'Go To Company Rules',
  'pay.lockedNote': 'This Period Is Locked. It Is History Now — Corrections Go In The Next Open Period As Dated Adjustments, Never By Rewriting What Was Paid.',
  'pay.rulesPinned': 'The Rules Active On The End Date Are Pinned To This Period. Changing Rates Later Creates A New Version And Leaves This Run Exactly As It Was Calculated.',
  /* The two halves of any sheet that mixes employees with outside labour. */
  'two.companyPayroll': 'Company Payroll',
  'two.outsideLabour': 'Outside Labour',
  'two.outsideNote': 'Paid By The Company, Not Employed By It',
  'two.peopleCount.one': '{count} Person',
  'two.peopleCount.other': '{count} People',
  'two.includeOutside.one': 'Include {count} Outside Worker',
  'two.includeOutside.other': 'Include {count} Outside Workers',
  'two.switchHint':
    'Leave This Off For A Filing. An Outside Worker On A Wages Return Is A '
    + 'Statement That This Company Employs Them. Turn It On To See What The '
    + 'Month Or The Job Actually Cost.',
  'two.bothTotal': 'Payroll And Outside Labour Together',
  /* Where the money is known and the headcount is not — a site's cost is a
     sum of cost lines, and the number of people behind it is a different
     question with a different answer per month. */
  'two.includeOutsideAmounts': 'Include Outside Labour',
  'two.outsideShort': 'Outside',
  'two.filingOnly': 'Company Payroll Only. Outside Labour Is Not On This Document.',
  'two.docsFullCost':
    'These Documents Show What The Month Cost: The Payroll, Then The Outside '
    + 'Labour On Pages Of Its Own, And The Two Totalled At The End.',
  'pay.noSlips': 'No Payslips Yet',
  'pay.noSlipsBody': 'Press Calculate Payroll To Produce Them From The Attendance Already Recorded.',
  'pay.noJobCost': 'No Job Cost Yet',
  'pay.noJobCostBody': "This Fills In Once Payroll Has Been Calculated — Each Row Is One Employee's Labour On One Job.",
  'login.tagline': 'One Platform, All Solutions',
  'login.headline': 'Every Hour, On The Right Job Number.',
  'login.blurb': 'Attendance Captured By GPS At The Site Boundary, Costed To The Project, And Carried Through To Payroll Without Anyone Retyping A Figure.',
  'login.gpsAttendance': 'GPS Attendance',
  'login.jobCosting': 'Job Costing',
  'login.wpsPayroll': 'WPS Payroll',
  'login.welcome': 'Welcome Back',
  'login.signInTo': 'Sign In To Your NoorOne Account.',
  'login.email': 'Email',
  'login.identifier': 'Username Or Email',
  'login.identifierExample': 'Your Name Or Address',
  'login.badIdentifier': 'That Is Not A Username Or An Email Address. A Username Is Letters, Numbers, Dots, Dashes Or Underscores — No Spaces.',
  'login.password': 'Password',
  'login.showPassword': 'Show Password',
  'login.hidePassword': 'Hide Password',
  'login.signIn': 'Sign In',
  'login.forgot': 'Forgotten Your Password?',
  'login.forgotTitle': 'Get Back In',
  'login.forgotBlurb': 'Type What You Sign In With. If There Is An Account And It Has An Email Address, A Link Goes To It.',
  'login.forgotSend': 'Send Me A Link',
  'login.forgotSending': 'Sending…',
  'login.forgotSentTitle': 'Check Your Email',
  'login.forgotSentBody': 'If That Account Exists And Has An Email Address, A Link Is On Its Way. It Is Good For 30 Minutes. No Email? Ask Whoever Set Your Account Up — They Can Set A New Password For You.',
  'login.backToSignIn': 'Back To Sign In',

  'reset.title': 'Set A New Password',
  'reset.blurb': 'At Least {count} Characters. Pick Something You Have Not Used Anywhere Else.',
  'reset.newPassword': 'New Password',
  'reset.again': 'Type It Again',
  'reset.save': 'Save It',
  'reset.saving': 'Saving…',
  'reset.tooShort': 'That Is Too Short. Use At Least {count} Characters.',
  'reset.mismatch': 'Those Two Do Not Match.',
  'reset.checking': 'Checking The Link…',
  'reset.expiredTitle': 'That Link Has Expired',
  'reset.expiredBody': 'A Reset Link Is Good For 30 Minutes And Works Once. Ask For A New One From The Sign-in Screen.',
  'reset.doneTitle': 'Password Changed',
  'reset.doneBody': 'Sign In With The New One. Every Other Device Has Been Signed Out.',
  'reset.backToSignIn': 'Back To Sign In',
  'reset.signsOutEverywhere': 'Saving This Signs You Out Everywhere, On Every Device.',

  'sec.title': 'Security',
  'sec.subtitle': 'A Second Lock On Your Own Account, And Who Else Has To Have One',
  'sec.yourFactor': 'Two-step Sign-in',
  'sec.yourFactorSub': 'A Six-digit Code From Your Phone, On Top Of Your Password.',
  'sec.on': 'On',
  'sec.off': 'Off',
  'sec.offBlurb': 'Your Password Is The Only Thing Standing Between Anybody Who Learns It And Everything You Can Reach. A Second Step Takes Two Minutes To Set Up And Makes A Stolen Password Worth Nothing On Its Own.',
  'sec.setUp': 'Set It Up',
  'sec.noReason': 'Two-step Sign-in Could Not Be Set Up, And The Server Gave No Reason. The Usual Cause Is That Authenticator Apps Are Switched Off For This Project — Turn TOTP On In The Supabase Dashboard Under Authentication, Multi-Factor Authentication, Then Try Again.',
  'sec.serverFault': 'The Authentication Server Failed Before It Judged Anything. The Usual Cause Is The Project Site URL, And Usually A Missing Scheme: Setting Up Two-step Sign-in Builds A Link From It, So “Example.com” Fails Exactly Like This Where “https://example.com” Works. Set It In The Supabase Dashboard Under Authentication, URL Configuration — Then This And The Password Reset Link Both Start Working.',
  'sec.likelyDisabled': 'It Looks Like Two-step Sign-in Is Switched Off For This Project. Turn TOTP On In The Supabase Dashboard Under Authentication, Multi-Factor Authentication.',
  'sec.scanBlurb': 'Open An Authenticator App — Google Authenticator, Microsoft Authenticator, 1Password — And Scan This. Then Type The Six Digits It Shows.',
  'sec.qrAlt': 'Code To Scan With An Authenticator App',
  'sec.orType': 'Or Type This In',
  'sec.copySecret': 'Copy',
  'sec.copied': 'Copied',
  'sec.enterCode': 'The Six Digits',
  'sec.enterCodeHint': 'It Changes Every Thirty Seconds. Any Current One Will Do.',
  'sec.turnOn': 'Turn It On',
  'sec.turnOff': 'Turn Off',
  'sec.authenticator': 'Authenticator App',
  'sec.askedEveryTime': 'Asked For Every Time You Sign In',
  'sec.lostPhone': 'Lost The Phone? Ask A Super Admin — They Can Take This Off Your Account So You Can Set It Up Again.',

  'sec.companyRule': 'Who Has To Use It',
  'sec.companyRuleSub': 'Your Own Choice Above Is Separate. This One Is For Everybody.',
  'sec.whoMust': 'Require Two-step Sign-in From',
  'sec.whoMustHint': 'Anybody It Applies To Keeps Their Sign-in But Can Do Nothing Until They Set It Up.',
  'sec.ruleOff': 'Nobody — Each Person Decides',
  'sec.ruleAdmins': 'Administrators — Anybody Who Can Change Pay Or Access',
  'sec.ruleEveryone': 'Everybody With A Login',
  'sec.ruleWarning': 'Choose Everybody Only If Every Person Has A Phone Of Their Own. On A Shared Site Handset There Is Nowhere For A Code To Live, And It Locks That Crew Out Of Their Own Attendance.',

  'sf.title': 'Two-step Sign-in',
  'sf.blurb': 'Type The Six Digits From Your Authenticator App.',
  'sf.codeLabel': 'Six-digit Code',
  'sf.confirm': 'Confirm',
  'sf.checking': 'Checking…',
  'sf.lostPhone': 'Lost The Phone? Ask A Super Admin To Take It Off Your Account.',
  'sf.enrolTitle': 'Set Up Two-step Sign-in',
  'sf.enrolBlurb': 'Your Company Now Asks For A Second Step. Until You Set One Up You Can Sign In But Nothing Will Load.',
  'sf.enrolNow': 'Set It Up Now',
  'sf.enrolWhy': 'It Takes About Two Minutes And Needs An Authenticator App On Your Phone.',
  'sf.signOut': 'Sign Out',
  'login.signingIn': 'Signing In…',
  'login.withFace': 'Sign In With Face',
  'login.withFingerprint': 'Sign In With Fingerprint',
  'login.withBiometrics': 'Sign In With Biometrics',
  'login.or': 'Or',
  'login.askAdmin': 'Access Is Granted By Your Administrator. If You Cannot Sign In, Ask Them To Check Your Account In Settings → Users.',
  'auto.title': 'Automatic Attendance',
  'auto.notForYou': 'Not Switched On For You',
  'auto.needsApp': 'Needs The App',
  'auto.marksYouIn': 'Your Phone Marks You In When You Reach A Site',
  'auto.off': 'Off',
  'auto.letMyPhone': 'Let My Phone Mark My Attendance',
  'geo.title': 'Site Boundary',
  'geo.label': 'Label',
  'geo.mainGate': 'Main Gate',
  'geo.radius': 'Radius',
  'geo.searchPlace': 'Search A Place, Or Paste Coordinates…',
  'geo.addBoundary': 'Add Boundary',
  'geo.useMyGps': 'Use My GPS',
  'geo.inactive': 'Inactive',
  'geo.keep': 'Keep',
  'common.saved': 'Saved',
  'auto.notEnabled': 'The Office Has Not Enabled This For Your Account. Scanning Your Face Is The Way You Mark Attendance.',
  'auto.whatItDoes': "With This On, Your Phone Notices When You Arrive At One Of The Company's Sites And Marks You In By Itself.",
  'auto.whatItRecords': 'It Records The Moment You Cross A Site Boundary, And Where That Site Is.',
  'auto.whatItDoesNot': 'It Does Not Record Where You Go Outside Those Sites, And Nobody Can See Your Movements Between Them.',
  'auto.noSignal': 'With No Signal It Keeps The Moments On Your Phone And Sends Them When The Signal Returns.',
  'auto.canSwitchOff': 'You Can Switch This Off Here At Any Time, And It Stops Immediately.',
  'auto.waitingToStart': 'Waiting To Start. If This Stays Here, Your Phone May Still Need Approving By The Office.',
  'geo.noBoundary': 'No Boundary Set. Until One Exists, Nobody’s Attendance Can Be Charged To This Project.',
  'geo.stopsImmediately': 'Attendance Stops Being Recorded At This Spot Immediately.',
  'pc.costByMonth': 'Cost By Month',
  'pc.couldNotRead': 'The Cost Of This Site Could Not Be Read',
  'pc.migrationHint': 'If This Mentions Project_cost_by_month, Migration 0068 Has Not Been Applied Yet.',
  'pc.month': 'Month',
  'pc.people': 'People',
  'pc.days': 'Days',
  'pc.wages': 'Wages',
  'pc.overtime': 'Overtime',
  'pc.toDate': 'To Date',
  'pc.total': 'Total',
  'pc.costByTrade': 'What Each Trade Cost',
  'pc.acrossTrades': '{count} Trades · {amount} In All',
  'pc.notInAGroup': 'Not In A Group',
  'pc.peopleAndDays': '{people} People · {days} Days',
  'pc.ofWhichOvertime': '{amount} Overtime',
  'pc.daysShort': '{days} Days',
  'pc.tradeIsTheirGroup': 'A Trade Is The Work Group On Each Person’s Record. Work By Anybody Not In A Group Is Shown On Its Own Line Rather Than Left Out, So These Add Up To The Site Total.',
  'pc.sameFiguresInTheReport': 'The Same Breakdown, With A Chart, Is In The Project Report.',
  'pc.wagesOnly': 'Wages And Overtime Only, Charged To The Site Each Person Actually Worked On. Vehicles, Fuel, Materials And Subcontractors Are Not Included — This System Does Not Hold Them.',
  'co.sampleAddress': 'Al Jurf Industrial 1, Ajman, UAE',
  'co.days': 'Days',
  'co.to': 'To',
  'co.aed': 'AED — UAE Dirham',
  'co.sar': 'SAR — Saudi Riyal',
  'co.qar': 'QAR — Qatari Riyal',
  'co.omr': 'OMR — Omani Rial',
  'co.tzDubai': 'Asia/Dubai (GST +4)',
  'co.tzRiyadh': 'Asia/Riyadh (AST +3)',
  'co.tzQatar': 'Asia/Qatar (+3)',
  'co.tzMuscat': 'Asia/Muscat (+4)',
  'co.adminOnly': 'Admin Only',
  'wps.agentId': 'Agent / Bank ID',
  'wps.agentIdHint': 'The Routing Code Your Exchange House Issued You.',
  'wps.agentName': 'Agent Name',
  'wps.agentNameHint': 'Recorded For Audit Only.',
  'wps.agentSample': 'Al Ansari Exchange',
  'wps.cannotProduce': 'The File Cannot Be Produced Yet',
  'wps.agentsDiffer': 'Agents Differ Slightly In The Columns They Expect. If Your Bank Rejects This File, Send Us The Rejection And The Layout Will Be Adjusted.',
  'wps.excluded': 'These Would Be Rejected By The Bank. They Are Excluded Rather Than Sent — Fix The Record And Produce The File Again.',
  'wps.allValid': 'Every Employee Validates. The File Is Ready To Submit.',
  'imp.what': 'What',
  'imp.file': 'File',
  'imp.columns': 'Columns',
  'imp.check': 'Check',
  'imp.import': 'Import',
  'imp.blankTemplate': 'Blank Template',
  'imp.back': 'Back',
  'imp.backToColumns': 'Back To Columns',
  'imp.somethingElse': 'Import Something Else',
  'imp.download': 'Download',
  'imp.yourFile': 'Your File, Your Column Names. Point Each One At The Field It Belongs To, And See Exactly What Would Happen Before Anything Is Kept.',
  'imp.correctInExcel': 'Download Them, Correct Them In Excel, And Import That File — The Headings Are The Same, So The Columns Map Themselves And Only These Rows Are In It.',
  'lv.to': 'To',
  'lv.recordLeave': 'Record Leave',
  'lv.file': 'Leave File',
  'lv.fileBuilding': 'Building…',
  'lv.reject': 'Reject',
  'lv.markReturned': 'Mark Returned',
  'lv.intro': 'Requests, Approvals And Returns. Approved Leave Shows On The Attendance Grid Instead Of Counting As Absence.',
  'lv.noKinds': 'Leave Cannot Be Recorded Until You Have Said What Kinds Your Company Grants — Annual, Sick, Unpaid, And How Each Is Paid. That Lives In Settings → Rules → Leave Types.',
  'lv.allReturned': "Nobody's Approved Leave Has Ended Without Them Returning.",
  'lv.overdueIntro': 'These People Were Due Back And Have Not Been Marked As Returned. If Their Phone Reaches A Site, Attendance Resumes On Its Own — But Until Somebody Confirms It Here, The Daily Alert Keeps Firing.',
  'lv.comesOffNext': 'These Days Come Off The Leave They Earn Next, In Days Rather Than In Pay.',
  'us.noAccounts': 'No Accounts Yet',
  'us.noAccountsBody': 'Create Logins So Staff And Workers Can Sign In.',
  'us.emailSample': 'ali.raza@noorone.ae',
  'us.crews': 'Crews',
  'crew.title': 'Crews {name} Looks After',
  'crew.blurb':
    'A User Given A Crew Sees, Marks And Corrects The Attendance Of Everybody '
    + 'In It, On Whichever Site They Are Working. Given None, Their Sites Decide '
    + 'As Before. It Changes Who They Reach, Never What They May Do — That Is '
    + 'Their Role.',
  'crew.noneGiven':
    'No Crews Yet, So This User Reaches Only The People On Their Own Sites.',
  'crew.noneDefined':
    'This Company Has Not Divided Its Work Into Crews Yet. Add Them On An '
    + 'Employee’s Record First.',
  'crew.retired': 'Retired',
  'us.password': 'Password',
  'us.bulkCreate': 'Bulk Create',
  'us.createLogin': 'Create Login',
  'us.suggest': 'Suggest',
  'us.loseAccess': 'They Lose Access Straight Away And Cannot Sign In Until Re-enabled. Their Record, Attendance And Payslips Are All Kept.',
  'us.removed': 'They Can No Longer Sign In, And The Account Is Removed Entirely. Their Employee Record, Attendance And Payslips Are Untouched — This Is Only Their Access.',
  'us.whoCanSignIn': "Who Can Sign In, And What They May See. An Employee Account Shows Only That Person's Own Attendance, Payslips And Loan Balance — And Cannot Change Any Of It.",
  'us.shownOnce': 'Give Them These Details. The Password Is Shown Once — After This Dialog Closes It Cannot Be Read Back, Only Replaced.',
  'us.passwordChanged': 'Password Changed. Give Them The New One Below — It Cannot Be Read Back.',
  'adv.none': 'No Advances Recorded',
  'adv.noneBody': 'Record One With The Button Above. Attach The Signed Receipt And It Stays Retrievable For As Long As The Record Exists.',
  'adv.months': 'Months',
  'adv.reject': 'Reject',
  'adv.chargeGratuity': 'Charge To End-of-service',
  'adv.noneForPerson': 'No Advances On Record For This Employee.',
  'adv.everyAdvance': 'Every Advance With Its Signed Receipt, And Every Deduction Traced Back To It.',
  'adv.clickToOpen': 'Click An Advance To Open Its Receipt, Or A Deduction To Open The Payslip That Took It.',
  'self.whichWay': 'Which Way',
  'self.inSince': 'Marked In Since {time}',
  'self.notInYet': 'Not Marked In Yet Today',
  'self.alreadyIn': 'You Are Already Marked In Since {time}. Marking In Again Does Not Close Your Day — Choose Finishing Work.',
  'self.nothingToClose': 'You Are Not Marked In, So There Is Nothing To Close. Choose Starting Work.',
  'self.wasAlreadyIn': 'You Were Already Marked In, So Your Day Is Unchanged. Choose Finishing Work To Close It.',
  'self.wasNotIn': 'You Were Not Marked In, So Nothing Was Closed. Your Day Is Unchanged.',
  'self.locationOff': 'Location Off',
  'self.site': 'Site',
  'self.markAgain': 'Mark Again',
  'self.recorded': 'Recorded.',
  'self.locationMustBeOn': 'Your Location Has To Be On. Marking Your Own Attendance Is The Only Case Where Nobody Else Is There To Confirm Where You Are, So The Coordinates Are What Stands In For A Witness — Without Them Nothing Can Be Recorded.',
  'self.noBoundaryYet': 'No Site Has A Boundary Drawn Yet, So Nothing Can Check Your Location. The Site You Choose Below Is The Only Record Of Where You Were.',
  'self.whichSite': 'Which Site Are You On?',
  'self.disagrees': 'Your Location Does Not Agree With That',
  'self.savedOnPhone': 'Saved On This Phone — It Will Send Itself When You Have Signal.',
  'self.tripStarted': 'Trip Started On {plate}',
  'self.faceNotEnrolled': 'Your Face Is Not Enrolled, So Nothing Can Recognise You Yet. Ask The Office To Enrol It — It Is Done Once And Takes A Minute.',
  'pp.title': 'Papers',
  'pp.whatIsIt': 'What Is It',
  'pp.docTitle': 'Title',
  'pp.titleHint': 'Left Blank, The Type Is Used.',
  'pp.titleSample': 'Building Permit 2026',
  'pp.issuedOn': 'Issued On',
  'pp.expiresOn': 'Expires On',
  'pp.expiryHint': 'A Permit That Lapses Stops The Job.',
  'pp.note': 'Note',
  'pp.noteSample': 'Renewal Applied For 3 Aug',
  'pp.nothingAttached': "Nothing Attached. The Permit, The Contract And The Client's Approval All Live In Somebody's Email Until They Are Here.",
  'pp.fiveIsTheLimit': 'Five Is The Limit. Delete One Before Adding Another.',
  'bl.pasteTitle': 'Paste Email Addresses',
  'bl.list': 'List',
  'bl.downloadSheet': 'Download Credential Sheet',
  'bl.pasteEmailList': 'Paste Email List',
  'bl.downloadNow': 'Download This Now — The Passwords Are Not Recoverable.',
  'bl.storedHashed': 'They Are Stored Hashed, Exactly Like Any Other Password. Once You Close This Dialog The Only Way Into An Account Is To Reset It.',
  'bl.everyActive': 'Every Active Employee Without An Account. Each Signs In With Their Own Email Address — Whatever They Already Use — So A Forgotten Password Can Actually Reach Them. Passwords Are Generated Here And Shown Once, On The Next Screen.',
  'bl.onePerLine': 'One Person Per Line: Employee Code And Their Email Address, In Either Order. Commas, Tabs Or Spaces All Work, So A Column Pair Copied Straight Out Of Excel Pastes Cleanly.',
  'pd.idSample': '2D4e8766-55aa-407f-8f80-f728c7259ba9',
  'pd.findIt': 'Find It',
  'pd.aboutToBeDestroyed': 'About To Be Destroyed',
  'pd.deletePermanently': 'Delete Permanently',
  'pd.whatWasDeleted': 'What Has Been Permanently Deleted',
  'pd.everywhereElse': 'Everywhere Else In This System, Finished Records Are Deactivated Rather Than Removed — So That A Payslip From Two Years Ago Still Means What It Said. This Is The Exception, And It Is Permanent. What Survives Is A Line In The Log Recording That It Happened, Who Did It And Why. Nothing Else.',
  'pd.andEverythingBelow': 'And Everything Below, Which Goes With It. These Are Not Counted Anywhere Afterwards, Because There Is Nothing Left To Count.',
  'pd.nothingPointsAtIt': 'Nothing Else In The System Points At This One.',
  'pd.wholeCompany': 'This Is The Whole Company. Every Employee, Every Payslip, Every Hour And Every Scan Goes With It, For Everybody.',
  'pd.cannotBeUndone': 'This Cannot Be Undone, And Nothing Is Kept. Everything Listed Above Goes At The Same Moment.',
  'shell.closeMenu': 'Close Menu',
  'shell.signOut': 'Sign Out',
  'shell.openMenu': 'Open Menu',
  'shell.search': 'Search',
  'shell.rulesIncomplete': 'Rules Incomplete',
  'shell.notPermitted': 'Not Permitted',
  'shell.notOpenToRole': 'This Section Is Not Open To Your Role',
  'shell.askAdmin': 'If You Need Access, Ask An Administrator To Adjust Your Role In Settings → Roles.',
  'cp.closeSearch': 'Close Search',
  'cp.close': 'Close',
  'cp.placeholder': 'Search People, Jobs, Plates, Documents…',
  'cp.searchEverything': 'Search Everything',
  'cp.employees': 'Employees',
  'cp.projects': 'Projects',
  'cp.vehicles': 'Vehicles',
  'cp.documents': 'Documents',
  'cp.payroll': 'Payroll',
  'cp.goTo': 'Go To',
  'uc.title': 'Shifts Nobody Closed',
  'uc.subtitle': 'Marked In And Never Marked Out. Each One Is Somebody Showing As Present And Being Paid For None Of That Day.',
  'uc.nothingOutstanding': 'Nothing Outstanding',
  'uc.nothingOutstandingBody': 'Every Shift Older Than Today Has A Leaving Time Against It.',
  'uc.leftOn': 'Left On',
  'uc.at': 'At',
  'uc.whoSaysSo': 'Who Says So',
  'uc.keptWithChange': 'Kept With The Change.',
  'uc.foremanConfirmed': 'Foreman Confirmed',
  'uc.superAdminOnly': 'Closing A Shift Changes What Somebody Is Paid, From A Fact Nobody Witnessed, So It Is Limited To A Super Admin. These Are Shown Here So You Can Find Out When Each Person Actually Left.',
  'uc.threeDays': 'Open For More Than Three Days. Ask Their Foreman Before Closing It — After This Long, A Time Typed From Memory Is A Guess With Somebody Else’s Wages Attached.',
  'pj.noProjects': 'No Projects Yet',
  'pj.noProjectsBody': 'Add A Job Number, Then Set Its Site Boundary So Attendance Can Be Recorded Against It.',
  'pj.nameSample': 'Marina Tower — Structural Works',
  'pj.locationSample': 'Dubai Marina',
  'pj.everyJobNumber': 'Every Job Number, And The Site Boundary That Charges Hours To It.',
  'pj.addFirst': 'Add The First Project',
  'pj.addProject': 'Add Project',
  'pj.none': 'None',
  'pj.deactivate': 'Deactivate',
  'pj.siteFile': 'Site File',
  'pj.siteFileBuilding': 'Building…',
  'pj.siteFileBlurb': 'Every Site With Its Coordinates, Its Boundary And The Hours It Keeps — The File To Keep, And To Import Back.',
  'ap.companyTheme': 'The Company Theme Applies To Everyone. Your Own Colours, And Light Or Dark, Stay Yours.',
  'ap.adminOnly': 'Only An Administrator Can Change The Company Theme. You Can Still Switch Between Light And Dark From The Header — That Choice Is Yours Alone.',
  'ap.pickAnyOfNine': "Pick Any Of The Nine If The Company's Is Hard For You To Read, Or Simply Not To Your Taste. It Changes Nothing For Anybody Else, And You Can Go Back To The Company's Whenever You Like.",
  'ap.useCompanyAgain': 'Use The Company Theme Again',
  'ap.headerControl': 'Use The Appearance Control In The Header To Switch Between Light, Dark And Matching Your Device. Each Of The Three Company Themes Is Designed In Both, So Nothing Loses Contrast Either Way — A Supervisor On A Night Shift And A Storekeeper Under Fluorescent Light Do Not Want The Same Screen.',
  'ap.noRightSet': 'There Is No Right Set: Somebody Who Lives In Documents Never Opens Payroll, And The Reverse Is Just As True. Pick The Ones You Actually Use.',
  'ap.backToDefault': 'Back To The Default',
  'aj.howYouKnow': 'How You Know',
  'aj.chooseSite': 'Choose The Site',
  'aj.howYouKnowHint': 'Optional, And Worth A Line. In A Month This Is The Only Thing That Will Say Why This Day Was Charged Where It Was.',
  'aj.evidenceSample': 'Foreman Confirmed The Crew Was At Al Jurf',
  'aj.changeJob': 'Move This Day To Another Job',
  'aj.chargedNow': 'This Day Is Charged To',
  'aj.movingOnlyMovesCost':
    'Moving It Changes Which Job Carries The {hours} Hours. The Hours '
    + 'Themselves, The Status And The Pay Do Not Change.',
  'aj.paidWithNoSite':
    '{hours} Hours Were Worked And Paid, And No Site Was Matched To Them. '
    + 'The Hours And The Pay Are Already Right — This Only Decides Which Job '
    + 'Carries The Cost.',
  'aj.chargeToThisJob': 'Charge To This Job',
  'aj.moveToThisJob': 'Move To This Job',
  'aj.charging': 'Saving…',
  'aj.putItBack': 'Remove This And Go Back To What The Scans Said',
  'aj.whichJob': 'Which Job Was This?',
  'aj.recordedAsGuess': 'This Will Be Recorded As A Guess, Based On Where They Were Last Seen. Reports Can Then Say How Much Of The Job Cost Rests On One.',
  'aj.survivesRecalc': 'It Survives A Recalculation. The Day Is Rebuilt From Its Scans Every Time Attendance Is Recomputed, And This Is Re-applied Afterwards — But Only To The Hours The Scans Could Not Place. A Site The Scans Did Find Is Never Overwritten.',
  'aj.onlyWorkedDay': 'Only A Worked Day Can Be Charged To A Job',
  'ef.nameSample': 'Ali Raza',
  'ef.emiratesIdSample': '784-XXXX-XXXXXXX-X',
  'ef.phoneSample': '+971 5X XXX XXXX',
  'ef.ibanSample': 'AE070331234567890123456',
  'ef.close': 'Close',
  'ef.active': 'Active',
  'sl.title': 'Salary Ledger',
  'sl.date': 'Date',
  'sl.reference': 'Reference',
  'sl.description': 'Description',
  'sl.earned': 'Earned',
  'sl.balance': 'Balance',
  'sl.nothingPayable': 'Nothing Is Payable.',
  'sl.noPayrollYet': 'No Payroll Has Been Run For This Employee Yet.',
  'sl.earnedIs': 'Earned Is What The Work Came To Before Anything Was Taken Off It, And Everything Taken Off Has Its Own Line Below — Advance Recovery, Fines, Cash Handed Over During The Month. The Balance Is What Is Still Owed. A Month Counts As Paid When Its Payroll Period Is Marked Paid.',
  'mp.appearance': 'Appearance',
  'mp.device': 'Device',
  'mp.gotIt': 'Got It',
  'mp.couldNotLoad': 'This Could Not Be Loaded',
  'mp.noEmployeeRecord': 'This Account Has No Employee Record',
  'mp.couldNotReach': 'It Is Not That There Is Nothing To Show — The App Could Not Reach It. Try Again, And Tell Your Administrator If It Keeps Happening.',
  'mp.notLinked': 'Your Login Works, But It Is Not Linked To An Employee, So There Is Nothing To Show. Ask An Administrator To Link It On Your Employee Profile.',
  'mp.payBeingWorkedOut': 'Your Most Recent Pay Is Still Being Worked Out And Is Not Shown Yet.',
  'cd.undoBtn': 'Undo',
  'cd.reset': 'Reset',
  'mg.prevMonth': 'Previous Month',
  'mg.nextMonth': 'Next Month',
  'mg.showingPeople': 'People {from}–{to} Of {total}',
  'mg.allEmployees': 'All Employees',
  'mg.nothingThisMonth': 'Nothing Recorded This Month',
  'mg.nothingBody': 'The Grid Fills In As Attendance Is Captured And Recalculated.',
  'mg.anyPresent': 'Any Day Present',
  'mg.anyAbsent': 'Any Day Absent',
  'mg.anyLeave': 'Any Day On Leave',
  'mg.employee': 'Employee',
  'mg.subAttendance': 'Attendance — Present, Absent Or On Leave',
  'mg.subSite': 'Site — The Job Number The Day Is Charged To',
  'mg.subApprovedOt': 'Approved Overtime, In Hours',
  'mg.subActualOt': 'Actual Overtime Recorded, In Hours',
  /* The six letters that close a row. Titles rather than headings: the
     column is a letter wide, and the words are what somebody sees when
     they rest on it. */
  'mg.subPresent': 'Days Present',
  'mg.subAbsent': 'Days Absent',
  'mg.subOnLeave': 'Days On Leave',
  'mg.subHoliday': 'Public Holidays',
  'mg.subRestDay': 'Weekly Rest Days',
  'mg.subOvertimeHours': 'Overtime Hours For The Month',
  'doc.noDocs': 'No Documents Yet',
  'doc.deleteTitle': 'Delete {title}?',
  'doc.deleteAria': 'Delete {title}',
  'doc.noDocsBody': 'Upload Passports, Visas And Contracts So Their Expiry Dates Start Driving Alerts.',
  'doc.sizeHint': 'Image Or PDF, Up To 10 MB.',
  'doc.storedPrivately': 'Stored Privately. Every View Opens A Link That Expires A Minute Later.',
  'doc.uploadDocument': 'Upload Document',
  'doc.uploadFirst': 'Upload The First Document',
  'doc.deleteWarn': 'The File Is Removed From Storage As Well As The Record, So This Cannot Be Undone. {name} Will Need It Uploaded Again.',
  'gc.title': 'Charge This Advance To The End-of-service',
  'gc.noEndOfService':
    'This Person Is Not On The Company Payroll, So They Have No '
    + 'End-of-service For An Advance To Be Charged Against. Closing It This '
    + 'Way Would Write The Money Off Without Anybody Deciding To — The '
    + 'Advance Would Show As Repaid, And No Settlement Would Ever Take It '
    + 'Back.',
  'gc.noEndOfServiceFix':
    'Put Them On The Payroll First If That Is The Intention, Or Write The '
    + 'Advance Off Instead, Which Records Who Decided It And Why.',
  'gc.amountToCharge': 'Amount To Charge',
  'gc.amountHint': 'Part Of It Is Allowed. Whatever Is Left Stays As An Ordinary Advance And Goes On Being Recovered From Salary.',
  'gc.whatWasAgreed': 'What Was Agreed',
  'gc.agreedHint': 'This Is What The Settlement Will Show Beside The Deduction, Possibly Years From Now. It Is The Only Thing That Will Explain The Figure To Them.',
  'gc.agreedSample': 'Asked For It Before Annual Leave — Agreed With The Owner',
  'gc.keepsFullValue': 'Their Gratuity Keeps Its Full Value On Screen. This Appears Once, On Their Final Settlement, As A Dated Deduction Against It.',
  'fl.plateSample': 'DXB A 12345',
  'fl.licenceHint': 'A Driver Cannot Be Assigned Without A Licence On File. The Database Refuses It.',
  'fl.tripsStart': "Trips Start Themselves When A Driver's Phone Detects Vehicle Motion. There Is No Button To Forget.",
  'fl.travelTime': 'Travel Time Is Charged To The Destination — Somebody Driving To Job 1570 Is Working For 1570. The Run Back To The Yard After The Last Site Is Left Unallocated, So No Job Pays For A Journey It Did Not Receive.',
  'fl.nothingAttached': 'Nothing Attached To This Vehicle Yet — No Registration, No Insurance, Nothing A Traffic Stop Would Ask For.',
  'fl.fiveDocuments': 'This Vehicle Has Its Five Documents. Delete One Before Adding Another. The Limit Is Enforced In The Database, So It Holds Whatever Any Screen Says.',
  'rl.choose': 'Choose…',
  'rl.paidLeave': 'Paid Leave',
  'rl.unpaidLeave': 'This Type Is Unpaid',
  'rl.document': 'Document',
  'rl.documentRequired': 'Document (Required)',
  'rl.reason': 'Reason',
  'rl.reasonSample': 'Fever, Seen At The Clinic',
  'rl.certificateHint': 'A Photograph Of The Certificate Is Enough.',
  'rl.joined': 'Joined',
  'rl.noTypes': 'Your Company Has Not Set Up Its Leave Types Yet, So A Request Cannot Be Made. Speak To Your Administrator.',
  'rl.sent': 'Your Request Has Been Sent. It Shows As Pending Until An Administrator Decides — You Will See The Result On This Screen.',
  'rl.nothingAccrued': 'Nothing Has Accrued For This Type. It May Not Accrue At All, Or You May Not Have Been Here Long Enough Yet.',
  'ft.nothingWaiting': 'Nothing Is Waiting On This Phone.',
  'ft.checkAgain': 'Check Again',
  'ft.allowLocation': 'Allow Location',
  'ft.openPermission': 'Open Permission',
  'ft.tryAgain': 'Try Again',
  'ft.sendNow': 'Send Now',
  'ft.allSent': 'All Sent',
  'rl.joinedOn': 'Joined {date}',
  'rl.unknown': 'Unknown',
  'rl.ofService': '{length} Of Service',
  'al.nothingYet': 'Nothing Recorded Yet',
  'al.nothingYetBody': 'Changes To Employees, Pay And Attendance Appear Here As They Happen.',
  'al.created': 'Created',
  'al.changed': 'Changed',
  'al.deleted': 'Deleted',
  'al.approved': 'Approved',
  'al.ran': 'Ran',
  'al.whoMaySee': 'The Trail Spans Salary And Payroll Changes Across The Whole Company, So It Is Limited To Roles Holding {permission} — By Default Super Admin, HR Admin And Payroll Officer.',
  'em.senderSample': 'NoorOne ERP',
  'em.noreplySample': 'noreply@yourcompany.ae',
  'em.hrSample': 'hr@yourcompany.ae',
  'em.youSample': 'you@yourcompany.ae',
  'em.nothingSent': 'Nothing Sent Yet',
  'em.nothingSentBody': 'Send A Test Above To Check The Connection.',
  'em.save': 'Save',
  'em.sendTest': 'Send Test',
  'ea.title': 'Correct This Advance',
  'ea.dateAgreed': 'Date Agreed',
  'ea.instalments': 'Instalments',
  'ea.reason': 'Reason',
  'ea.reasonSample': 'Family Emergency',
  'ea.recordedAgainst': 'Whatever You Change Here Is Recorded Against This Advance With Your Name On It, And Shows In Its History.',
  'fe.backToAttendance': 'Back To Attendance',
  'fe.deleteTitle': 'Delete The Face For {name}?',
  'fe.deleteBody': 'They Stop Being Recognised At The Kiosk Immediately And Will Have To Be Enrolled Again. Their Attendance History Is Not Affected.',
  'fe.deleteConfirm': 'Delete The Face',
  'fe.deleteAria': 'Delete The Face For {name}',
  'fe.enrolAria': 'Enrol {name}',
  'fe.noMatch': 'Nobody Matched',
  'fe.noMatchBody': 'Try A Shorter Search.',
  'fe.noneActive': 'No Active Employees',
  'fe.noneActiveBody': 'Add Employees Before Enrolling Faces.',
  'fe.searchPlaceholder': 'Search By Name Or Code…',
  'fe.title': 'Face Enrolment',
  'fe.threeViews': 'Three Views Of Each Face — Straight On, And Turned A Little Each Way — So The Kiosk Still Knows Somebody Who Is Not Looking Right At It.',
  'fe.notEnrolled': 'Anyone Not Enrolled Must Still Be Marked By Roll-call.',
  'fe.noPhotograph': 'No Photograph Is Kept. The Camera Produces 128 Numbers Describing The Face And The Image Is Discarded — The Numbers Cannot Be Turned Back Into A Picture. Tell The Person What Is Being Recorded Before You Capture It; The Consent Is Stored Against Your Name.',
  'fe.alreadyEnrolled': "This Face Is Already Enrolled As {name}. Enrolling It Twice Would Split This Person's Attendance Across Two Records. Check You Have The Right Person, Or Delete The Other Enrolment First.",
  'ra.noWorkspace': 'Signed In, But This Account Has No Workspace',
  'ra.toFix': 'To Fix',
  'ra.runMigration': 'Run {file} In The SQL Editor, If You Have Not Already.',
  'ra.signOutAndBack': 'Sign Out And Back In.',
  'ra.tryAgain': 'Try Again',
  'ra.signOut': 'Sign Out',
  'bc.thisDeviceOnly': 'On This Device Only. Turning It On Elsewhere Is A Separate Step.',
  'bc.turnedOn': 'Turned On For This Device',
  'bc.notSetUp': 'Not Set Up On This Device',
  'bc.turnOff': 'Turn Off On This Device',
  'osn.title': 'On Site Now',
  'osn.nobodyIn': 'Nobody Is Marked In',
  'osn.nobodyInBody': 'People Appear Here The Moment A Face Is Scanned, And Drop Off When They Are Scanned Out. An Empty List Before The Shift Starts Is Expected; An Empty One At Nine In The Morning Means Nothing Is Reaching The Scanner.',
  'osn.couldNotRead': 'Who Is On Site Could Not Be Read',
  'osn.migrationHint': 'If This Mentions {view}, Migration 0069 Has Not Been Applied.',
  'dt.recordingUntilFinish': 'Your Location Is Being Recorded Continuously Until You Press Finish. It Keeps Going With The Screen Off And With The App Closed.',
  'dt.nothingLost': 'Nothing Is Lost — They Go As Soon As There Is Signal.',
  'dt.startedByMistake': 'Started By Mistake — Discard',
  'dt.pressStart': 'Press Start When You Set Off And Finish When You Are Done. Between Those Two, Your Route, Distance And Every Stop Are Recorded. Nothing Is Recorded Before Or After — The Location Goes Off The Moment The Trip Ends.',
  'rc.markCrew': 'Mark The Crew In When Work Starts And Out When It Ends. The Hours Between Are What Gets Paid.',
  'rc.passThePhone': 'Pass The Phone Down The Line — Each Person Is Recognised And Stamped On Their Own.',
  'rc.noBoundary': 'This Site Has No Boundary Set, So Nothing Can Confirm You Are There. The Roll-call Is Still Accepted And Flagged.',
  'rc.outsideSite': 'You Appear To Be Outside This Site. The Roll-call Will Be Recorded And Marked For Review — It Is Not Blocked, Because A Poor GPS Fix Should Not Cost Your Crew Its Day.',
  'rc.noLocation': 'No Location',
  'rc.outsideBoundary': 'Outside The Site Boundary',
  'sc.thisDevice': 'This Device',
  'sc.testCamera': 'Test The Camera',
  'sc.wherePhone': 'Where This Phone Thinks It Is',
  'sc.numbersRecorded': 'The Numbers Already Recorded',
  'sc.disagree': 'This Looks For Figures That Disagree With Each Other — It Cannot Tell You That Somebody Was Where Their Coordinates Say They Were, Only That Nothing In The Record Contradicts Itself. The One Check No Software Can Make For You Is The Map Above: Stand Somewhere You Know, Measure, And See Whether The Pin Lands There.',
  'st.gratuityFullValue': 'Gratuity Is Shown At Its Full Value And Anything Already Drawn Against It Appears Above As Its Own Dated Line. That Is Why The Two Can Be Checked Against Each Other — See The Advance Statement For The Same Entries.',
  'st.changedSinceApproval': 'Changed Since Approval',
  'st.exportHeldBack': 'The Export Is Held Back Until The Gaps Above Are Resolved. A Settlement Document Is Treated As Final By Whoever Receives It.',
  'dr.month': 'Month',
  'dr.year': 'Year',
  'dr.from': 'From',
  'dr.to': 'To',
  'mp2.notConfigured': 'Maps Are Not Configured',
  'mp2.keyRejected': "The Key Is Present And Was Rejected. Almost Always This Is The Referrer Restriction: The App Runs Inside A WebView Whose Origin Is Not The Dashboard's Domain, So A Key Restricted To That Domain Alone Refuses It.",
  'mp2.addReferrer': "In Google Cloud → Credentials, Add This To The Key's Allowed Referrers:",
  'ep.noEmployees': 'No Employees Yet',
  'ep.noEmployeesBody': 'Add Your First Employee, Or Import The Existing Master File.',
  'ep.addFirst': 'Add The First Employee',
  'nb.sweepNow': 'Run The Absence Sweep And Expiry Checks Now',
  'nb.whatAppears': 'Absences, Overdue Returns And Expiring Documents Appear Here.',
  'nb.markAllRead': 'Mark All Read',
  'nb.nothingNeedsYou': 'Nothing Needs You',
  'rt.somethingBroke': 'Something Broke',
  'rt.couldNotShow': 'This Page Could Not Be Shown',
  'rt.backToDashboard': 'Back To Dashboard',
  'db.deleteThis': 'Delete This Record',
  'db.cannotBeUndone': 'This Cannot Be Undone. A Copy Of The Record Is Kept In The Activity Log, But The Row Itself And Anything That Depends On It Are Removed.',
  'file.sendAria': 'Send {name}',
  'file.open': 'Open The File',
  'file.opening': 'Opening…',
  'file.view': 'View',
  'file.send': 'Send',
  'file.sendNow': 'Send Now',
  'file.preparing': 'Preparing…',
  'file.readyTapSend': 'Ready — Tap Send Again.',
  'file.savedInstead': 'Saved Instead.',
  'file.noShareSheet': 'This Browser Would Not Open The Share Sheet. Saving Instead.',
  'db.deleteTitle': 'Delete {label}?',
  'db.deleteAria': 'Delete {label}',
  'db.superAdminOnly': 'Super Admin Only. A Copy Is Kept In The Activity Log; The Record Itself Is Removed Along With Anything Depending On It.',
  'xl.chooseLanguage': 'Choose The Document Language',
  'xl.wholeDocument': 'The Whole Document, In One Language. Names, Sites And Anything Typed By A Person Stay Exactly As They Were Written.',
  'mp2.addKey': 'Add {variable} To The Environment And Rebuild. Coordinates Are Still Recorded And Every Other Screen Works Without It.',
  'dash.noCostPermission': 'Days Worked Over A Period Are Worked Out From The Same Figures As Cost, Which You Do Not Have Permission To See. This Shows Who Is On Site Today Instead.',
  'dash.costsUnreadable': 'Costs Could Not Be Read, So The Figures Below Are Missing Rather Than Zero: {message}',
  'dash.reviewDevices': 'Review Devices',
  'rf.subtitle': 'Hours Short, Overtime Missing, A Payslip That Looks Wrong.',
  'rf.body': 'Anything You Raise Stays Here With Its Answer, So You Can Check Back Rather Than Having To Ask Again.',
  'rf.writtenDown': 'Whatever You Report Is Written Down With The Date And Time, And Goes To The Office. You Will See The Answer Here — Nothing Depends On Catching Somebody At The Right Moment.',
  'rp.ownScreen': 'Exports From Its Own Screen, Where The Layout It Needs Lives.',
  'rp.labourCost': 'Labour Cost Per Job Over A Date Range Rather Than A Single Payroll Month — The One Report With No Screen Of Its Own. A Summary By Project, And The Employee-by-employee Detail Behind It.',
  'rp.open': 'Open',
  'ap.saveTheBar': 'Save The Bar',
  'ih.neverFinished': 'This Run Never Finished — The Browser Was Closed, Or The Connection Went. Whatever Went In Stayed In; The Counts Above Are Exact.',
  'ih.closeIt': 'Close It',
  'rw.remove': 'Remove',
  'rw.breaksFixed': 'Breaks At A Fixed Time',
  'sl2.settings': 'Settings',
  'sl2.howItBehaves': 'How The System Behaves For Your Company.',
  'ns.pressBackAgain': 'Press Back Again To Close',
  'pc2.title': 'Notifications On This Phone',
  'pc2.subtitle': 'Send Yourself One And See Whether It Arrives.',
  'rh.nothingRecorded': 'Nothing Recorded Against This One. Either It Has Not Been Changed Since The Audit Trail Was Switched On, Or This Kind Of Record Is Not Tracked.',
  'rh.nothingWorthReporting': 'Nothing Worth Reporting Changed.',
  'ef.selfScanHint': 'Face Scan Only, And Their Location Must Be On. Without A Position The Database Refuses The Scan — There Is No Way Around It, Because This Is The One Case With Nobody Standing There To Witness It.',
  'ef.autoAttendanceHint': "Only The Company's Half Of This. They Still Have To Switch It On In Their Own Portal, And The Handset Still Has To Be Approved — None Of Which Happens Without All Three. Deliberately Separate From The Switch Above: Scanning A Face Is One Moment Somebody Chose, And This Runs Unattended All Day.",
  'll.noSignIns': 'No Sign-ins Recorded',
  'll.noSignInsBody': 'Every Successful Sign-in Appears Here From Now On.',
  'cv.firstScan': 'This Takes A Moment On The First Scan.',
  'ld.loading': 'Loading',
  'imp.mustBeFilled': 'Must Be Filled',
  'fk.noColumn': 'This Database Has No {column} Column, So Scans Are Uploading Without It. Attendance Is Unaffected; The Site You Name Is Not Being Kept Alongside The Measured One Until Migration 0065 Is Applied.',
  'fl.noLicence': 'No Driving Licence On File For This Person. Upload It Under Documents As A \u201cCertificate Or Licence\u201d With {word} In The Title \u2014 Saving This Vehicle Will Be Refused Until You Do.',
  'leaves.needsDocument': '{type} Requires A Supporting Document.',
  'rl.needsDocument': '{type} Needs A Supporting Document — A Medical Certificate, For Example.',
  'bi.inArabic': 'In Arabic',
  'bi.optionalHint': 'Optional. Left Empty, Arabic Screens And Documents Show The English.',
  'co.holidayExampleAr': 'عيد الفطر',
  'co.upcoming': 'Upcoming',
  'rules.leaveExampleAr': 'إجازة سنوية',
  'rules.allowanceExampleAr': 'بدل موقع',
  'rules.leaveTypeN': 'Leave Type {n}',
  'rules.aLeaveType': 'Leave Type',
  'rules.removeNamed': 'Remove {name}',
  'rules.lunchAr': 'الغداء',
  'set.arabicCoverage': 'Names Without Arabic',
  'set.arabicCoverageSub': 'These Show Their English On Arabic Screens And Documents Until Somebody Writes The Arabic.',
  'set.arabicComplete': 'Everything The Company Named Has An Arabic Name.',
  'set.arabicDesignations': 'Job Titles',
  'set.arabicProjects': 'Projects',
  'set.arabicHolidays': 'Holidays',
  'set.arabicRoles': 'Roles',
  'set.arabicEmployees': 'Employees',
  'set.arabicMissing.one': '{count} Missing',
  'set.arabicMissing.other': '{count} Missing',
  'set.arabicHowToFill': 'Type Them In As You Go, Or Propose Them All At Once With Npm Run Translate:master — That Writes A File For You To Check Before Anything Is Saved.',
  'fk.nearestIs': 'Nearest Is {jobNo} — {site}.',
  'fe.cameraStarting': 'Starting The Camera…',
  'fe.cameraNotReady': 'The Camera View Is Not Ready.',
  'st.correctTimes': 'Correct These Times',
  'st.addShift': 'Add A Shift',
  'st.correctIntro': 'The Original Is Kept Exactly As The Phone Recorded It. This Is Written Beside It As A Correction, With Your Name On It.',
  'st.addIntro': 'For A Shift The Phones Never Captured — A Dead Battery, A Handset Left In The Pickup. It Carries No Scans, So The Note Is What It Rests On.',
  'st.arrived': 'Arrived',
  'st.left': 'Left',
  'st.leftHint': 'Leave It Empty For A Shift Still Open.',
  'st.why': 'Why',
  'st.whyHint': 'Read Months Later By Somebody Asking Why The Hours Differ From The Scans.',
  'st.whySample': 'Foreman Confirmed They Left At Six',
  'st.recalcNote': 'The Hours Are Worked Out Again From These Times — Overtime, Night Hours And The Site Split With Them. Nothing Is Typed.',
  'st.saving': 'Saving…',
  'st.theShift': 'The Shift',
  'st.shiftsThisDay.one': '{count} Shift This Day',
  'st.shiftsThisDay.other': '{count} Shifts This Day',
  'st.sumOfThese': 'Hours Are The Sum Of These',
  'dh.title': 'What Was Changed On This Day',
  'dh.nothingChanged': 'Nothing On This Day Has Been Changed By Hand.',
  'dh.somebody': 'Somebody',
  'dh.added': 'Added',
  'dh.changed': 'Changed',
  'dh.removed': 'Removed',
  'dh.aShift': 'A Shift',
  'dh.theDay': 'The Day',
  'st.noShifts': 'No Shifts',
  'st.nothingRecorded': 'Nothing Was Recorded For This Day',
  'st.noSite': 'No Site',
  'st.carried': 'Same Site',
  'st.carriedWhy': 'This Shift Matched No Site Boundary. Its Hours Were Charged To The Site Worked Earlier That Day.',
  'st.stillOpen': 'Still Open',
  'st.edit': 'Change These Times',
  'st.remove': 'Remove This Shift',
  'st.removeTitle': 'Remove This Shift?',
  'st.removeBody': 'It Stays In The Record Marked As Removed, And Stops Counting Towards The Hours. Say Why Below.',
  'st.corrected': 'Corrected',
  'st.srcGeofence': 'From The Site Fence',
  'st.srcRollcall': 'From A Roll Call',
  'st.srcTrip': 'From A Trip',
  'st.removeWhySample': 'Same Arrival Scanned Twice',
  'ld.backToLeave': 'Back To Leave',
  'ld.edit': 'Correct This Request',
  'ld.editTitle': 'Correct This Leave Request',
  'ld.editIntro': 'The Decision And Its History Are Kept. What You Change Is Appended To Them, With Your Name On It.',
  'ld.notFound': 'That Leave Request Could Not Be Opened',
  'ld.notFoundBody': 'It May Have Been Removed, Or You May Not Have Permission To See It.',
  'ld.unknownPerson': 'Unknown',
  'ld.dates': 'Dates',
  'ld.days': 'Days',
  'ld.halfDay': 'Half Day',
  'ld.expectedBack': 'Expected Back',
  'ld.expectedHint': 'Once This Date Passes With No Attendance, A Daily Alert Starts.',
  'ld.actuallyBack': 'Actually Back',
  'ld.notSaid': 'Not Said',
  'ld.notYet': 'Not Yet',
  'ld.raisedOn': 'Raised On',
  'ld.reason': 'Reason',
  'ld.type': 'Leave Type',
  'ld.history': 'Decisions And Changes',
  'ld.why': 'Why',
  'ld.whyHint': 'It Sits Beside The Original Decision, Which Is Otherwise The Only Record.',
  'ld.whySample': 'Wrong Month Entered',
  'ld.recalcNote': 'Every Day On Both Sides Of The Change Is Worked Out Again — A Day That Stops Being Leave Stops Being Paid As Leave.',
  'leaves.status.pending': 'Awaiting A Decision',
  'leaves.status.approved': 'Approved',
  'leaves.status.rejected': 'Rejected',
  'leaves.status.cancelled': 'Cancelled',
  'leaves.status.taken': 'Taken',
  'pm.title': 'Nothing Is Lost — The Database Is Behind This App',
  'pm.body': 'A Recent Update Reads Columns That Have Not Been Added Yet, And A Query Naming One Of Them Returns Nothing At All Rather Than Returning Less. That Is Why Screens Look Empty. Every Figure Is Still There.',
  'pm.how':
    'Run Npm Run Db:pending -- --From <Migration>, Then Paste '
    + 'Supabase/setup/pending.sql Into The SQL Editor. The Full Schema.sql Is '
    + 'Over A Megabyte And The Editor Refuses It. Check-columns.sql Lists '
    + 'Exactly Which Are Missing.',
  'st.correctIn': 'Correct The Check-in Time',
  'st.correctOut': 'Correct The Check-out Time',
  'st.correct': 'Correct',
  'st.correctInIntro': 'The Scan Below Stays Exactly As The Camera Recorded It. What Changes Is The Time The Hours Are Counted From — For Somebody Who Was On Site Before The Phone Said So.',
  'st.correctOutIntro': 'The Scan Below Stays Exactly As The Camera Recorded It. What Changes Is The Time The Hours Are Counted To — For Somebody Who Worked Past The Moment They Scanned Out, Or Scanned Out Late.',
  'st.whyLate': 'Arrived At Seven, Phone Had No Signal Until Twenty Past',
  'adp.shiftN': 'Shift {n}',
  'qe.couldNotBeRead': 'This Could Not Be Read',
  /* Names the file somebody can actually paste. This used to say "apply the
     pending migrations" and point at the checker, which lists what is missing
     and does not apply anything — so the instruction ended one step short of
     the thing that fixes it. */
  'qe.missingColumn':
    'The Database Is Missing A Column This Build Reads. Run '
    + 'Npm Run Db:pending, Then Paste Supabase/setup/pending.sql Into The SQL '
    + 'Editor. Check-columns.sql Lists Exactly Which Are Missing.',
  'qe.notPermitted': 'The Database Refused The Request. Your Role May Not Carry The Permission This Screen Needs.',
  'qe.nothingLost': 'Nothing Has Been Deleted. This Is A Failure To Read, Not A Failure To Keep.',
  'pay.periodsFailed': 'The Payroll Periods Could Not Be Read',
  'pay.slipsFailed': 'The Payslips Could Not Be Read',
  'pay.jobCostFailed': 'The Job Cost Could Not Be Read',
  'ntp.title': 'This Period Ran, And Found Nobody To Pay',
  'ntp.noEmployees': 'There Are No Employees On The Books Yet. Add Them, Then Calculate This Period Again.',
  'ntp.allJoinedLater': 'Everybody On The Books Joined After This Period Ended. The Earliest Joining Date Is {date}, And This Period Ended {period} — So Nobody Was Employed During It. Create A Period Covering A Month People Were Actually Here For.',
  'ntp.noAttendance': 'People Were Employed During This Period, But No Attendance Was Recorded Between {from} And {to}. Check That The Recompute Job Is Running, And That Scans Exist For Those Dates.',
  'ntp.nothingLost': 'Nothing Has Been Deleted. A Run That Finds Nobody Writes No Payslips, Which Looks The Same As A Period Never Calculated.',
  'status.present': 'Present',
  'status.absent': 'Absent',
  'status.leave': 'On Leave',
  'status.on_leave': 'On Leave',
  'status.holiday': 'Public Holiday',
  'status.rest_day': 'Weekly Holiday',
  'status.not_joined': 'Not Joined Yet',
  'status.left': 'Left',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.pending': 'Pending',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.cancelled': 'Cancelled',
  'status.draft': 'Draft',
  'status.open': 'Open',
  'status.closed': 'Closed',
  'status.done': 'Done',
  'status.completed': 'Completed',
  'status.confirmed': 'Confirmed',
  'status.planned': 'Planned',
  'status.in_progress': 'In Progress',
  'status.on_hold': 'On Hold',
  'status.suspended': 'Suspended',
  'status.retired': 'Retired',
  'status.blocked': 'Blocked',
  'status.superseded': 'Superseded',
  'status.resolved': 'Resolved',
  'status.cleared': 'Cleared',
  'status.discarded': 'Discarded',
  'status.skipped': 'Skipped',
  'status.failed': 'Failed',
  'status.queued': 'Queued',
  'status.running': 'Running',
  'status.calculating': 'Calculating',
  'status.review': 'In Review',
  'status.locked': 'Locked',
  'status.paid': 'Paid',
  'status.settled': 'Settled',
  'status.disbursed': 'Disbursed',
  'status.taken': 'Taken',
  'status.sent': 'Sent',
  'status.accepted': 'Accepted',
  'status.submitted': 'Submitted',
  'status.validated': 'Validated',
  'status.exported': 'Exported',
  'status.attendance.left': 'Left For The Day',
  'status.employee.left': 'Left The Company',
  'status.period.open': 'Not Calculated',
  'status.awaited': 'Not Marked Yet',
  'att.offSite': 'Off Site',
  'att.offSiteWhy':
    'A Mark On This Day Was Taken Outside Every Site Boundary. Open The Day To '
    + 'See Which One, When, And How Far Away — The Time May Need Correcting.',
  'att.dayClosed':
    'This Day Closed At Midnight. Its Hours Count As Approved, And Only The '
    + 'Office Can Change Them Now.',
  'att.manual': 'By Hand',
  'cn.correctedBy': 'Corrected By {name}',
  'cn.overtimeBy': 'Overtime Set By {name}',
  'cn.somebody': 'Somebody',
  'att.changeSite': 'Change The Site This Day Is Charged To',
  'att.changeSiteFor': 'Change The Site For {name}',
  'att.siteSetByHand': 'This Site Was Set By Hand, Not Matched From A Scan.',
  'att.noSiteSet': 'No Site — Set',
  'att.noSite': 'No Site',
  'att.daysPending.one': '{count} Day Pending',
  'att.daysPending.other': '{count} Days Pending',
  'att.datedAfterToday': ' · {count} Dated After Today',
  'shell.expandSidebar': 'Expand The Sidebar',
  'shell.collapseSidebar': 'Collapse The Sidebar',
  'osn.markedIn.one': '{count} Marked In',
  'osn.markedIn.other': '{count} Marked In',
  'osn.inAt': 'In At {time}',
  'osn.oneShiftOpen': 'One Shift',
  'osn.manyShiftsOpen': '{count} Shifts',
  'osn.earlierThatDay': ' · {duration} Earlier That Day',
  'osn.earlierToday': ' · {duration} Earlier Today',
  'cp.closeKey': 'Close',
  'cp.openKey': 'Open',
  'cp.navigateKey': 'Navigate',
  'osn.noSiteRecorded': 'No Site Recorded',
  'osn.fromEarlierDay': ' · {count} From An Earlier Day',
  'kiosk.and': ' And ',
  'kiosk.matchesBoth': 'This Face Matches Both {names}. Ask The Supervisor To Mark This One By Roll-call.',
  'kiosk.narrowing': 'Narrowing… ±{metres} M',
  'kiosk.facesHere.one': '{count} Face On This Device',
  'kiosk.facesHere.other': '{count} Faces On This Device',
  'kiosk.viewsInAll': ' {count} Views In All.',
  'kiosk.startArriving': 'Start Scanning — Arriving',
  'kiosk.startLeaving': 'Start Scanning — Leaving',
  'kiosk.jobNo': 'Job {no}',
  'kiosk.waitingToUpload': ' · {count} Waiting To Upload',
  'kiosk.outsideBoundary': ' · Outside The Boundary',
  'kiosk.boundaryUnclear': ' · Boundary Unclear',
  'kiosk.siteDisagrees': ' · Site Disagrees',
  'kiosk.conflictNote': 'The Site You Named Is Not Where This Phone Says It Is. Scans Are Being Recorded Against {site} And The Disagreement Is Being Kept With Them.',
  'imp.confirmTitle': 'Import {count} {what}?',
  'imp.willBeCreated.one': '{count} Will Be Created.',
  'imp.willBeCreated.other': '{count} Will Be Created.',
  'imp.willBeUpdated.one': '{count} Will Be Updated.',
  'imp.willBeUpdated.other': '{count} Will Be Updated.',
  'imp.willBeLeft.one': '{count} Will Be Left Alone.',
  'imp.willBeLeft.other': '{count} Will Be Left Alone.',
  'imp.wontGoIn.one': '{count} Row Will Not Go In — You Can Download Them Afterwards And Fix Them.',
  'imp.wontGoIn.other': '{count} Rows Will Not Go In — You Can Download Them Afterwards And Fix Them.',
  'imp.yourFileOf': 'Your File Of {what}',
  'imp.rowsToImport.one': '{count} Row To Import',
  'imp.rowsToImport.other': '{count} Rows To Import',
  'imp.blankIgnored': ', {count} Blank Rows Ignored',
  'imp.rowN': 'Row {n} — {what}',
  'imp.andRefused': ', {count} Refused',
  'imp.eitherWay.one': '{count} Date Could Be Read Either Way',
  'imp.eitherWay.other': '{count} Dates Could Be Read Either Way',
  'fl.matchedSummary': '{stops} Stop(s) Matched To A Site, {segments} Attendance Segment(s) Created.',
  'fl.tripTitle': 'Trip · {who} · {when}',
  'fl.kmh': '{speed} Km/h',
  'fl.stopN': 'Stop {n}',
  'fl.stillThere': 'Still There',
  'fl.noSiteWithinRange': 'No Site Within Range',
  'rules.draftSavedAs': 'Draft Saved As Version {version}.',
  'rules.takeAllOut': 'Take All {count} Versions Out Of Force?',
  'rules.deactivateAll': 'Deactivate All {count}',
  'rules.liveFrom': 'Rules Are Live From {date}.',
  'rules.settingsLeft.one': '{count} Setting Left',
  'rules.settingsLeft.other': '{count} Settings Left',
  'rules.stillMissing.one': '{count} Setting Is Still Missing. It Is Listed Below.',
  'rules.stillMissing.other': '{count} Settings Are Still Missing. They Are Listed Below.',
  'sc.roundedTo': 'Rounded To {minutes} Min',
  'sc.couldNotRun': 'The Self-check Could Not Run: {message}',
  'sc.disagree.one': '{count} Check Found Figures That Do Not Agree. It Names The Rows To Open.',
  'sc.disagree.other': '{count} Checks Found Figures That Do Not Agree. Each One Below Names The Rows To Open.',
  'sc.hide': 'Hide',
  'sc.showN': 'Show {count} Of Them',
  'pc.costedElsewhere': '{runs} Payroll Runs Have Been Calculated And Put Cost On {sites} Sites, None Of It Here. Either Nobody Has Worked On This One, Or Their Hours Reached No Job Number.',
  'pc.sinceStarted': '{amount} Of Labour Since This Site Started',
  'pc.nobodyOnSite': 'Nobody On Site',
  'ih.undoTitle': 'Undo The Import Of {file}?',
  'ih.undoBody': 'This Deletes The {count} Records That Import Created. Anything It Updated Stays As It Is.',
  'ih.removedCount': '{count} Removed. ',
  'ih.keptInUse': '{count} Left In Place Because Something Already Refers To Them.',
  'ih.notImported': 'Not Imported',
  'att.beforeRules': '{count} Days Fall Before Your Rules Take Effect, So Nothing Could Be Worked Out For Them',
  'att.earliestIs': '. The Earliest Is {date}',
  'att.waitingOnRules': '{count} Days Waiting On The Company Rules Being Completed.',
  'att.recalculated': 'Recalculated {count} Days.',
  'att.skippedCount': '{count} Skipped.',
  'att.failedCount': '{count} Failed.',
  'att.andMore': 'And {count} More.',
  'pay.calculatedSlips': 'Calculated {count} Payslips.',
  'pay.skippedNoSalary': '{count} Skipped With No Salary On Record: {names}',
  /* The standing version of the line above. That one is said once and lost on
     the next reload; this is asked again every time the period is opened, and
     it covers the two causes the run never sees. See migration 0164. */
  'pay.unpaidTitle.one': 'One Person Has Attendance This Month And No Payslip',
  'pay.unpaidTitle.other': '{count} People Have Attendance This Month And No Payslip',
  'pay.unpaid.waiting_approval':
    'Taken On At The Site And Not Yet Confirmed By The Office. Approve Them On '
    + 'The Employees Screen And Run The Payroll Again — Every Day Already '
    + 'Recorded Is Paid Once They Are Confirmed.',
  'pay.unpaid.no_salary': 'No Salary On Record For This Month. Add One Under Salary Revisions, Then Calculate Again.',
  'pay.unpaid.left_before': 'Recorded As Having Left Before This Month Began, So Payroll Never Saw Them. Check The Leaving Date On Their File.',
  'pay.unpaid.joined_after': 'Recorded As Joining After This Month Ended, So Payroll Never Saw Them. Check The Joining Date On Their File.',
  'pay.unpaid.not_calculated': 'Has A Salary And Was Not Calculated. Run The Payroll Again, And Read The Errors If It Reports Any.',
  'pay.failedCount': '{count} Failed.',
  'pay.periodMarked': 'Period Marked {status}.',
  'pay.licence': 'Licence {no}',
  'pay.labourCostThisPeriod': 'Labour Cost This Period',
  'proj.jobExists': 'Job Number "{no}" Already Exists.',
  'proj.editJob': 'Edit Job {no}',
  'proj.deactivateTitle': 'Deactivate Job {no}?',
  'proj.chargedRecently': '{count} Shifts Have Been Charged To This Job Number In The Last Thirty Days. ',
  'pc.nothingCosted': 'Nothing Costed Yet',
  'pc.noRunYet': 'No Payroll Run Has Been Calculated Yet',
  'pc.noDayHere': 'No Day Has Been Allocated To This Site',
  'pc.landsHere': "Labour Lands Here When A Payroll Run Allocates Somebody's Day To This Site.",
  'pc.noneAnywhere': 'Labour Reaches This Panel From A Calculated Payroll Run. None Has Produced Job Cost Yet Anywhere In The Company, So There Is Nothing To Show For Any Site — This One Included.',
  'att.setEffectiveDate': '. Set The Effective Date In Settings → Rules To That Date Or Earlier, Activate Again, Then Recalculate.',
  'mg.chargedToJob': 'Charged To Job {no} — The Site They Last Worked',
  'mg.otRecorded': '{hours}h Overtime (Recorded, Not Yet Approved)',
  'mg.otApproved': '{hours}h Overtime Approved',
  'dash.deafAbsences': '{count} On Days Nothing Was Recorded At All',
  'dash.ofDays': ', {percent}% Of Days',
  'geo.recordedInside': 'Attendance For Job {no} Is Recorded Inside These Circles',
  'geo.narrowing': '±{metres} M…',
  'geo.blankUses': 'Blank Uses The Company Default Of {metres} M',
  'geo.deleteTitle': 'Delete The {label} Boundary?',
  'geo.deleteLabel': 'Delete {label}',
  'rc.markedArrived': '{count} Marked As Arrived. Mark Them Out When Work Finishes And The Hours Between Will Be Calculated.',
  'rc.markedFinished': '{count} Marked As Finished. Their Hours Are Being Calculated Now.',
  'bc.onThisDevice': '{what} On This Device',
  'bc.signInWith': 'Sign In With {what}',
  'bc.turnOn': 'Turn On {what} Sign-in',
  'bc.confirming': 'Confirming…',
  'aj.chargeDayTo': "Charge {name}'s Day To A Job Number",
  'aj.daysEarlier': ' {count} Days Earlier',
  'mg.dayN': 'Day {n}',
  'mg.jobNo': 'Job {no}',
  'mg.offSite': 'A Mark On This Day Was Taken Off Site — Open It To See Which',
  'mg.peopleOnPage.one': 'One Person',
  'mg.peopleOnPage.other': '{count} People',
  'mg.typeIn': 'Edit',
  'mg.typingOn': 'Editing',
  /* How a cell is saved, which the screen never said.
     -------------------------------------------------------------------------
     The office asked outright: "what do I have to do to save it? Ctrl+S does
     nothing, and when I click another cell the old value comes back." Every
     cell saves itself the moment the caret leaves it — there is no Save button
     and there should not be one, because a sheet of four thousand cells saved
     in one press is a sheet that fails in one press. But a rule nobody is told
     is not a rule, it is a trap. */
  'mg.typingHow':
    'Click A Cell And Type: P Present, A Absent, L Leave, H Holiday, R Rest Day '
    + '— Or A Leave Code Such As ANNUAL. The Job Number Goes In The Next Column '
    + 'And The Overtime Hours In The One After It. Every Cell Saves On Its Own As '
    + 'Soon As You Leave It — Enter Saves And Drops A Row, Tab Saves And Moves '
    + 'Right, The Arrow Keys Move, Escape Cancels. A Saved Cell Turns Green For A '
    + 'Moment; A Refused One Turns Red And Says Why. The Recorded Overtime Cannot '
    + 'Be Edited: That Is What The Scans Produced.',
  'mg.typeHere': 'Type Into {name}, {date}',
  'mg.typedNote': 'Typed In The Monthly Grid',
  'mg.notAStatus':
    '“{value}” Is Not A Status. Use P, A, L, H Or R, Or One Of The Company’s Leave Codes.',
  'mg.notHours': '“{value}” Is Not A Number Of Hours.',
  'mg.tooManyHours':
    '“{value}” Is More Than A Day Holds. Overtime Is Written In Hours — 2 Or 2.5, Not Minutes And Not A Job Number.',
  'mg.noSuchJob': 'No Site Has The Job Number {no}.',
  'mg.refusedOn': 'Not Saved — {name}, {date}',
  'mg.normalHours': '{hours}h Normal',
  'mg.nothingRecorded': '{date} — Nothing Recorded',
  'aj.lastRecordedOn': 'They Were Last Recorded On',
  'aj.dayBefore': ' The Day Before',
  'adv.statementTitle': 'Advance Statement · {name}',
  'adv.actionsFor': 'Actions For The {what} Advance',
  'adv.theAdvance': 'The {what} Advance',
  'adv.inFull': 'In Full',
  'cd.backToGps': "{name}'s Day Goes Back To Being Calculated From The GPS Record, The Roll-call And The Site Rules. Any Overtime Adjustment On It Is Cleared Too. Nothing Is Lost — The Correction Stays In The Activity Log.",
  'cd.correctDayOf': "Correct {name}'s Day",
  'cd.alreadyCorrected': 'Already Corrected',
  'ef.codeInUse': 'Employee ID "{code}" Is Already In Use. Choose Another.',
  'ef.editName': 'Edit {name}',
  'tk.alreadySettled': 'Already Settled',
  'tk.inSettlement': 'In Settlement',
  'tk.amountIn': 'Amount ({currency})',
  'fl.jobAndSite': 'Job {no} — {site}',
  'lv.leaveRequestOf': "{name}'s Leave Request",
  'lv.balancesResult': '{balances} Balances Across {people} People For {year}.',
  'nb.sweepResult': '{absence} Absence, {leave} Overdue Leave, {documents} Document. {queued} Days Queued.',
  'nb.unreadLabel': 'Notifications, {count} Unread',
  'nb.minAgo': '{count} Min Ago',
  'nb.daysAgo': '{count} Days Ago',
  'pp.fileTooBig': 'That File Is {size} MB. The Limit Is {limit} MB.',
  'pp.papersSubtitle': 'Permits, Contracts And Drawings · {count} Of {max}',
  'fl.actionsFor': 'Actions For {plate}',
  'fl.fileTooBig': 'That File Is {size} MB. The Limit Is {limit} MB.',
  'ns.alwaysOn': 'Always On',
  'ns.oneDigest': 'One Message A Day Covering Everybody',
  'ns.separateEach': 'A Separate Message For Each',
  'ns.oneMessageADay': 'One Message A Day',
  'ns.switchedOn': '{on} Of {total} Switched On',
  'tbl.filteredBy': 'Filtered By "{query}" — {shown} Of {total} Rows',
  'tbl.records.one': '{count} Row',
  'tbl.records.other': '{count} Rows',
  'tbl.filteredFrom': ' (Filtered From {total})',
  'mp2.placeSearchFailed': 'Place Search Failed: {message}',
  'bn.removedAcross': '{rows} Absences Removed Across {days} Days.',
  'bn.removeTitle': 'Remove {count} Absences?',
  'mg.clickToOpen': 'Click To Open',
  'mg.clickToCorrect': 'Click To Correct',
  'ap.upToModules': 'Up To {max} Modules, One Thumb Away. Only You See This.',
  'em.sentTo': 'Sent To {address}. Check The Log Below.',
  'pay.lastCalculated': 'Last Calculated {when}',
  'pay.daysBreakdown': '{worked} Worked · {holiday} Holiday · {absent} Absent · {leave} On Leave',
  'pay.dWorked': '{n} Worked',
  'pay.dHoliday': '{n} Holiday',
  'pay.dLeave': '{n} On Leave',
  'tbl.records.two': '{count} Rows',
  'tbl.records.few': '{count} Rows',
  'tbl.records.many': '{count} Rows',
  'mg.openOrCorrect': 'Click To Open · Alt-click To Correct',
  'dash.costThisMonth': ' · {amount} This Month',
  'dash.stillUnset.one': '{count} Setting Still Unset',
  'dash.stillUnset.other': '{count} Settings Still Unset',
  'edp.expiredDaysAgo': 'Expired {count} Days Ago',
  'edp.expiresIn': 'Expires In {count} Days — {date}',
  'fl.fileTooBigFixed': 'That File Is {size} MB. The Limit Is 5 MB.',
  'nb.minsAgo': '{count} Min Ago',
  'nb.yesterday': 'Yesterday',
  'nb.daysAgoShort': '{count} Days Ago',
  'pd.wasDeleted': '{what} Has Been Permanently Deleted.',
  'pd.nameErased': 'Name Erased',
  'us.notLinked': 'Not Linked',
  'us.newPasswordFor': 'New Password For {name}',
  'stl.airTicketIn': 'Air Ticket ({currency})',
  'shell.settingsUnset': '{count} Settings Still Unset',
  'cp.searching': 'Searching…',
  'cp.nothingFound': 'Nothing Found For “{query}”.',
  'tbl.filteredFromTotal': ' (Filtered From {total})',
  'tbl.pageOfOffice': 'Page {page} Of {pages}',
  'tbl.perPage': 'Rows Per Page',
  'adp.fromCentre': ' · {metres} M From The Site Centre',
  'att.onSiteNowText': 'On Site Now',
  'bn.recordedOn': 'On {days} Days Between {first} And {last} This Company Recorded ',
  'sl.ledgerOf': 'Salary Ledger · {name}',
  'tk.historyOnly': 'History Only',
  'dt.sinceStillHere': 'Since {time}, Still Here',
  'wps.leftOut': ' {count} Employees Left Out.',
  'rp.twoSheets': 'Two Sheets',
  'co.andArabicMonth': 'And Writes The Month In Arabic — {example}.',
  'ih.leftAlone': 'Left Alone',
  'im.emptyEveryRow': 'Empty In Every Row',
  'nb.justNow': 'Just Now',
  'nb.hoursAgo': '{count} H Ago',
  'imp.wontGoInTitle': '{count} Rows Will Not Go In',
  'imp.worthALook': 'Worth A Look',
  'imp.rejectionsSub': 'Each With The Reason. Download Them As A Spreadsheet, Fix Them, And Import That File.',
  'common.never': 'Never',
  'common.daysAgoShort': '{count}d Ago',
  'dash.payrollReady': 'Ready',
  'dash.payrollBlocked': 'Blocked',
  'mg.legendPresent': 'Present',
  'mg.legendAbsent': 'Absent',
  'mg.legendLeave': 'Leave',
  'mg.legendHoliday': 'Public Holiday',
  'mg.legendWeekly': 'Weekly Holiday',
  'mg.legendNotIn': 'Not In Yet',
  'mg.legendApproved':
    'APRVD Is The Overtime That Will Be Paid. Faint Where Nobody Adjusted It — '
    + 'Then The Recorded Hours Stand. ACTL Is Always What Was Recorded.',
  'mg.gridNote.one': '{count} Employee · Each Cell Shows Status, Job Number, And Worked+overtime Hours · {currency} Figures Appear On The Payslip',
  'mg.gridNote.other': '{count} Employees · Each Cell Shows Status, Job Number, And Worked+overtime Hours · {currency} Figures Appear On The Payslip',
  'tbl.recordsOf': '{shown} Of {total} Records',
  'common.justNow': 'Just Now',
  'common.minsAgoShort': '{count}m Ago',
  'common.hoursAgoShort': '{count}h Ago',
  'common.expiresOn': 'Expires {date}',
  'ih.createdWord': 'Created',
  'ih.updatedWord': 'Updated',
  'ih.refusedWord': 'Refused',
  'fl.unassigned': 'Unassigned',
  'fl.licenceWord': 'Licence',
  'im.matched': 'Matched',
  'im.likely': 'Likely',
  'att.compromised': 'Compromised',
  'pay.noteWord': 'Note',
  'pay.remarkFor': 'Remark For',
  'pay.remarkLabel': 'Remark',
  'pay.remarkHint': 'Kept With This Month And Shown On The Statement. Clear It To Remove It.',
  'pay.remarkPlaceholder': 'Paid Late — Bank Holiday',
  'psd.receiptWord': 'Receipt',
  'adp.attendanceFallback': 'Attendance',
  'shell.productName': 'NoorOne ERP',
  'shell.tagline': 'Smart ERP',

  /* -------------------------------------------------------------------------
   * The platform console
   * -------------------------------------------------------------------------
   * What a NoorOne operator reads, above every company. Translated like
   * everything else — an operator has a language too, and the alternative is
   * one screen in the product that is English because nobody was watching.
   * ---------------------------------------------------------------------- */
  'plat.console': 'Platform',
  'plat.operators': 'Operators',
  'plat.settings': 'Settings',
  'plat.account': 'Your Account',
  'pacc.title': 'Your Account',
  'pacc.subtitle': 'Your Picture, Your Second Factor, And How This Console Looks',
  'pacc.who': 'Who You Are Here',
  'pacc.whoSub': 'Shown On Every Company You Open, On Their Own Visits Log.',
  'pacc.factorSub': 'This Account Can Open Every Company On The Platform. It Is The One That Most Needs A Second Lock.',
  'pacc.factorBlurb': 'Your Password Is The Only Thing Standing Between Anybody Who Learns It And Every Salary, Every Passport And Every Bank Account On This Platform. Two Minutes Now.',
  'pacc.lostPhone': 'Lost The Phone? Another Operator With The Settings Grant Can Clear It Against The Database. There Is Deliberately No Button For It Here.',
  'pacc.appearance': 'Light And Dark',
  'pacc.appearanceSub': 'Yours Alone. It Does Not Change What Any Company Sees.',
  'pacc.ruleTitle': 'Every Operator',
  'pacc.ruleSub': 'A Rule For All Of Them, Not Just For You.',
  'pacc.ruleLabel': 'Require Two-step Sign-in From Every Operator',
  'pacc.ruleHint': 'Anybody Without One Keeps Their Sign-in And Can Do Nothing Until They Set One Up. They Can Still Reach This Screen.',
  'pacc.ruleWarning': 'Turn This On Once You Have Set Yours Up. Switching It On First Takes Your Own Permissions Away In The Same Instant — You Would Still Be Able To Enrol From Here, But It Is An Unpleasant Minute.',

  /* -------------------------------------------------------------------------
   * What NoorOne sets for everybody
   * ---------------------------------------------------------------------- */
  'pset.emailTitle': 'Sending Email',
  'pset.emailSub': 'One Verified Address, Used For Every Company. They No Longer Set This Themselves.',
  'pset.emailOn': 'Email Is On',
  'pset.provider': 'Sent Through',
  'pset.providerHint': 'Which Service Carries Every Message. The Keys And Passwords For Each Are Set Once Against The Functions, Never Here — So Switching Is A Choice, Not A Re-deployment. No Company Can Tell Which Is In Use.',
  'pset.providerGmail': 'Gmail — An Ordinary Account With An App Password',
  'pset.providerBrevo': 'Brevo — The Sending API',
  'pset.providerSmtp': 'Any Mail Server — Host, Port, User, Password',
  'pset.sendsTitle': 'Recent Sends',
  'pset.sendsSub': 'Every Attempt, In The Words The Provider Used. No Company Sees These.',
  'pset.sendsNone': 'Nothing Has Been Attempted Yet. If A Screen Has Just Told Somebody A Message Was Sent, It Did Not Reach This List — And That Is Worth Knowing.',
  'pset.sendsAccepted': 'Accepted By The Provider',
  'pset.providerReady': 'This One Is Configured And Can Send.',
  'pset.logoMissing': 'The Message Logo Is Not At This Address, So Mail Goes Out With The Name As Text And No Mark. Deploy The Front End So The File Is Reachable.',
  'pset.providerNotReady': 'This One Cannot Send Yet.',
  'pset.emailOnHint': 'Off, Nothing Is Sent And Every Attempt Is Still Recorded In Each Company\'s Delivery Log.',
  'pset.fromAddress': 'Sends From',
  'pset.fromAddressHint': 'Must Be An Address Your Mail Provider Has Verified, Or Everything Bounces.',
  'pset.fromName': 'Shown As',
  'pset.fromNameHint': 'Used Where A Company Has No Name To Show. A Payslip Carries The Employer\'s Name Instead.',
  'pset.replyTo': 'Replies Go To',
  'pset.replyToHint': 'Where Somebody Hitting Reply Reaches You. Left Blank, Replies Go To The Sending Address.',

  'pset.defaultsTitle': 'What A New Company Starts As',
  'pset.defaultsSub': 'The Colours A Company Opens On. Everything Else About How They Run — Currency, Time Zone, Language — They Answer For Themselves When They Apply.',
  'pset.theme': 'Theme',
  'pset.themeHint': 'Their Colours Until They Change Them. Everybody In That Company Sees This One.',
  'pset.appliesToNew': 'Changing These Affects Companies Approved From Now On. Nothing Already Running Moves.',
  'pset.save': 'Save',
  'pset.saved': 'Saved.',
  'pset.readOnly': 'You Can Read These. Changing Them Needs The Settings Grant.',

  /* -------------------------------------------------------------------------
   * The people who run it
   * ---------------------------------------------------------------------- */
  'ops.title': 'Platform Operators',
  'ops.subtitle': 'Everybody Who Can See Every Company. Keep It Short.',
  'ops.add': 'Add Operator',
  'ops.you': 'You',
  'ops.unrestricted': 'Unrestricted',
  'ops.switchedOff': 'Switched Off',
  'ops.insideACompany': 'Inside A Company',
  'ops.switchOff': 'Switch Off',
  'ops.switchOn': 'Switch On',

  'ops.addTitle': 'Add A Platform Operator',
  'ops.addBlurb': 'They Will See Every Company And Be Able To Open Any Of Them. They Start With The Day-to-day Grants — Reviewing Applications And Opening A Tenant — And Not With Everything. Raising Somebody Beyond That Is Done Against The Database, Deliberately.',
  'ops.name': 'Their Name',
  'ops.email': 'Their Email',
  'ops.emailHint': 'Must Not Already Belong To A Company. An Account Cannot Be Both.',
  'ops.password': 'First Password',
  'ops.passwordHint': 'At Least 12 Characters. Give It To Them Directly And Have Them Change It.',
  'ops.create': 'Create Operator',
  'ops.adding': 'Creating…',
  'eml.handledForYou': 'NoorOne Sends Your Email',
  'eml.handledForYouBody': 'Payslips, Alerts And Letters Go Out From NoorOne’s Own Verified Address, With {company} Named On The Message. There Is Nothing To Set Up Here, And Nothing That Can Be Left Half-configured. What You Choose Is Which Notifications You Want — That Is On The Notifications Screen.',
  'login.subtitle': 'Sign In To Your NoorOne Account.',
  'login.wrongDetails': 'That Email And Password Do Not Match. Check Both, Or Ask Your Administrator To Reset Your Password.',
  'common.more': 'More',
  'plat.willSuspendFirst': 'This Company Is Still Running, So It Will Be Suspended First — That Is One Step, Not Two.',

  /* ---- removing a company -------------------------------------------------
   * Written to be read by somebody about to do something irreversible, so it
   * says what will go rather than asking them to be sure.
   * ---------------------------------------------------------------------- */
  'plat.delete': 'Delete',
  'plat.deleteCompany': 'Delete This Company',
  'plat.deleteWarning': 'Everything Goes: {count} Employees, Their Attendance, Documents And Logins. There Is No Undo. Suspending Keeps All Of It And Stops Every Sign-in — That Is Usually What Is Wanted.',
  'plat.deleteReason': 'Why Is This Being Deleted?',
  'plat.deleteReasonHint': 'Kept After The Company Is Gone. It Is The Only Thing That Will Explain This Later.',
  'plat.deleteReasonPlaceholder': 'Approved Twice By Mistake — This Is The Duplicate',
  'plat.typeName': 'Type The Company Name To Confirm',
  'plat.deleteForGood': 'Delete For Good',
  'plat.deleting': 'Deleting…',
  'plat.deletedWithLeftovers': 'Deleted, With Something Left Over',
  'plat.leftBehindBlurb': 'The Company Is Gone. These Could Not Be Removed And Are Still There — Worth Clearing By Hand.',
  'plat.deleted': 'Removed',
  'plat.orphans': '{files} Files, {logins} Logins Left',
  'plat.accessEnds': 'Access Ends {time}',
  'ra.runBootstrap': 'Edit The Values At The Top Of {file} So The Email Matches This Account Exactly, Then Run It.',
  'sl.sinceJoining': 'Since Joining {date}.',
  'fk.hoursAfterRecalc': 'Hours Appear After The Next Recalculation.',
  'im.leftAsThey': 'These Are Left As They Are — Blank On A New Record, Unchanged On An Existing One.',
  'plat.overview': 'Overview',

  /* -------------------------------------------------------------------------
   * The console's front page
   * -------------------------------------------------------------------------
   * One question — what needs me — and then the companies that are quietly on
   * their way to becoming a support call. Nothing here is a figure from
   * inside a tenant; see the note at the top of OverviewPage.
   * ---------------------------------------------------------------------- */
  'ov.needsYou': 'Waiting On You',
  'ov.needsYouBlurb': 'These Will Not Move Until Somebody Looks At Them.',
  'ov.allClear': 'Nothing Waiting',
  'ov.allClearBlurb': 'No Applications To Read And No Company Stuck In Setup.',

  'ov.applicationsWaiting': '{count} To Review',
  'ov.applicationsDetail': 'Companies Asking To Join. Read What They Sent And Decide.',
  'ov.stuck': '{count} Still Not Running',
  'ov.stuckDetail': 'Signed Up, Never Finished Their Rules. Nothing They Do Costs Money Until They Have.',

  'ov.liveCompanies': 'Companies',
  'ov.peoplePaid': 'People',
  'ov.peoplePaidDetail': 'Across Every Live Company',
  'ov.quiet': 'Gone Quiet',
  'ov.quietDetail': 'Nothing Recorded In {days} Days',
  'ov.suspended': 'Suspended',

  'ov.stuckTitle': 'Signed Up, Not Running',
  'ov.stuckSub': 'Past Their First {days} Days With Rules Still Unset. Worth A Call — This Is Where A New Company Gives Up.',
  'ov.joinedAgo': '{days} Days Ago',

  'ov.quietTitle': 'Nothing Happening',
  'ov.quietSub': 'Set Up, Used, And Then Stopped. Either They Have Moved On Or Something Broke And Nobody Said.',
  'ov.lastSeen': '{days} Days Ago',
  'ov.neverUsed': 'Never Used',

  'ov.recentVisits': 'Where Operators Have Been',

  /* -------------------------------------------------------------------------
   * A company's first hour
   * -------------------------------------------------------------------------
   * Read once, by somebody who has never seen this system, on a dashboard
   * where every figure is nought. Each line says what it is and the next says
   * why it matters — because "add your sites" means nothing until you know
   * that attendance is matched against their boundaries.
   * ---------------------------------------------------------------------- */
  'start.title': 'Getting Your Company Running',
  'start.blurb': 'Five Things, In This Order. The Rest Of The App Works Meanwhile.',
  'start.count': '{done} Of {total}',
  'start.first': 'Start Here',

  'start.rules': 'Set Your Rules',
  'start.rulesWhy': 'Hours, Overtime And How A Monthly Salary Becomes A Daily Rate. Nothing Is Calculated Until These Are In — This System Ships With No Rates Of Its Own.',
  'start.people': 'Add Your People',
  'start.peopleWhy': 'One At A Time, Or Import A Spreadsheet You Already Keep.',
  'start.sites': 'Draw Your Sites',
  'start.sitesWhy': 'A Job Number And A Boundary On The Map. Attendance Is Matched Against Those Boundaries, So A Site Without One Records Nothing.',
  'start.logins': 'Give Your Staff Logins',
  'start.loginsWhy': 'Your Supervisors, Your Accountant, Anybody Who Needs To Sign In. Yours Is The Only One So Far.',
  'start.payroll': 'Open Your First Payroll Period',
  'start.payrollWhy': 'The Month You Are Paying For. Attendance Inside It Becomes Wages.',
  'plat.notEmailed': 'They Have Not Been Told',
  'plat.noSender': 'No Sender Address Is Set, So Nothing Was Emailed. The Decision Itself Is Saved. Set PLATFORM_EMAIL_FROM When Your Provider Is Working, Then Use Tell Them.',
  'plat.mailFailed': 'The Decision Is Saved But The Message Did Not Go. Try Tell Them, Or Write To Them Yourself.',
  'plat.resendBlurb': 'Send This Decision To The Applicant Again — Useful If It Was Made While Email Was Down.',
  'plat.tellThem': 'Tell Them',
  'plat.sending': 'Sending…',
  'plat.betweenUs': 'Between Us',
  'plat.betweenUsPlaceholder': 'Called Them — Waiting On A Clearer Licence Scan',
  'plat.addNote': 'Add',

  /* ---- when the vendor was in here, and a door that is shut ------------- */
  'act.visitsTitle': 'When NoorOne Was In Your Data',
  'act.visitsSub': 'Every Time An Operator Opens Your Company It Is Recorded Here, With The Reason They Gave.',
  'act.noVisits': 'Nobody From NoorOne Has Opened Your Company.',
  'act.byNoorOne': 'NoorOne',

  'off.companyTitle': 'This Company Is Switched Off',
  'off.companyBody': 'Signing In Is Suspended For Everybody Here. Nothing Has Been Deleted. Speak To NoorOne To Find Out What Is Needed.',
  'off.accountTitle': 'This Account Is Switched Off',
  'off.accountBody': 'Your Company Is Running Normally — It Is This Login That Has Been Disabled. Ask One Of Your Administrators To Switch It Back On.',

  /* -------------------------------------------------------------------------
   * Asking to join
   * -------------------------------------------------------------------------
   * Read by somebody who has never used this system and has no company in it
   * yet. Every other screen is written for people who already know what a
   * job number and a period are; nothing here can assume that.
   *
   * Arabic first-class from the first screen, and it has to be: an office in
   * Sharjah deciding whether to trust a payroll system with their wages will
   * not be encouraged by a sign-up form that cannot speak to them.
   * ---------------------------------------------------------------------- */
  'reg.title': 'Bring Your Company Onto NoorOne',
  'reg.blurb': 'Create Your Account First. You Will Tell Us About The Company On The Next Screen, And Can Come Back To It Whenever You Like.',
  'reg.yourName': 'Your Name',
  'reg.yourNamePlaceholder': 'The Name You Sign Documents With',
  'reg.workEmail': 'Work Email',
  'reg.password': 'Password',
  'reg.passwordHint': 'At Least Eight Characters.',
  'reg.createAccount': 'Create The Account',
  'reg.creating': 'Creating…',
  'reg.emailTaken': 'There Is Already An Account With That Email. Sign In Instead, Or Use Another Address.',
  'reg.haveAccount': 'Already Have An Account?',
  'reg.checkYourEmail': 'Check Your Email',
  'reg.checkYourEmailBody': 'We Have Sent A Link To {email}. Open It To Confirm The Address, Then Come Back And Sign In.',
  'reg.backToSignIn': 'Back To Sign In',

  'login.newCompany': 'New Company?',
  'login.registerYours': 'Register Yours',

  'app.loading': 'Loading Your Application…',
  'app.tellUs': 'Tell Us About Your Company',
  'app.tellUsBlurb': 'Six Answers To Begin With. Everything Else Is On The Next Screen And Is Saved As You Type, So You Can Go And Find A Document Without Losing What You Have Entered.',
  'app.companyName': 'Company Name',
  'app.companyNameHint': 'What People Call It Day To Day.',
  'app.legalName': 'Registered Name',
  'app.legalNameHint': 'Exactly As It Appears On The Trade Licence.',
  'app.tradeLicence': 'Trade Licence Number',
  'app.tradeLicenceHint': 'The Number On The Licence Itself. We Check It Against The Document You Attach.',
  'app.contactName': 'Who To Contact',
  'app.contactEmail': 'Their Email',
  'app.contactPhone': 'Their Phone',
  'app.continue': 'Continue',
  'app.starting': 'Saving…',

  'app.aboutCompany': 'The Company',
  'app.companyNameAr': 'Company Name In Arabic',
  'app.legalNameAr': 'Registered Name In Arabic',
  'app.arabicHint': 'Optional. Used On Payslips And Letters When A Document Is Produced In Arabic.',
  'app.registration': 'Registration',
  'app.licenceExpiry': 'Licence Expires',
  'app.licenceExpiryHint': 'So We Know Whether The Document We Are Looking At Is Current.',
  'app.molId': 'MOHRE Establishment ID',
  'app.molIdHint': 'Needed Later For WPS Salary Files. Enter It Now And That Step Is Done.',
  'app.trn': 'Tax Registration Number',
  'app.trnHint': 'Your VAT Number, If You Have One.',
  'app.whereAndSize': 'Where, And How Big',
  'app.emirate': 'Emirate',
  'app.choose': 'Choose…',
  'app.howMany': 'People On The Payroll',
  'app.howManyHint': 'Roughly Is Fine. Nothing Is Calculated From This.',
  'app.address': 'Address',
  'app.howItRuns': 'How It Should Run',
  'app.currency': 'Currency',
  'app.timezone': 'Time Zone',
  'app.language': 'Language',
  'app.languageHint': 'What The Workspace Opens In. Everybody Can Change Their Own Afterwards.',

  'app.documents': 'What Proves It',
  'app.documentsBlurb': 'A Photo Or A PDF. The Trade Licence Is The One We Cannot Review Without; The Rest Help.',
  'app.whatIsThis': 'What Is This?',
  'app.kind.trade_licence': 'Trade Licence',
  'app.kind.establishment_card': 'Establishment Card',
  'app.kind.vat_certificate': 'VAT Certificate',
  'app.kind.other': 'Something Else',
  'app.attach': 'Attach A File',
  'app.uploading': 'Uploading…',

  'app.readyToSend': 'Send It For Review',
  'app.readyBody': 'We Will Read It And Come Back To You. If Anything Is Unclear We Will Ask Rather Than Refuse.',
  'app.needLicence': 'Attach The Trade Licence First — There Is Nothing To Review Without It.',
  'app.send': 'Send For Review',
  'app.sending': 'Sending…',
  'app.weAsked': 'We Asked You Something',

  'app.waitingTitle': 'With Us Now',
  'app.waitingBody': 'We Are Reading Through {company}. This Page Will Say What Was Decided As Soon As It Is — Check Back, And Watch Your Email In Case We Write.',
  'app.sentOn': 'Sent {date}',
  'app.approvedTitle': 'You Are In',
  'app.approvedBody': 'Your Company Is Set Up And You Are Its Administrator. The First Thing To Do Is Enter Your Rules — Hours, Rates And Holidays — Because Nothing Is Calculated Until They Are.',
  'app.openWorkspace': 'Open Your Workspace',
  'app.refusedTitle': 'Not This Time',
  'app.refusedBody': 'This Application Has Been Refused. If The Reason Below Is Something You Can Put Right, You Are Welcome To Apply Again.',
  'app.whatWeSaid': 'What We Said',
  'plat.companies': 'Companies',
  'plat.applications': 'Applications',
  'plat.visits': 'Visits',
  'plat.errors': 'Errors',
  'plat.noErrors': 'Nothing Has Failed',
  'plat.noErrorsBody': 'Technical Failures Land Here The Moment A Company Hits One. An Empty List Is The Good Outcome.',
  'plat.errOpen': 'Open',
  'plat.errAll': 'In Total',
  'plat.errShowAll': 'Show Resolved Too',
  'plat.errShowOpen': 'Show Only Open',
  'plat.errResolved': 'Resolved',
  'plat.errOpenOne': 'Open',
  'plat.errCompany': 'Company',
  'plat.errNoCompany': 'Not Signed In',
  'plat.errRole': 'Role',
  'plat.errWhen': 'Happened',
  'plat.errReceived': 'Received',
  'plat.errBuild': 'Build',
  'plat.errHint': 'Hint',
  'plat.errDetail': 'Detail',
  'plat.errRoute': 'Screen',
  'plat.errStack': 'Stack',
  'plat.errNote': 'Note',
  'plat.errMore': 'Full Detail',
  'plat.errLess': 'Hide Detail',
  'plat.errClose': 'Mark Dealt With',

  'plat.live': 'Live',
  'plat.settingUp': 'Setting Up',
  'plat.settingUpSub': 'No Rules Activated Yet',
  'plat.suspended': 'Suspended',
  'plat.findCompany': 'Search By Name Or Trade Licence',
  'plat.noCompanies': 'No Companies Yet',
  'plat.noCompaniesBody': 'The First One To Be Approved Will Appear Here.',
  'plat.suspend': 'Suspend',
  'plat.restore': 'Restore',
  'plat.open': 'Open',

  'plat.openCompany': 'Open This Company',
  'plat.openWarning': 'You Will See And Be Able To Change Everything In This Company, As Its Super Admin. Every Change Is Written Into Their Own Activity Log Under Your Name.',
  'plat.reason': 'Why Are You Going In?',
  'plat.reasonHint': 'Kept With The Visit And Shown To The Company. Say What You Are There To Do.',
  'plat.reasonPlaceholder': 'Payroll For August Did Not Run — Checking The Period Dates',
  'plat.openAndEnter': 'Open The Company',
  'plat.opening': 'Opening…',
  'plat.youAreInside': 'You Are Inside {company}',
  'plat.leave': 'Leave',

  'plat.waiting.zero': 'Nothing Waiting',
  'plat.waiting.one': '{count} Waiting',
  'plat.waiting.two': '{count} Waiting',
  'plat.waiting.few': '{count} Waiting',
  'plat.waiting.many': '{count} Waiting',
  'plat.waiting.other': '{count} Waiting',
  'plat.decided': 'Decided',
  'plat.noneWaiting': 'Nothing To Review',
  'plat.noneWaitingBody': 'Applications Appear Here As Soon As They Are Submitted.',
  'plat.noneDecided': 'Nothing Decided Yet',
  'plat.view': 'View',
  'plat.review': 'Review',

  'plat.legalName': 'Registered Name',
  'plat.tradeLicence': 'Trade Licence',
  'plat.expires': 'Expires {date}',
  'plat.molId': 'MOHRE Establishment ID',
  'plat.trn': 'Tax Registration Number',
  'plat.contact': 'Who To Call',
  'plat.whereAndSize': 'Where, And How Big',
  'plat.aboutPeople.zero': 'No Employees Given',
  'plat.aboutPeople.one': 'About {count} Employee',
  'plat.aboutPeople.two': 'About {count} Employees',
  'plat.aboutPeople.few': 'About {count} Employees',
  'plat.aboutPeople.many': 'About {count} Employees',
  'plat.aboutPeople.other': 'About {count} Employees',
  'plat.documents': 'What They Attached',
  'plat.noDocuments': 'Nothing Attached. There Is Nothing Here To Review.',
  'plat.lastSaid': 'Last Said To Them',

  'plat.note': 'What To Tell Them',
  'plat.noteHint': 'Sent To The Applicant With The Decision. Required To Refuse Or To Ask A Question.',
  'plat.notePlaceholder': 'The Trade Licence Scan Is Unreadable — Please Send A Clearer One',
  'plat.askForMore': 'Ask For More',
  'plat.refuse': 'Refuse',
  'plat.approveAndCreate': 'Approve And Create',

  'plat.noVisits': 'No Visits Yet',
  'plat.noVisitsBody': 'Every Time An Operator Opens A Company It Is Recorded Here, And On That Company\'s Own Activity Screen.',
  'plat.leftAt': 'Left {time}',
  'plat.stillIn': 'Still Inside',
  'bk.title': 'Backups',
  'bk.blurb': 'A Copy Of Everything This Company Has, Kept For {days} Days. Take One Before Anything You Are Nervous About, And Have One Taken Every Night.',
  'bk.takeOne': 'Take A Copy Now',
  'bk.takeOneBlurb': 'One A Day. The Nightly Copy Does Not Use It Up.',
  'bk.takeNow': 'Take A Copy',
  'bk.taking': 'Copying…',
  'bk.whatFor': 'What Is It For?',
  'bk.whatForHint': 'Optional, And Worth Writing. In A Fortnight This Is The Only Thing That Will Tell You Which Copy Is Which.',
  'bk.whatForPlaceholder': 'Before Importing August Attendance',
  'bk.caveat': 'This Copies The Records: People, Attendance, Payroll, Leave, Documents And Settings. It Does Not Copy The Document Files Themselves — Those Stay Where They Are And Their Links Keep Working. It Is A Copy To Go Back To, Not A File To Take Away.',
  'bk.nightly': 'Every Night',
  'bk.nightlyBlurb': 'Taken Automatically, Whether Anybody Remembers Or Not.',
  'bk.everyDay': 'Take A Copy Every Day',
  'bk.everyDayHint': 'Nothing Is Taken On A Day The Company Has No Activity To Record.',
  'bk.atWhatTime': 'At What Time',
  'bk.atWhatTimeHint': 'Read In {zone}, Which Is This Company\'s Own Clock.',
  'bk.saveSchedule': 'Save The Schedule',
  'bk.lastNightly': 'Last Taken Automatically On {date}.',
  'bk.retentionIsOurs': 'Copies Are Kept For {days} Days. How Long That Is, Is Set By Your Provider Rather Than Here.',
  'bk.copies': 'The Copies',
  'bk.copiesBlurb': 'Newest First. Each One Is Removed {days} Days After It Was Taken.',
  'bk.noneYet': 'No Copies Yet',
  'bk.noneYetBody': 'Take One Now, Or Switch On The Nightly Copy And One Will Be Waiting Tomorrow.',
  'bk.rows': 'Records',
  'bk.tables': 'Tables',
  'bk.size': 'Size',
  'bk.keptUntil': 'Kept Until',
  'bk.takenBy': 'Taken By {name}',
  'bk.wasRestored': 'Restored From',
  'bk.askToGoBack': 'Ask To Go Back',
  'bk.goBackWarning': 'Going Back To {date} Replaces Everything Recorded Since Then. Attendance, Payroll, Leave, New People — All Of It Returns To How It Was That Day, And Nothing Afterwards Will Look Wrong. Your Provider Does This By Hand, After Reading What You Write Below.',
  'bk.whyGoBack': 'Why Do You Need To Go Back?',
  'bk.whyGoBackHint': 'At Least Twenty Characters. A Person Reads This And Decides.',
  'bk.sendRequest': 'Send The Request',
  'bk.needMore': '{count} More Characters.',
  'bk.requestOpen': 'A Request Is Waiting',
  'bk.requestOpenBlurb': 'Your Provider Has It. Only One Can Be Open At A Time.',
  'bk.askedOn': 'Asked On {date}',
  'bk.withdraw': 'Withdraw',
  'bk.withdrawTitle': 'Withdraw The Request?',
  'bk.withdrawBody': 'Nothing Will Be Restored. You Can Ask Again Afterwards.',
  'bk.pastRequests': 'Earlier Requests',
  'bk.theySaid': 'They Said:',
  'bk.kind.manual': 'Taken By Hand',
  'bk.kind.scheduled': 'Nightly',
  'bk.kind.pre_restore': 'Before A Restore',
  'bk.status.pending': 'Waiting',
  'bk.status.approved': 'Approved',
  'bk.status.rejected': 'Refused',
  'bk.status.restored': 'Restored',
  'bk.status.withdrawn': 'Withdrawn',
  'set.backups': 'Backups',
  'set.backupsBlurb': 'Copies To Go Back To',
  'pbk.title': 'Backups And Restores',
  'pbk.blurb': 'Every Company\'s Copies, How Long They Are Kept, And The Requests Waiting On You. Restoring Replaces Everything A Company Has Done Since The Copy Was Taken.',
  'pbk.waiting': 'Waiting On You',
  'pbk.waitingBlurb': 'A Company Has Asked To Go Back. Read What They Wrote Before Deciding.',
  'pbk.nothingWaiting': 'Nothing Waiting',
  'pbk.nothingWaitingBody': 'No Company Has Asked To Be Restored.',
  'pbk.askedBy': 'Asked By {name} On {date}',
  'pbk.yourNote': 'Your Answer',
  'pbk.yourNoteHint': 'The Company Reads This. Ten Characters At Least If You Are Refusing.',
  'pbk.restoreNow': 'Restore Now',
  'pbk.approveOnly': 'Approve, Restore Later',
  'pbk.refuse': 'Refuse',
  'pbk.refuseNeedsReason': 'Write An Answer Before Refusing. It Is The Only Explanation They Get.',
  'pbk.confirmTitle': 'Restore {company}?',
  'pbk.confirmBody': 'Everything {company} Has Recorded Since That Copy Was Taken Will Be Deleted And Replaced: Attendance, Payroll, Leave, New People, Changed Settings. Nothing Afterwards Will Look Wrong. A Copy Of What Exists Right Now Is Taken First, And It Is Kept.',
  'pbk.confirmGo': 'Restore The Company',
  'pbk.perCompany': 'Every Company',
  'pbk.perCompanyBlurb': 'How Much Each Is Storing, And How Long You Are Keeping It. Seven Days Unless You Say Otherwise; Ninety Is The Ceiling.',
  'pbk.keepFor': 'Days',
  'pbk.set': 'Set',
  'pbk.copies': 'Copies',
  'pbk.stored': 'Stored',
  'pbk.nightly': 'Nightly',
  'pbk.on': 'On',
  'pbk.off': 'Off',
  'pbk.newest': 'Newest Copy {date}',
  'pbk.never': 'No Copies At All',
  'pbk.noProtection': 'No Copies And No Nightly Schedule. This Company Has Nothing To Go Back To.',
  'pbk.decided': 'Already Decided',
  'pbk.youSaid': 'You Said:',
  'pbk.rowsPlaced': '{count} Records Put Back.',
  'plat.backups': 'Backups',
  'pbk.notRunning': 'Nothing Is Running On A Schedule',
  'pbk.pgCron': 'Pg_cron {state}',
  'pbk.pgNet': 'Pg_net {state}',
  'pbk.jobsScheduled': '{count} Jobs Scheduled',
  'pbk.lastRan': 'Backup Job Last Ran {when}.',
  'pbk.whole': 'The Whole Platform, In One File',
  'pbk.wholeBlurb': 'Every Company, Every Operator, Every Setting. This Is The Copy That Can Leave — The Others Live Inside The Database They Are Backing Up, Which Is No Use On The Day The Database Is What Went.',
  'pbk.download': 'Download Everything',
  'pbk.preparing': 'Preparing…',
  'pbk.downloaded': 'Downloaded {name}, {size}.',
  'pbk.rowsInAll': 'Records In All',
  'pbk.tablesInAll': 'Tables',
  'pbk.importToggle': 'Restore From A File',
  'pbk.importWarning': 'This Empties Every Table In The Database And Writes The File In Its Place. It Is Meant For A Fresh Project — It Refuses Outright Once A Single Payslip Exists, Because A Wage Record Is Not Ours To Discard. Nothing About It Can Be Undone From This Screen.',
  'pbk.chooseFile': 'The Export File',
  'pbk.typePhrase': 'Type The Phrase To Confirm',
  'pbk.typePhraseHint': 'ERASE EVERYTHING, Exactly. It Is A Phrase Rather Than A Tick Box Because A Tick Box Arrives Ticked By Accident.',
  'pbk.importNow': 'Erase And Restore',
  'pbk.imported': '{rows} Records Restored, {problems} Tables Reported A Problem.',
  'pbk.sessionGone': 'Your Session Has Expired. Sign In Again.',
  'pbk.serverSaid': 'The Server Answered {status}.',
  'pbk.accounts': 'Sign-in Accounts',
  'pbk.onImageKit': 'Files On ImageKit',
  'pbk.includePayloads': 'Include The Per-company Backup Contents',
  'pbk.includePayloadsHint': 'Off By Default. Each Company Snapshot Is A Whole Copy Of That Company, So This Can Multiply The File Size Several Times Over. Turn It On When The Point Is Moving The Entire Platform Somewhere Else.',
  'cp.leave': 'Leave',
  'cp.advances': 'Advances',
  'cp.payslips': 'Payslips',
  'cp.logins': 'Logins',
  'cp.trips': 'Trips',
  'cp.holidays': 'Holidays',
  'cp.designations': 'Designations',
  'sf.blurbEmail': 'We Will Email A Code To The Address On Your Account.',
  'sf.willSendTo': 'The Code Goes To {where}.',
  'sf.willSend': 'The Code Goes To The Address On Your Account.',
  'sf.sendCode': 'Email Me A Code',
  'sf.sending': 'Sending…',
  'sf.codeSent': 'Sent. The Code Works For {minutes} Minutes.',
  'sf.sendAnother': 'Send Another Code',
  'sf.useEmailInstead': 'Email Me A Code Instead',
  'sf.useAppInstead': 'Use My Authenticator App Instead',
  'sf.lostPhoneEmail': 'No Authenticator? Have The Code Emailed Instead. If You Cannot Reach Either, Your Administrator Can Turn The Second Step Off For You.',
  'set.mfaEmailAllowed': 'Allow A Code By Email',
  'set.mfaEmailAllowedHint': 'Lets Somebody Without An Authenticator App Prove The Second Step With A Code Sent To Their Account\'s Email. Switch This Off To Make An Authenticator The Only Way Through — Stronger, But Most Site Staff Cannot Install One.',

  /* ---- click-to-detail panels -------------------------------------------
     Opened over the page rather than instead of it, so closing one puts the
     reader back exactly where they were. See src/components/drill.tsx. */
  'drill.attendanceDay': 'The Day',
  'drill.employee': 'Employee',
  'drill.advances': 'Advances',
  'drill.project': 'Site',
  'drill.openFully': 'Open The Page',
  'drill.inspect': 'Open The Detail',
  'drill.notFound': 'That Record Is No Longer There.',
  'drill.nothingRecorded': 'Nothing Was Recorded For This Day.',
  'drill.status': 'Status',
  'drill.arrived': 'Arrived',
  'drill.left': 'Left',
  'drill.worked': 'Worked',
  'drill.overtime': 'Overtime',
  'drill.chargedTo': 'Charged To',
  'drill.shifts': 'Shifts',
  'drill.stillOn': 'Still On Site',
  /* A shift closed by a scan from another day — see the note in
     AttendanceDayPanel and migration 0200. */
  'drill.daysLater': '(+{days}d)',
  'drill.ranPastTheDay':
    'A Shift Above Ran Past The End Of Its Own Day, So It Carries No Hours. '
    + 'A Scan Taken On Another Day Has Closed It — Correct The Times On The '
    + 'Day Page And The Hours Will Follow.',
  'drill.enteredByHand': 'This Day Was Entered By Hand Rather Than From A Scan.',
  'drill.number': 'Number',
  'drill.trade': 'Trade',
  'drill.joined': 'Joined',
  'drill.state': 'State',
  'drill.passport': 'Passport Expires',
  'drill.idExpiry': 'ID Expires',
  'drill.theirAdvances': 'Their Advances',
  'drill.theirAttendance': 'Their Attendance',
  'drill.stillOwed': 'Still Owed',
  'drill.remaining': 'Left',
  'drill.noAdvances': 'No Advances Have Been Taken.',
  'drill.leave': 'Leave',
  'drill.leaveType': 'Type',
  'drill.from': 'From',
  'drill.to': 'To',
  'drill.days': 'Days',
  'drill.dueBack': 'Due Back',
  'drill.returned': 'Returned',
  'drill.overstayed.one': 'Back One Day After The Approved Return. That Day Comes Off The Leave Entitlement, Not Off Pay.',
  'drill.overstayed.other': 'Back {count} Days After The Approved Return. Those Days Come Off The Leave Entitlement, Not Off Pay.',
  'drill.jobNumber': 'Job Number',
  'drill.client': 'Client',
  'drill.started': 'Started',

  /* ---------------------------------------------------------------------------
     Importing a month of the office's own spreadsheet.
     The refusals are worded to send somebody to one cell in their own file.
     -------------------------------------------------------------------------- */
  'attImp.importSheet': 'Import A Sheet',
  'attImp.title': 'Import An Attendance Sheet',
  'attImp.subtitle': 'Bring A Month You Already Keep In Excel Into The Record.',
  'attImp.startFromOurs': 'Start From Our Blank Sheet',
  'attImp.startFromOursBody':
    'It Is Laid Out The Way Yours Already Is — One Date Across Four Columns, '
    + 'Repeated For The Whole Month — With The Dates Filled In And A Page '
    + 'Explaining Every Column. Fill It In And Bring It Back Here.',
  'attImp.whichMonth': 'Which Month',
  'attImp.downloadBlank': 'Download The Blank Sheet',
  'attImp.rosterNotReady': 'The Employee List Has Not Loaded Yet. Close This And Open It Again.',
  'attImp.rosterLoading': 'Loading The Employee List…',
  'attImp.chooseFilled': 'Choose Your Filled-In Sheet',
  'attImp.reading': 'Reading The File…',
  'attImp.notOurSheetLong':
    'This Does Not Look Like An Attendance Sheet. It Needs A Row Of Headings '
    + 'With ATNDS And SITE On It, And A Date Above Each Group. Download The '
    + 'Blank Sheet Above And Fill That In.',
  'attImp.days': 'Days',
  'attImp.people': 'People',
  'attImp.dates': 'Dates',
  'attImp.refused': 'Refused',
  'attImp.futureDropped.one':
    '1 Day On This Sheet Has Not Happened Yet. It Is Left Out.',
  'attImp.futureDropped.other':
    '{count} Days On This Sheet Have Not Happened Yet. They Are Left Out.',
  'attImp.outsideEmployment.one':
    '1 Day Falls Outside Somebody’s Employment — Before They Joined, Or After '
    + 'They Left. It Is Left Out.',
  'attImp.outsideEmployment.other':
    '{count} Days Fall Outside Somebody’s Employment — Before They Joined, Or '
    + 'After They Left. They Are Left Out.',
  'attImp.onlyYourSites':
    'You See The People On Your Own Sites. Codes From Elsewhere Are Reported As '
    + 'Unknown Here Even Where They Are Correct — Import The Sheet For Your Own '
    + 'Crew, Or Ask The Office To Import The Whole Month.',
  'attImp.sitesNotFound':
    'These Job Numbers Match No Site: {list}. Those Days Still Come In, With No '
    + 'Site Against Them.',
  'attImp.refusedCells.one': '1 Cell Refused',
  'attImp.refusedCells.other': '{count} Cells Refused',
  'attImp.restStillGoesIn': 'The Rest Still Goes In',
  'attImp.atCell': 'Row {row}, Column {column}',
  'attImp.badLetter': '“{value}” Is Not One Of The Status Letters.',
  'attImp.badHours': '“{value}” Is Not A Number Of Hours A Day Could Hold.',
  'attImp.noId': 'This Row Has A Name — “{value}” — And No Employee ID.',
  'attImp.noDate': 'There Is No Date Above This Group Of Columns.',
  'attImp.notOurSheet': 'This Sheet Is Not Laid Out The Way The Import Expects.',
  'attImp.noSuchCode': 'No Employee Has This Code. The Name On The Sheet Is “{value}”.',
  'attImp.whatIsL': 'What Does L Mean On This Sheet?',
  'attImp.whatIsLHint':
    'The Sheet Says Only That A Day Was Leave. Which Kind Decides Whether It Is '
    + 'Paid, So It Is Asked Once Rather Than Guessed Three Thousand Times.',
  'attImp.chooseOne': 'Choose One',
  'attImp.note': 'Where These Figures Came From',
  'attImp.noteHint': 'Stored On Every Day The Import Writes, And Shown On The Record.',
  'attImp.defaultNote': 'Imported From {file}',
  'attImp.nothingToImport': 'There Is Nothing In This File To Import.',
  'attImp.sayWhere': 'Say Where These Figures Came From.',
  'attImp.chooseLeaveKind': 'Choose Which Kind Of Leave L Means.',
  'attImp.readyBlurb': 'Nothing Is Written Until You Press Import.',
  'attImp.pickAnother': 'Choose Another File',
  'attImp.importDays.one': 'Import 1 Day',
  'attImp.importDays.other': 'Import {count} Days',
  'attImp.writing': 'Writing {done} Of {total}…',
  'attImp.daysWritten.one': '1 Day Written',
  'attImp.daysWritten.other': '{count} Days Written',
  'attImp.daysRefused.one': '1 Day Was Refused, With The Reason Beside It:',
  'attImp.daysRefused.other': '{count} Days Were Refused, With The Reason Beside Each:',
  'attImp.andMore.one': 'And 1 More.',
  'attImp.andMore.other': 'And {count} More.',
  'attImp.done': 'Done',

  /* ---------------------------------------------------------------------------
     A worker's own papers, in his own portal. View only: there is no download,
     no send, and no link on the screen that could be copied out of it.
     -------------------------------------------------------------------------- */
  'meDocs.title': 'My Documents',
  'meDocs.view': 'View',
  'meDocs.viewOnly': 'View Only',
  'meDocs.opening': 'Opening…',
  'meDocs.until': 'Valid Until {date}',
  'meDocs.noneHeld': 'No Documents Yet',
  'meDocs.noneHeldBody':
    'Copies Of Your Passport, Visa, Emirates ID And Contract Appear Here As The '
    + 'Office Adds Them.',
  'meDocs.viewOnlyNote':
    'These Open On This Screen Only. There Is No Link To Copy, And Nothing Is '
    + 'Saved To Your Phone.',

  /* ---------------------------------------------------------------------------
     What a company is told when the platform is behind, and all it is told.
     The migration commands and the column names go to the platform operator.
     -------------------------------------------------------------------------- */
  'pm.companyTitle': 'Some Screens May Look Empty For A Short While',
  'pm.companyBody':
    'An Update Is Still Being Finished. Nothing Has Been Lost, It Has Been '
    + 'Reported, And Everything Will Reappear When It Is Done.',
  'set.arabicTypeThemIn': 'Type Them In As You Go.',
} as const;
